import request, { type RequestConfig } from './request'

/** 兼容 R<T> 业务异常对象与 Axios HTTP 异常，供页面展示后端可操作原因。 */
export function apiErrorMessage(error: any, fallback: string): string {
  const body = error?.response?.data || error || {}
  const message = body?.msg || body?.message || fallback
  const requestId = body?.requestId || error?.response?.headers?.['x-request-id']
  return requestId && !String(message).includes(requestId)
    ? `${message}（request-id: ${requestId}）`
    : String(message)
}

/** 面向用户的错误提示：隐藏 SQL/Mapper/Java 堆栈，避免批处理页面泄露技术细节。 */
export function friendlyErrorMessage(error: any, fallback = '操作失败，请检查数据后重试'): string {
  const raw = String(error?.response?.data?.msg || error?.msg || error?.message || '')
  if (!raw || /sql|mapper|mybatis|java\.|org\.springframework|error updating database|constraint|duplicate entry|cannot be null|doesn't have a default value|data too long/i.test(raw)) {
    return fallback
  }
  return raw
}

// UAT-FB#99 (2026-06-17): 通用 KV 存储 — P0 22 个 HR 配置 store 的后端持久化
export interface KvVO {
  key: string
  value: any
  version: number
  scope: string
  description?: string
  updatedAt?: string
  updatedBy?: number
}
export const sysKvApi = {
  get: (key: string, config?: RequestConfig) =>
    request.get<KvVO | null>(`/sys-kv/${encodeURIComponent(key)}`, config),
  listByPrefix: (prefix: string) =>
    request.get<KvVO[]>(`/sys-kv`, { params: { prefix } }),
  put: (key: string, value: any, version?: number, scope?: string, description?: string) =>
    request.put<KvVO>(`/sys-kv/${encodeURIComponent(key)}`, { value, version, scope, description }),
  delete: (key: string) => request.delete(`/sys-kv/${encodeURIComponent(key)}`),
}

export const authApi = {
  login: (data: { username: string; password: string; otp?: string }) => request.post('/auth/login', data),
  me: () => request.get('/auth/me'),
  logout: () => request.post('/auth/logout'),
  // V112 多角色真切换
  myRoles: (config?: RequestConfig) => request.get('/auth/my-roles', config),
  switchRole: (roleCode: string, orgId?: number) =>
    request.post('/auth/switch-role', null, { params: orgId != null ? { roleCode, orgId } : { roleCode } }),
  // UAT-FB#96 (2026-06-17): 演示模式 — 列全部可切换账号 + 伪装登录
  switchableUsers: (config?: RequestConfig) => request.get('/auth/switchable-users', config),
  switchToUser: (userId: number) => request.post(`/auth/switch-to-user/${userId}`),
  // V235: 改密 (强制改密态 + 用户主动改密共用). 强制改密态下返回新 token, 前端替换 store 无缝续会话.
  changePassword: (data: { oldPassword: string; newPassword: string }) =>
    request.post('/auth/change-password', data),
}

export const orgApi = {
  tree: () => request.get('/org/tree'),
  /** 权限配置专用组织树：需要 roles:read，不授予组织业务数据访问权。 */
  scopeTree: () => request.get('/org/scope-tree'),
  /** 可选的组织名称映射；无 org:read 时允许调用方静默降级。 */
  list: (config?: RequestConfig) => request.get('/org/list', config),
  // 预览下一个可用编码(含软删/停用/历史顺延) — 让前端"预览即最终编号"
  nextCode: (prefix: string, pad: number) =>
    request.get<string>('/org/next-code', { params: { prefix, pad } }),
  get: (id: number) => request.get(`/org/${id}`),
  create: (data: any) => request.post('/org', data),
  update: (id: number, data: any) => request.put(`/org/${id}`, data),
  // 组织合并(超管直接执行): { targetId, reason } → 子组织/员工转目标 + 源停用
  merge: (id: number, data: { targetId: number; reason: string }) =>
    request.post<any>(`/org/${id}/merge`, data),
  // 组织合并(普通管理员提交 proc 审批, 通过后自动执行): { targetId, reason }
  mergeApply: (id: number, data: { targetId: number; reason: string }) =>
    request.post<any>(`/org/${id}/merge-apply`, data),
  // 组织拆分: { newOrg:{name,type}, empIds:[], childOrgIds:[], reason }; split=超管直接, splitApply=提交审批
  split: (id: number, data: any) => request.post<any>(`/org/${id}/split`, data),
  splitApply: (id: number, data: any) => request.post<any>(`/org/${id}/split-apply`, data),
  remove: (id: number) => request.delete(`/org/${id}`),
  positions: (id: number) => request.get(`/org/${id}/positions`),
  headcount: (id: number) => request.get(`/org/${id}/headcount`),
  /** UAT-FEEDBACK #2: 全部组织当年编制汇总 */
  headcountList: (year?: number) =>
    request.get<any[]>('/org/headcount/list', { params: year ? { year } : {} }),
  /** UAT-FEEDBACK #3: 编制预测报告 xlsx 下载 (浏览器直开) */
  headcountForecastUrl: (year?: number) =>
    `/api/org/headcount/export-forecast${year ? `?year=${year}` : ''}`,
  /** 2026-07-28 B: 组织数据完整性体检 (多根/断链/职位未挂组织) */
  integrityCheck: () => request.get<any>('/org/integrity-check'),
  /** 2026-07-28 A: 闭包表全量重建 (从 parent_id 递归重算, 幂等) */
  rebuildClosure: () => request.post<any>('/org/closure/rebuild'),
  changeLog: (id: number) => request.get(`/org/${id}/change-log`),
  allChanges: (params: any = {}) => request.get('/org/changes', { params }),
  changesStats: () => request.get('/org/changes/stats'),
  changesKpi: () => request.get('/org/changes/kpi'),
  revokeChangeLog: (id: number, reason: string) =>
    request.post(`/org/change-log/${id}/revoke`, { reason }),
  virtualOrgs: () => request.get('/org/virtual'),
  change: (id: number, data: any) => request.post(`/org/${id}/change`, data),
  moveTo: (id: number, newParentId: number) => request.post(`/org/${id}/move-to/${newParentId}`),
  employees: (id: number, page = 1, size = 20, positionId?: number) =>
    request.get(`/org/${id}/employees`, { params: { page, size, ...(positionId ? { positionId } : {}) } }),
  typeRules: (parentType?: string) =>
    request.get('/org/type-rules', { params: parentType ? { parentType } : {} }),
  exportExcel: () =>
    request.get('/org/export', { responseType: 'blob' }),
  importExcel: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post('/org/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  templates: () => request.get('/org/templates'),
  fromTemplate: (data: any) => request.post('/org/from-template', data),
}

export const empApi = {
  page: (params: any) => request.get('/emp', { params }),
  get: (id: number) => request.get(`/emp/${id}`),
  /** 新增员工工号预览；只展示，最终编号由 create 在后端事务中分配。 */
  nextEmpNo: () => request.get<string>('/emp/next-emp-no'),
  create: (data: any, config?: any) => request.post('/emp', data, config),
  update: (id: number, data: any, config?: any) => request.put(`/emp/${id}`, data, config),
  remove: (id: number) => request.delete(`/emp/${id}`),
  // status: 'ACTIVE'/'RESIGNED'/'ALL' (可选) — 让 byEmpType 按前端 archiveTab 联动
  stats: (status?: string, config?: RequestConfig) => request.get('/emp/stats', {
    ...config,
    params: status ? { status } : {},
  }),
  // 去 mock (2026-07-13): HR 数据分析 司龄/年龄/学历 分布真聚合 (替代前端写死 TENURE_DIST/AGE_DIST/EDU_DIST)
  statsDistribution: (status?: string) =>
    request.get<{ tenureDist: any[]; ageDist: any[]; eduDist: any[]; total: number }>(
      '/emp/stats/distribution', { params: status ? { status } : {} }),
  // UAT-FB#56 (2026-06-16): 试用期员工 — employee.status='PROBATION', 区别于 emp_probation 流程实例
  // 性能重构: 改服务端分页 + 条件下推, 返 PageR<{records,total,page,size}>
  probationList: (params?: {
    page?: number; size?: number; keyword?: string; orgId?: string | number
    posName?: string; probEndFrom?: string; probEndTo?: string
  }) => request.get('/emp/probation-list', { params }),
  // P2.2 (2026-06-21): 员工任职轨迹 — 跨表聚合时间线 (调动/转正/调薪/合同/离职)
  timeline: (empId: number) => request.get<any[]>(`/emp-timeline/${empId}`),

  // UAT-FB#89 (2026-06-17): HR 按需解密 PII (身份证 / 手机 / 银行卡) — 走审计 + reason 强校验
  reveal: {
    idNo: (id: number, reason: string) =>
      request.get<{ idNo: string }>(`/emp/${id}/reveal/id-no`, { params: { reason } }),
    mobile: (id: number, reason: string) =>
      request.get<{ mobile: string }>(`/emp/${id}/reveal/mobile`, { params: { reason } }),
    bankCard: (id: number, reason: string) =>
      request.get<{ bankCard: string }>(`/emp/${id}/reveal/bank-card`, { params: { reason } }),
  },

  // UAT 反馈段 C: 员工档案变更日志 (V135 真接, 替代前端 mock store)
  // 返回 [{id, empId, empNo, changeType, operatorLabel, effDate, createdAt,
  //       reason, changedFields:[{field,label,before,after}], beforeData, afterData}]
  changeLogByEmp: (empId: number) =>
    request.get<any[]>(`/emp-change-log/by-emp/${empId}`),
  changeLogByEmpNo: (empNo: string) =>
    request.get<any[]>(`/emp-change-log/by-emp-no/${empNo}`),
  changeLogPage: (params?: {
    page?: number; size?: number; keyword?: string; changeType?: string
    from?: string; to?: string
  }) => request.get<EmpChangeLogPage>('/emp-change-log/page', { params }),
  changeLogStats: () => request.get<EmpChangeLogStats>('/emp-change-log/stats'),

  // emp 产品深度 P1 (2026-06-04 凌晨): 批量操作 (commit 6f5b15c 后端 14 单测)
  // 返回 BatchResult { totalCount, successCount, failedCount, failures: [{empId,empName,reason}] }
  batch: {
    transfer: (data: { empIds: number[]; toOrgId: number; toPosName?: string; toLevel?: string; reason: string }) =>
      request.post('/emp/batch/transfer', data),
    updateManager: (data: { empIds: number[]; newManagerId: number; reason: string }) =>
      request.post('/emp/batch/update-manager', data),
    // YGCS037 (2026-07-01) 组4 C: 批量编辑白名单字段 (center/workCity/joinDate/empType/status/orgId/managerId)
    updateAttrs: (data: {
      empIds: number[]; reason: string;
      center?: string; workCity?: string; joinDate?: string;
      empType?: string; status?: string; orgId?: number; managerId?: number;
    }) => request.post('/emp/batch/update-attrs', data),
  },
}

// XCCS·AI 离职风险预警 (V265, 2026-07-09): 规则打分(可解释) + AI 归因(润色).
// 后端 AttritionRiskController /emp/attrition-risk. 风险名单/单人快照/触发扫描.
export interface AttritionRiskFactor {
  code: string
  label: string
  weight: number
  hitScore: number
  detail: string
}
export interface AttritionRiskItem {
  id: number
  empId: number
  empName?: string
  empNo?: string
  orgPath?: string
  snapshotDate: string
  riskScore: number
  riskLevel: 'LOW' | 'MID' | 'HIGH'
  factorsJson?: string
  aiComment?: string
}
export const attritionRiskApi = {
  // 触发批量 AI 风险扫描 (算分+写快照+HIGH/MID 加 AI 归因). orgId 可选. 返回 { scored }
  scoreAll: (orgId?: number) =>
    request.post<{ scored: number }>('/emp/attrition-risk/score-all', null,
      { params: orgId != null ? { orgId } : {} }),
  // 单人评分
  scoreOne: (empId: number) => request.post<AttritionRiskItem>(`/emp/attrition-risk/score/${empId}`),
  // 风险名单 (最新快照). level=LOW|MID|HIGH 可选. 返回 MP Page { records, total, ... }
  list: (params: { current?: number; size?: number; level?: string; orgId?: number } = {}) =>
    request.get<{ records: AttritionRiskItem[]; total: number }>('/emp/attrition-risk/list', { params }),
  // 单人最新快照 (因子明细+AI 归因) + 近6次趋势. 返回 { latest, trend:[{date,score,level}] }
  getByEmp: (empId: number) =>
    request.get<{ latest: AttritionRiskItem | null; trend: Array<{ date: string; score: number; level: string }> }>(
      `/emp/attrition-risk/emp/${empId}`),
}

// W3-c (2026-06-03): 员工账号开通 (真接 rbac_user, 后端 EmpAccountController)
// 返回 { username, defaultPassword, empName } — 前端弹一次性密码 dialog (类似 V60 apiKey 模式)
export const empAccountApi = {
  provision: (empId: number, roleCode?: string) =>
    request.post(`/emp-account/${empId}/provision`, null, { params: roleCode ? { roleCode } : undefined }),
}

// V124 (2026-06-09): 角色持有人自定义增删改 (Admin 在 RoleManagementPanel 持有人 Drawer 用).
// 复用 V112 rbac_user_role 表 (复合主键 user_id + role_id)。普通角色直接生效，高权限角色双人审批后生效。
// 后端: hasAuthority('roles') (= 角色与权限菜单, 默认仅 GROUP_HR/admin 有).
export interface RbacUserRoleHolder {
  userId: number
  username: string
  nickname?: string
  empId?: number
  empNo?: string
  empName?: string
  empStatus?: string
  defaultOrgId?: number
  defaultOrgName?: string
}
export const empSavedFilterApi = {
  list: () => request.get<any[]>('/emp/saved-filters'),
  save: (data: any) => request.post<any>('/emp/saved-filters', data),
  remove: (id: number) => request.delete<void>(`/emp/saved-filters/${id}`),
  publish: (id: number, orgId: number, permission: 'READ_ONLY' | 'EDITABLE' = 'READ_ONLY') => request.post<any>(`/emp/saved-filters/${id}/publish`, null, { params: { orgId, permission } }),
  setOrgDefault: (id: number, orgId: number) => request.post<any>(`/emp/saved-filters/${id}/default`, null, { params: { orgId } }),
}

export interface EmpChangeLogField {
  field?: string
  label?: string
  before?: unknown
  after?: unknown
}

export interface EmpChangeLogItem {
  id: number
  empId: number
  empNo: string
  empName?: string
  changeType: string
  reason?: string
  operatorId?: number
  operatorLabel?: string
  effDate?: string
  createdAt?: string
  changedFields: EmpChangeLogField[]
}

export interface EmpChangeLogPage {
  total: number
  page: number
  size: number
  records: EmpChangeLogItem[]
}

export interface EmpChangeLogStats {
  total: number
  empCount: number
  currentYear: number
  currentYearCount: number
  byType: Record<string, number>
}
export const rbacUserRoleApi = {
  /** 列出某角色下所有持有人 */
  listByRole: (roleCode: string) =>
    request.get<any, { code: number; msg: string; data: RbacUserRoleHolder[] }>(
      '/rbac/user-role/by-role', { params: { roleCode } }
    ),
  /** Admin 给员工分配角色 */
  assign: (data: { empId: number; roleCode: string; defaultOrgId?: number; reason: string }) =>
    request.post('/rbac/user-role/assign', data),
  assignmentMode: (roleCode: string) =>
    request.get<{ approvalRequired: boolean; mode: 'DIRECT' | 'DUAL_APPROVAL' }>('/rbac/user-role/assignment-mode', { params: { roleCode } }),
  submitPrivilegedAssignment: (data: { empId: number; roleCode: string; defaultOrgId?: number; reason: string }) =>
    request.post('/rbac/privilege-change-requests/role-assignment', data),
  /** Admin 移除某用户的某角色 (注意: 走 userId + roleCode, 因为 rbac_user_role 表无独立 id 列) */
  unassign: (userId: number, roleCode: string, reason: string) =>
    request.delete('/rbac/user-role', { params: { userId, roleCode, reason } }),
  /**
   * V124-enh (Q1, 2026-06-09): 一次性拿所有角色 → 持有人数 Map.
   * 给 RoleManagementPanel 主表 "持有人数" 列用, 替代前端 mock store 假数据.
   * 返回 e.g. { GROUP_HR: 1, CENTER_HR: 0, DEPT_MGR: 3, ... } — 含所有非删角色.
   */
  counts: () =>
    request.get<any, { code: number; msg: string; data: Record<string, number> }>(
      '/rbac/user-role/counts'
    ),

  /**
   * 2026-07-17: 角色 → **流程可解析人数** (与后端 ProcRoleQueryMapper 同口径: 账号启用 + 有在职员工档案)。
   *
   * 与 counts 的差额 = 持有该角色但流程用不上的人。实测 GROUP_HR 持有 1(admin) / 流程可用 0
   * (admin 没有员工档案) —— 而 GROUP_HR 是 SLA 超时升级的兜底角色, 即兜底发给空气。
   * 列表并排显示这两个数, 配错一眼可见。
   */
  countsUsable: () =>
    request.get<any, { code: number; msg: string; data: Record<string, number> }>(
      '/rbac/user-role/counts-usable'
    ),
}

export const dashboardApi = {
  overview: (config?: RequestConfig) => request.get('/dashboard/overview', config),
}

// 加分件 B: Dashboard AI 解读 — 后端 DashboardInsightController @ GET /dashboard/ai-insight
//   返 { available, insight, generatedAt, model, cached, msg }.
//   AI 未配置/失败 available=false + msg 引导; 当天同数据域走 sys_kv 缓存, force=true 重生成.
//   silent: 失败不弹全局 toast — 卡片内按 available/msg 统一渲染降级引导.
export const dashboardInsightApi = {
  interpret: (force = false) =>
    request.get<any>('/dashboard/ai-insight', {
      params: { force }, silent: true, timeout: 90000,
    } as any),
}

export const meApi = {
  /** V4-ZD: 员工自助仪表盘 (HR 可传 previewEmpId 预览他人) */
  dashboard: (previewEmpId?: number) =>
    request.get('/me/dashboard', { params: previewEmpId ? { previewEmpId } : {} }),

  /** V279 M5: 我的假期余额 (真引擎) — 年假(天)+调休(小时), 按次假种不列入 */
  leaveBalance: (previewEmpId?: number) =>
    request.get<any[]>('/me/leave-balance', { params: previewEmpId ? { previewEmpId } : {} }),

  /** 移动端申请可用假种，读取后端 att_leave_type 启用配置。 */
  leaveTypes: () => request.get<any[]>('/me/leave-type'),

  /** Phase A: 历史薪资 (含真社保扣 W2 真值). HR 可传 previewEmpId. */
  payrollHistory: (year?: number, previewEmpId?: number) =>
    request.get('/me/payroll-history', {
      params: { ...(year ? { year } : {}), ...(previewEmpId ? { previewEmpId } : {}) },
    }),

  /** Phase A: 月度考勤汇总. HR 可传 previewEmpId. */
  attMonthly: (year: number, month?: number, previewEmpId?: number) =>
    request.get('/me/att-monthly', {
      params: { year, ...(month ? { month } : {}), ...(previewEmpId ? { previewEmpId } : {}) },
    }),

  /** 月报可核对的真实日期明细（请假/出差/公出/加班）。 */
  attMonthlyDetails: (year: number, month: number, previewEmpId?: number) =>
    request.get('/me/att-monthly/details', {
      params: { year, month, ...(previewEmpId ? { previewEmpId } : {}) },
    }),

  /** 我的月度排班，员工身份由后端登录态确定。 */
  schedule: (yearMonth: string) =>
    request.get<any[]>('/me/schedule', { params: { yearMonth } }),

  /** P1: 我的调休余额 (COMP_OFF 加班累积 − 调休假使用). */
  compBalance: (previewEmpId?: number) =>
    request.get('/me/comp-balance', { params: { ...(previewEmpId ? { previewEmpId } : {}) } }),

  /** Phase A: 员工提交请假 (DRAFT). 强制本人, 不接受 previewEmpId. */
  createLeave: (req: any) => request.post('/me/leave-request', req),

  /** Phase A: 员工请假提交 DRAFT → PENDING. */
  submitLeave: (id: number) => request.post(`/me/leave-request/${id}/submit`),

  /** Phase B-1: 我的请假记录 (status 可空, 用于 leaveBalance.used 算). HR 可传 previewEmpId. */
  listMyLeaveRequests: (status?: string, previewEmpId?: number, applyType?: string) =>
    request.get('/me/leave-request', {
      params: { ...(status ? { status } : {}), ...(previewEmpId ? { previewEmpId } : {}), ...(applyType ? { applyType } : {}) },
    }),
  pageMyLeaveRequests: (current = 1, size = 20, status?: string, previewEmpId?: number) =>
    request.get('/me/leave-request/page', {
      params: { current, size, ...(status ? { status } : {}), ...(previewEmpId ? { previewEmpId } : {}) },
    }),

  /** Phase B-2: 我提交的档案变更 (V56 emp_change_request). HR 可传 previewEmpId. */
  listMyEmpChanges: (status?: string, previewEmpId?: number) =>
    request.get('/me/emp-change', {
      params: { ...(status ? { status } : {}), ...(previewEmpId ? { previewEmpId } : {}) },
    }),

  /** Phase B-2: 员工自助提交档案变更 (1 字段 1 次, DRAFT+submit 一步走). 强制本人. */
  submitMyEmpChange: (data: { fieldName: string; oldValue?: string; newValue: string; reason?: string }) =>
    request.post('/me/emp-change', data),

  /** Phase B-3: 我的补卡申请 (V53 punch_correction_request). HR 可传 previewEmpId. */
  listMyPunchCorrections: (status?: string, previewEmpId?: number) =>
    request.get('/me/punch-correction', {
      params: { ...(status ? { status } : {}), ...(previewEmpId ? { previewEmpId } : {}) },
    }),
  pageMyPunchCorrections: (current = 1, size = 20, status?: string, previewEmpId?: number) =>
    request.get('/me/punch-correction/page', {
      params: { current, size, ...(status ? { status } : {}), ...(previewEmpId ? { previewEmpId } : {}) },
    }),

  /** Phase B-3: 员工自助提交补卡申请 (DRAFT+submit 一步走). 强制本人. */
  submitMyPunchCorrection: (data: {
    correctDate: string;
    correctType?: string;
    actualIn?: string;
    actualOut?: string;
    reason: string;
    proofUrl?: string;
  }) => request.post('/me/punch-correction', data),

  /** 本人档案安全投影，联系方式默认脱敏. */
  profile: () => request.get('/me/profile'),

  /** 本人主动查看完整联系方式；接口不接受员工参数，后端固定取当前登录员工. */
  profileContact: () => request.post('/me/profile/contact/reveal', undefined, { silent: true }),

  /** 移动端二期: 内部通讯录安全投影. */
  directory: (params?: { keyword?: string; current?: number; size?: number }) =>
    request.get('/me/directory', { params: params || {} }),

  /** 移动端二期: 本人入职办理与步骤. */
  onboarding: () => request.get('/me/onboarding'),

  /** 移动端二期: 本人社保档案与月度核算. */
  social: () => request.get('/me/social'),

  leaveDetail: (id: number) => request.get(`/me/leave-request/${id}`),
  cancelLeave: (id: number) => request.post(`/me/leave-request/${id}/cancel`),
  requestLeaveCancellation: (id: number, reason: string) =>
    request.post(`/me/leave-request/${id}/cancellation`, { reason }),
  createTrip: (data: any) => request.post('/me/trip', data),
  createFieldWork: (data: any) => request.post('/me/field-work', data),

  listMyOvertime: (status?: string) =>
    request.get('/me/overtime', { params: status ? { status } : {} }),
  pageMyOvertime: (current = 1, size = 20, status?: string) =>
    request.get('/me/overtime/page', { params: { current, size, ...(status ? { status } : {}) } }),
  overtimeDetail: (id: number) => request.get(`/me/overtime/${id}`),
  // 由表单统一展示上下文错误，避免响应拦截器与页面各弹一次相同失败提示。
  createOvertime: (data: any) => request.post('/me/overtime', data, { silent: true }),
  submitOvertime: (id: number) => request.post(`/me/overtime/${id}/submit`, undefined, { silent: true }),
  cancelOvertime: (id: number) => request.post(`/me/overtime/${id}/cancel`),

  punchCorrectionDetail: (id: number) => request.get(`/me/punch-correction/${id}`),
  submitPunchCorrection: (id: number) => request.post(`/me/punch-correction/${id}/submit`),
  cancelPunchCorrection: (id: number) => request.post(`/me/punch-correction/${id}/cancel`),
}

export const dictApi = {
  get: (type: string) => request.get(`/dict/${type}`),
  batch: (types: string[]) => request.get('/dict/batch', { params: { types: types.join(',') } }),
}

/** 员工类型权威目录：业务表只保存稳定标准码，名称及联动属性由后端字典统一维护。 */
export const employeeTypeCatalogApi = {
  list: (includeDisabled = true) =>
    request.get('/md/employee-type', { params: { includeDisabled } }),
  create: (data: any) => request.post('/md/employee-type', data),
  update: (code: string, data: any) => request.put(`/md/employee-type/${encodeURIComponent(code)}`, data),
  setEnabled: (code: string, enabled: boolean) =>
    request.put(`/md/employee-type/${encodeURIComponent(code)}/status`, { enabled }),
}

// UAT-FB#122 (2026-06-18): 假勤管理顶部 KPI 真聚合
export const attDashboardApi = {
  todayOverview: () =>
    request.get<{
      total: number; normal: number; late: number; early: number; miss: number; absent: number;
      leave: number; trip: number; out: number; rest: number;
      pendingApprove: number; alertExceptions: number;
      isDemo: boolean; demoReason?: string;
    }>('/att/dashboard/today-overview'),
  // 2026-07-10: 今日运营详情真聚合 (打卡时间分布 + 各组织今日考勤), 替代前端两处 mock
  todayDetail: () =>
    request.get<{
      timeline: Array<{ hour: string; count: number }>;
      byLocation: Array<{ location: string; normal: number; late: number; early: number; miss: number; absent: number; leave: number; rate: number }>;
    }>('/att/dashboard/today-detail'),
}

// UAT-FB#122 (2026-06-18): 排班 真后端 API (合并: 网格视图 + 单员工/月视图)
//   网格:   GET /att/schedule-grid/list?startDate&endDate | POST /att/schedule-grid/batch
//   单员工: GET /att/schedule?empId&yearMonth | 全月: GET /att/schedule?yearMonth&orgId
//   生成:   POST /att/schedule/generate-month {year,month,empIds[]} | PUT/DELETE /att/schedule/{id}
//   注: V191 合并原两处重名声明 (fdad033 引入的网格 API 撞了 V43 经典 API), 修 vite build 报错
export const attScheduleApi = {
  // 网格看板 (Index.vue)
  list: (startDate: string, endDate: string, empIds?: number[]) =>
    request.get<any[]>('/att/schedule-grid/list', { params: { startDate, endDate, ...(empIds?.length ? { empIds: empIds.join(',') } : {}) } }),
  batch: (rows: Array<{ empId: number; schedule: Record<string, string> }>) =>
    request.post<{ saved: number; msg: string }>('/att/schedule-grid/batch', { rows }),
  // 单员工 / 月视图 (AttendanceCalendar / TeamCalendarHeatmap)
  listByEmp: (empId: number, yearMonth: string) =>
    request.get('/att/schedule', { params: { empId, yearMonth } }),
  listByMonth: (yearMonth: string, orgId?: number) =>
    request.get('/att/schedule', { params: { yearMonth, ...(orgId ? { orgId } : {}) } }),
  generateMonth: (data: { year: number; month: number; empIds: number[] }) =>
    request.post('/att/schedule/generate-month', data),
  update: (id: number, patch: any) => request.put(`/att/schedule/${id}`, patch),
  remove: (id: number) => request.delete(`/att/schedule/${id}`),
}

export const rbacApi = {
  users: (params: any) => request.get('/rbac/users', { params }),
  roles: () => request.get('/rbac/roles'),
  // P0-3: 角色增删改接真 (内置角色禁删, code 创建后不可改)
  createRole: (data: { code: string; name: string; scopeType?: string; description?: string; sort?: number }) =>
    request.post<number>('/rbac/role', data),
  /** active: V282 (2026-07-17) 停用真生效 (1=启用 0=停用); 内置角色传 0 会被后端拒 400 */
  updateRole: (id: number, data: { name?: string; scopeType?: string; description?: string; sort?: number; active?: number }) =>
    request.put(`/rbac/role/${id}`, data),
  deleteRole: (id: number) => request.delete(`/rbac/role/${id}`),
  // P0-1 阶段0: 真角色"模块级"权限 (落 rbac_role_perm → 驱动鉴权)
  getRoleModules: (id: number) => request.get<{ all: any[]; granted: string[]; locked: boolean }>(`/rbac/role/${id}/modules`),
  setRoleModules: (id: number, modules: string[]) => request.put(`/rbac/role/${id}/modules`, { modules }),
  // P1 阶段3: 真角色"动作级"权限精配 (落 rbac_role_perm 的 type='API', 配出"只读薪酬"这种角色)
  listActionPerms: () => request.get<any[]>('/rbac/action-perms'),
  getRoleActionPerms: (id: number) => request.get<{ granted: string[]; locked: boolean }>(`/rbac/role/${id}/action-perms`),
  setRoleActionPerms: (id: number, perms: string[]) => request.put(`/rbac/role/${id}/action-perms`, { perms }),
  // P1-E 方案Z: 权限矩阵面板接真 — 一次拉全 (roles + API perms + 每角色已授码)
  matrix: () => request.get<{ roles: any[]; perms: any[]; grantedByRole: Record<string, string[]> }>('/rbac/matrix'),
  submitPrivilegeChange: (data: { requestType: 'ROLE_MODULES' | 'ROLE_ACTION_PERMS'; roleId: number; codes: string[]; reason: string }) =>
    request.post<{ id: number }>('/rbac/privilege-change-requests', data),
  submitRoleScopeChange: (data: { roleId: number; scopeType: 'ALL'; reason: string }) =>
    request.post<{ id: number }>('/rbac/privilege-change-requests/role-scope', data),
  submitUserScopeChange: (data: { userId: number; scopeType: 'ALL'; reason: string }) =>
    request.post<{ id: number }>('/rbac/privilege-change-requests/user-scope', data),
  submitRoleBindingScopeChange: (data: { userId: number; roleCode: string; scopeType: 'ALL'; reason: string }) =>
    request.post<{ id: number }>('/rbac/privilege-change-requests/role-binding-scope', data),
  privilegeChangeRequests: (status?: string) =>
    request.get<any[]>('/rbac/privilege-change-requests', { params: status ? { status } : {} }),
  approvePrivilegeChange: (id: number, comment?: string) =>
    request.post(`/rbac/privilege-change-requests/${id}/approve`, { comment }),
  rejectPrivilegeChange: (id: number, comment: string) =>
    request.post(`/rbac/privilege-change-requests/${id}/reject`, { comment }),
  // 定期访问复核：批次快照、负责人确认/撤销；逾期由后端定时任务自动回收。
  createAccessReview: (data: { reviewType?: 'QUARTERLY' | 'AD_HOC'; dueAt: string }) =>
    request.post('/rbac/access-reviews', data),
  accessReviewCycles: () => request.get('/rbac/access-reviews'),
  accessReviewItems: (cycleId: number) =>
    request.get(`/rbac/access-reviews/${cycleId}/items`),
  confirmAccessReview: (itemId: number, reason?: string) =>
    request.post(`/rbac/access-reviews/items/${itemId}/confirm`, { reason }),
  revokeAccessReview: (itemId: number, reason: string) =>
    request.post(`/rbac/access-reviews/items/${itemId}/revoke`, { reason }),
  previewMasterDataRepair: () => request.get<any>('/system/diagnostics/master-data-repair/preview'),
  applyMasterDataRepair: (data: { items: Array<{ type: string; userId: number; roleId?: number }>; reason: string }) =>
    request.post<any>('/system/diagnostics/master-data-repair/apply', data),
  // P1-E 方案Z: 当前登录用户 API 动作码平面列表 (useCurrentUser 构造 modulePermissions 用)
  myActionPerms: () => request.get<string[]>('/rbac/my-action-perms'),
  perms: () => request.get('/rbac/perms'),
  menus: () => request.get('/rbac/menus'),
  // UAT-FB#107: 真后端账号管理
  usersWithEmp: () => request.get('/rbac/users-with-emp'),
  employeesForBinding: () => request.get('/rbac/employees-for-binding'),
  companyEmailDomain: () => request.get('/rbac/company-email-domain'),
  createUser: (data: {
    empId: number
    workEmail?: string
    roleCodes: string[]
    mfaEnabled?: boolean
    status?: number
    dataScope?: { scopeType: 'INHERIT' | 'ALL' | 'ORG' | 'EMP' | 'SELF'; orgIds?: number[]; empIds?: number[] }
  }) => request.post('/rbac/user', data, { silent: true }),
  updateUser: (id: number, data: {
    workEmail?: string
    roleCodes?: string[]
    mfaEnabled?: boolean
    status?: number
    dataScope?: { scopeType: 'INHERIT' | 'ALL' | 'ORG' | 'EMP' | 'SELF'; orgIds?: number[]; empIds?: number[] }
  }) => request.put(`/rbac/user/${id}`, data),
  updateAuthorization: (id: number, data: {
    authorizationVersion: number
    workEmail?: string
    roleCodes?: string[]
    mfaEnabled?: boolean
    status?: number
    dataScope?: { scopeType: 'INHERIT' | 'ALL' | 'ORG' | 'EMP' | 'SELF'; orgIds?: number[]; empIds?: number[] }
    roleBindingRoleCode?: string
    roleBindingScope?: { scopeType: 'INHERIT' | 'ALL' | 'ORG' | 'EMP' | 'SELF'; orgIds?: number[]; empIds?: number[] }
  }) => request.put(`/rbac/user/${id}/authorization`, data),
  userDataScope: (id: number) => request.get<{ scopeType: 'INHERIT' | 'ALL' | 'ORG' | 'EMP' | 'SELF'; orgIds: number[]; empIds: number[] }>(`/rbac/user/${id}/data-scope`),
  roleBindingDataScope: (userId: number, roleCode: string) => request.get<{ scopeType: 'INHERIT' | 'ALL' | 'ORG' | 'EMP' | 'SELF'; orgIds: number[]; empIds: number[] }>(`/rbac/user/${userId}/role/${encodeURIComponent(roleCode)}/data-scope`),
  updateRoleBindingDataScope: (userId: number, roleCode: string, data: { scopeType: 'INHERIT' | 'ALL' | 'ORG' | 'EMP' | 'SELF'; orgIds?: number[]; empIds?: number[] }) => request.put(`/rbac/user/${userId}/role/${encodeURIComponent(roleCode)}/data-scope`, data),
  resetUserPwd: (id: number) => request.post(`/rbac/user/${id}/reset-password`),
  toggleUserMfa: (id: number) => request.post(`/rbac/user/${id}/toggle-mfa`),
  resetUserMfa: (id: number) => request.post(`/rbac/user/${id}/reset-mfa`),
  setupMfa: () => request.post<{ secret: string; otpauthUri: string }>('/auth/mfa/setup'),
  confirmMfa: (code: string) => request.post('/auth/mfa/confirm', { code }),
  disableMfa: () => request.post('/auth/mfa/disable'),
  toggleUserActive: (id: number) => request.post(`/rbac/user/${id}/toggle-active`),
  deleteUser: (id: number) => request.delete(`/rbac/user/${id}`),
}

export const systemApi = {
  entities: () => request.get('/system/legal-entities'),
  seals: () => request.get('/system/seals'),
  integrations: () => request.get('/system/integrations'),
  roadmap: () => request.get('/system/roadmap'),
  settings: () => request.get('/system/settings'),
}

// ⭐ #8 系统日志/业务审计 真后端 (audit_log 表 + AuditAspect 已写真数据)
// type: SYSTEM (LOGIN/EXPORT 等) / BUSINESS (业务模块) / 不传=全部
export const auditLogApi = {
  page: (params: {
    type?: 'SYSTEM' | 'BUSINESS'
    bizModule?: string
    opType?: string
    opUsername?: string
    from?: string
    to?: string
    status?: 0 | 1
    page?: number
    size?: number
  }) => request.get('/audit-log', { params }),
  modules: () => request.get('/audit-log/modules'),
  opTypes: () => request.get('/audit-log/op-types'),
  /**
   * 按业务实体 (bizType + bizId) 拉审计流水.
   * 指令 3 配套: 面试详情底部"变更历史" 时间轴数据源.
   * 后端: GET /audit-log/by-biz?bizType=rec_interview&bizId={id}
   */
  byBiz: (bizType: string, bizId: number | string) =>
    request.get<any[]>('/audit-log/by-biz', { params: { bizType, bizId } }),
}

// P1-5: 临时授权 (接 proc 审批 + 到期回收)
export const tempGrantApi = {
  list: () => request.get<any[]>('/temp-grant'),
  apply: (data: any) => request.post<any>('/temp-grant', data),
  revoke: (id: number) => request.post(`/temp-grant/${id}/revoke`),
  // P1-7 员工自助权限申请
  selfApply: (data: any) => request.post<any>('/temp-grant/self-apply', data),
  mine: () => request.get<any[]>('/temp-grant/mine'),
  applicableRoles: () => request.get<Array<{ code: string; name: string }>>('/temp-grant/applicable-roles'),
  breakGlass: {
    trigger: (data: { witnessEmpNos: string[]; reason: string; durationHours: number }) =>
      request.post<any>('/break-glass', data),
    revoke: (id: number, reason?: string) => request.post(`/break-glass/${id}/revoke`, { reason }),
    listActive: () => request.get<any[]>('/break-glass/active'),
    mineActive: (config?: RequestConfig) => request.get<any | null>('/break-glass/active-mine', config),
    searchWitnesses: (keyword: string) =>
      request.get<Array<{ empNo: string; name: string }>>('/break-glass/witnesses', { params: { keyword } }),
  },
}

export const shiftSwapApi = {
  mine: () => request.get<any[]>('/me/shift-swaps'),
  counterpartPending: () => request.get<any[]>('/me/shift-swaps/counterpart'),
  candidates: (from?: string, to?: string) =>
    request.get<any[]>('/me/shift-swaps/candidates', { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } }),
  create: (data: { applicantScheduleId: number; targetScheduleId: number; reason: string }) =>
    request.post<any>('/me/shift-swaps', data),
  approveCounterpart: (id: number) => request.post<any>(`/me/shift-swaps/${id}/counterpart-approve`),
  rejectCounterpart: (id: number, reason: string) =>
    request.post<any>(`/me/shift-swaps/${id}/counterpart-reject`, { reason }),
  cancel: (id: number) => request.post<any>(`/me/shift-swaps/${id}/cancel`),
  pending: () => request.get<any[]>('/att/shift-swaps/pending'),
  reviewed: () => request.get<any[]>('/att/shift-swaps/reviewed'),
  approve: (id: number) => request.post<any>(`/att/shift-swaps/${id}/approve`),
  reject: (id: number, reason: string) => request.post<any>(`/att/shift-swaps/${id}/reject`, { reason }),
}

// P1-7: 合规/权限健康真扫
export const complianceApi = {
  scan: () => request.post<Array<{ code: string; name: string; category: string; severity: string; result: string; affectedCount: number; samples: string[]; recommendation: string }>>('/compliance/scan'),
}

export const attApi = {
  shifts: () => request.get('/att/shifts'),
  rules: () => request.get('/att/rules'),
  leaves: () => request.get('/att/leaves'),
  overview: () => request.get('/att/overview'),
  // ⭐ UAT-FB#101 (2026-06-18) Phase 2: 出差 / 公出 申请
  //   后端 V184: leave_request 加 apply_type (LEAVE/TRIP/FIELD_WORK) + extra JSON
  //   ⚠️ extra 是后端 String 字段 (存 JSON 文本), 调用方必须 JSON.stringify, 不能直接传对象!
  //     否则 Jackson 报 "Cannot deserialize String from Object" (2026-07-10 修的员工出差/公出提交崩溃).
  //   trip body:       { empId, startDate, endDate, extra: '{"destCity","destCountry","reason","proofFiles"}' }
  //   fieldWork body:  { empId, date, startTime, endTime, extra: '{"location","company","activity"}' }
  //   月度计算: TRIP/FIELD_WORK 期间打卡缺失自动豁免, 累计到 att_monthly.trip_days / field_work_days
  trip: (data: any) => request.post('/att/trip', data),
  fieldWork: (data: any) => request.post('/att/field-work', data),
  // 7.2.1 YGCS047: 出差/公出列表 - 复用 /att/leaves 加 applyType 过滤
  listTrips: (params?: { empId?: number; status?: string }) =>
    request.get<any[]>('/att/leaves', { params: { ...params, applyType: 'TRIP' } }),
  listReviewedTrips: () =>
    request.get<any[]>('/att/leaves/reviewed', { params: { applyType: 'TRIP' } }),
  listFieldWorks: (params?: { empId?: number; status?: string }) =>
    request.get<any[]>('/att/leaves', { params: { ...params, applyType: 'FIELD_WORK' } }),
  listReviewedFieldWorks: () =>
    request.get<any[]>('/att/leaves/reviewed', { params: { applyType: 'FIELD_WORK' } }),
  previewApproverRepair: () => request.get<any>('/att/approver-repair/preview'),
  applyApproverRepair: (data: { items: Array<{ type: string; id: number; currentApproverId?: number | null }>; reason: string }) =>
    request.post<any>('/att/approver-repair/apply', data),
  getApproverPolicy: () => request.get<any>('/att/approver-policy'),
  saveApproverPolicy: (data: { policy: { fallbackRoleCodes: string[] }; version?: number | null }) =>
    request.post<any>('/att/approver-policy', data),
}

// ⭐ V47 (2026-06-02): 班次配置 (AttShift) — 5 端点真后端 CRUD + toggle
//   后端: AttController /att/shifts (V43 commit fc3fdc7), 写操作需 @PreAuthorize hasAuthority('att')
//   entity 字段: id/code(UK)/name/type(NORMAL/FLEX/SHIFT/COMP)/workStart/workEnd(LocalTime)/
//                lunchStart/lunchEnd/workHours/punchRequired/flexMinutes/enabled/sortOrder/orgId
//   注意: list() 后端只返 enabled=1 (含已逻辑删除剔除), 前端 KPI "禁用" 显示从客户端不可见
export const attShiftApi = {
  list: () => request.get('/att/shifts'),
  create: (data: any) => request.post('/att/shifts', data),
  update: (id: number, data: any) => request.put(`/att/shifts/${id}`, data),
  toggle: (id: number) => request.put(`/att/shifts/${id}/toggle`),
  remove: (id: number) => request.delete(`/att/shifts/${id}`),
}

// (排班 单员工/月视图 API 已并入上方 attScheduleApi — V191 删除原重复声明, 修 vite build 重名报错)

// ⭐ V43 + F3 (2026-06-02): 月度考勤汇总 (AttMonthly) — 6 端点真后端
//   状态机: OPEN → CALCULATING → CONFIRMED → LOCKED
//   后端 entity 字段: empId/year/month/standardDays/actualDays/lateCount/earlyCount/
//                    absentDays/leaveDays/otWeekdayHours/otWeekendHours/otHolidayHours/
//                    deduction/status/confirmedAt/confirmedBy
//   端点定义: backend AttMonthlyController
export const attMonthlyApi = {
  get: (empId: number, year: number, month: number) =>
    request.get('/att/monthly', { params: { empId, year, month } }),
  listByMonth: (year: number, month: number, status?: string, config?: RequestConfig, includeRoster = false, fresh = false) =>
    request.get('/att/monthly/list', {
      ...config,
      // 导入完成后必须绕过浏览器/网关对 GET 的短暂缓存，确保列表立即读取刚提交的月账。
      params: {
        year,
        month,
        ...(status ? { status } : {}),
        ...(includeRoster ? { includeRoster: true } : {}),
        ...(fresh ? { _refresh: Date.now() } : {}),
      },
    }),
  latestPeriod: () =>
    request.get<{ year: number; month: number } | null>('/att/monthly/latest-period'),
  // 月度部门统计 (att/Index 真化): 按部门聚合 att_monthly, 真查库 @DataScope; year/month 省略取最新月; 无数据返 []
  deptStats: (year?: number, month?: number) =>
    request.get<any[]>('/att/monthly/dept-stats', { params: { ...(year ? { year } : {}), ...(month ? { month } : {}) } }),
  calculate: (data: { empId: number; year: number; month: number }) =>
    request.post('/att/monthly/calculate', data),
  batchCalculate: (data: { year: number; month: number; empIds?: number[] }) =>
    request.post<{
      total: number
      success: number
      failed: number
      errors: string[]
      startedAt?: string
      finishedAt?: string
    }>('/att/monthly/batch-calculate', data),
  confirm: (id: number) => request.post(`/att/monthly/${id}/confirm`),
  lock: (id: number) => request.post(`/att/monthly/${id}/lock`),
  lockMonth: (year: number, month: number, options?: { forceRemaining?: boolean; forceReason?: string }) =>
    request.post<{ forced: number; locked: number; total: number; status: string }>('/att/monthly/lock-month', {
      year,
      month,
      forceRemaining: options?.forceRemaining ?? false,
      forceReason: options?.forceReason,
    }),
  unlockMonth: (year: number, month: number, reason: string) =>
    request.post<{ unlocked: number; status: string }>('/att/monthly/unlock-month', { year, month, reason }),
  // V270 员工确认闭环
  // HR 首次推送或给已 PUSHED 员工补发提醒
  batchPush: (data: { year: number; month: number; empIds?: number[]; deadlineDays?: number }) =>
    request.post<{
      selected: number
      pushed: number
      reminded: number
      notified: number
      notificationFailed: number
      deadline: string
    }>('/att/monthly/batch-push', data),
  // 员工本人确认 (empId 由 token 解析)
  empConfirm: (id: number) => request.post<any>(`/att/monthly/${id}/emp-confirm`),
  // HR 强制确认
  forceConfirm: (id: number, reason: string) => request.post<any>(`/att/monthly/${id}/force-confirm`, { reason }),
  // 去 mock (2026-07-13): 一键催办真推送 — HR 给员工发考勤提醒, 真写 notify_message, 员工通知中心可见.
  //   返回 { sent: bool, msgId }; sent=false = 员工不在数据范围/未关联档案, 未送达.
  remind: (empId: number, message: string, title?: string) =>
    request.post<{ sent: boolean; msgId: number | null }>('/att/monthly/remind', { empId, message, ...(title ? { title } : {}) }),
  // 去 mock (2026-07-13): 导入月度考勤明细 upsert att_monthly (替代前端 batchImportAttendance 写 localStorage)
  importMonthly: (year: number, month: number, rows: any[]) =>
    request.post<{ ok: boolean; matched: number; unmatched: number; total: number; errors: string[] }>(
      '/att/monthly/import', { year, month, rows }),
}

// ⭐ V199 (2026-06-22): 月度考勤申诉 (AttMonthlyDispute) — 真后端
//   前端 attMonthlyStore.ts dispute 三函数从 localStorage 落后端 att_monthly_dispute.
//   - raise: 员工发起 (不卡权限)
//   - listActive: HR 审批中心待审 (@PreAuthorize att, @DataScope 拦截)
//   - byPeriod: 员工查自己某周期申诉
//   - resolve: HR 裁定 (APPROVED 按白名单写回 att_monthly)
//   - uploadEvidence: 上传附件 (multipart)
//   后端: AttMonthlyDisputeController /att/monthly-dispute
export const attDisputeApi = {
  raise: (data: {
    empId: number; year: number; month: number
    field: string; fieldLabel?: string
    expectedValue?: string | number; actualValue?: string | number; reason: string
  }) => request.post('/att/monthly-dispute', data),
  listActive: () => request.get('/att/monthly-dispute/active'),
  byPeriod: (empId: number, year?: number, month?: number) =>
    request.get('/att/monthly-dispute/by-period', {
      params: { empId, ...(year != null ? { year } : {}), ...(month != null ? { month } : {}) },
    }),
  // 2026-07-05 P0: resolvedBy 由后端从 UserContext 取 empId, 前端不再传 (传了后端也忽略)
  resolve: (id: number, data: { decision: 'APPROVED' | 'REJECTED'; comment?: string }) =>
    request.post(`/att/monthly-dispute/${id}/resolve`, data),
  uploadEvidence: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post(`/att/monthly-dispute/${id}/evidence`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

export const payApi = {
  schemes: () => request.get('/pay/schemes'),
  batches: () => request.get('/pay/batches'),
  archive: () => request.get('/pay/archive'),
  // ⭐ A2 v1: 后端算薪引擎 (Q3 SPEC + B-3 试用期倍率)
  // 输入: { empId?, structureId, totalSalary, workDays?, conditions? }
  // 输出: PayslipDto { rows, grossTotal, taxableTotal, netTotal, appliedProbationFactor, warnings }
  calculate: (req: {
    empId?: number
    structureId: number
    totalSalary: number
    workDays?: number
    conditions?: Record<string, boolean>
  }) => request.post('/pay/calculate', req),
}

// ⭐ V129 Phase B (2026-06-10): 浏览器自助打卡
//   - in/out: 上下班打卡 (前端只传定位事实，来源由后端固定为 HRMS_WEB)
//   - today: 今日 in/out 状态 (首页)
//   - history: 月度历史 (日历视图)
//   后端: AttMePunchController /api/me/punch/*
export interface PunchReadiness {
  ready: boolean
  code: string
  title: string
  message: string
  action: string
  empId?: number | null
  empNo?: string | null
  orgId?: number | null
  orgName?: string | null
  locationPolicyConfigured: boolean
  scheduledToday: boolean
  warnings: string[]
}

export const mePunchApi = {
  readiness: () => request.get<PunchReadiness>('/me/punch/readiness', { silent: true }),
  reverseGeocode: (lat: number, lng: number) =>
    request.get('/me/punch/location/address', { params: { lat, lng } }),
  in: (body: { lat?: number | null; lng?: number | null; address?: string }) =>
    request.post('/me/punch/in', body),
  out: (body: { lat?: number | null; lng?: number | null; address?: string }) =>
    request.post('/me/punch/out', body),
  today: () => request.get('/me/punch/today'),
  history: (year: number, month: number) =>
    request.get('/me/punch/history', { params: { year, month } }),
}

// ⭐ V129 Phase B (2026-06-10): GPS 打卡围栏 HR CRUD
//   后端: AttPunchFenceController /api/att/punch-fence
export const attPunchFenceApi = {
  list: (params?: { orgId?: number; enabled?: number }) =>
    request.get('/att/punch-fence', { params }),
  get: (id: number) => request.get(`/att/punch-fence/${id}`),
  create: (data: any) => request.post('/att/punch-fence', data),
  update: (id: number, data: any) => request.put(`/att/punch-fence/${id}`, data),
  toggle: (id: number) => request.put(`/att/punch-fence/${id}/toggle`),
  remove: (id: number) => request.delete(`/att/punch-fence/${id}`),
}

// V277 (2026-07-16): WiFi 打卡白名单 HR CRUD (bssid 命中视为在办公范围, 与围栏双校验)
export const attPunchWifiApi = {
  list: (params?: { orgId?: number; enabled?: number }) =>
    request.get('/att/punch-wifi', { params }),
  get: (id: number) => request.get(`/att/punch-wifi/${id}`),
  create: (data: any) => request.post('/att/punch-wifi', data),
  update: (id: number, data: any) => request.put(`/att/punch-wifi/${id}`, data),
  toggle: (id: number) => request.put(`/att/punch-wifi/${id}/toggle`),
  remove: (id: number) => request.delete(`/att/punch-wifi/${id}`),
}

// ⭐ V129 Phase B (2026-06-10): 实体打卡机 HR CRUD (海康威视等)
//   后端: AttPunchDeviceController /api/att/punch-device
//   注: 设备 webhook 自鉴权 (X-Device-Id + X-Api-Key 头), 不走前端 token.
export const attPunchDeviceApi = {
  list: (params?: { orgId?: number; enabled?: number }) =>
    request.get('/att/punch-device', { params }),
  get: (id: number) => request.get(`/att/punch-device/${id}`),
  create: (data: {
    deviceId: string
    deviceBrand?: string
    deviceModel?: string
    deviceLocation?: string
    orgId?: number
    enabled?: number
  }) => request.post('/att/punch-device', data),
  update: (id: number, data: any) => request.put(`/att/punch-device/${id}`, data),
  remove: (id: number) => request.delete(`/att/punch-device/${id}`),
  regenerateKey: (id: number) => request.post(`/att/punch-device/${id}/regenerate-key`),
}

// ⭐ V129 Phase B (2026-06-10): 月度考勤核算定时任务 (HR 可配置触发模式)
//   完全照搬 V125-P7 PayrollJob 模式, 改成核算考勤汇总.
//   后端: AttMonthlyJobController /api/att/monthly-job/*
export const attMonthlyJobApi = {
  run: (year: number, month: number) =>
    request.post('/att/monthly-job/run', { year, month }),
  list: (params?: { year?: number; month?: number }) =>
    request.get('/att/monthly-job/list', { params }),
  get: (jobId: number) => request.get(`/att/monthly-job/${jobId}`),
  progress: (jobId: number) => request.get(`/att/monthly-job/${jobId}/progress`),
  retry: (jobId: number, empId: number) =>
    request.post(`/att/monthly-job/${jobId}/retry/${empId}`),
  recoverStale: () => request.post('/att/monthly-job/recover-stale'),
  getConfig: () => request.get('/att/monthly-job/config'),
  updateConfig: (config: {
    triggerMode: 'MANUAL_ONLY' | 'AUTO_CRON'
    cronExpression?: string
    cronDescription?: string
    enabled?: number
  }) => request.put('/att/monthly-job/config', config),
}

// ⭐ V125 P0 上线 (2026-06-10): 全员 binding 回填 (Admin only)
//   - preview: 试算 (dryRun, 不写库) — 报告多少人成功/已绑跳过/缺工资档案
//   - execute: 真执行 (插 emp_salary_binding, source_type=BACKFILL_V125)
export const salaryBindingBackfillApi = {
  preview: (templateId?: number) =>
    request.post('/admin/salary-binding/backfill/preview', { templateId: templateId ?? null }),
  execute: (templateId?: number) =>
    request.post('/admin/salary-binding/backfill/execute', { templateId: templateId ?? null }),
}

// ⭐ V125-P7 (2026-06-09): 月度算薪定时任务 (HR 可配置触发模式)
//   - run: 手动触发月度算薪 (返 jobId, 异步跑)
//   - progress: 前端 3s 轮询进度
//   - list/get: 历史任务表 + 详情 (含 failed 明细)
//   - retry: 重试单员工
//   - config: 读/改 trigger_mode (MANUAL_ONLY / AUTO_CRON) + cron 表达式
export const payrollJobApi = {
  run: (year: number, month: number) => request.post('/payroll-job/run', { year, month }),
  list: (params?: { year?: number; month?: number }) =>
    request.get('/payroll-job/list', { params }),
  get: (jobId: number) => request.get(`/payroll-job/${jobId}`),
  progress: (jobId: number) => request.get(`/payroll-job/${jobId}/progress`),
  retry: (jobId: number, empId: number) =>
    request.post(`/payroll-job/${jobId}/retry/${empId}`),
  getConfig: () => request.get('/payroll-job/config'),
  updateConfig: (config: {
    triggerMode: 'MANUAL_ONLY' | 'AUTO_CRON'
    cronExpression?: string
    cronDescription?: string
    enabled?: number
  }) => request.put('/payroll-job/config', config),
}

// ⭐ P1.3 Q4 (2026-06-01): 调岗模块 (IMPACT 评估 + workflow 配置)
export const transferApi = {
  // UAT P0 (2026-06-11): 全员调岗列表 (emp"调动管理" tab 主表)
  list: (params?: { status?: string; limit?: number }) =>
    request.get('/transfer/list', { params: params || {} }),
  byEmp: (empId: number) => request.get(`/transfer/by-emp/${empId}`),
  get: (id: number) => request.get(`/transfer/${id}`),
  create: (data: any) => request.post('/transfer', data),
  /** 管理员直接调动：跳过审批，直接写入调动日志并同步员工任职信息。 */
  direct: (data: any) => request.post('/transfer/direct', data),
  submit: (id: number, config?: any) => request.post(`/transfer/${id}/submit`, undefined, config),
  rescheduleEffectiveDate: (id: number, data: { effectiveDate: string; reason: string }) =>
    request.put(`/transfer/${id}/effective-date`, data),
  evalImpact: (id: number, config?: any) => request.post(`/transfer/${id}/eval-impact`, undefined, config),
  advance: (id: number, config?: any) => request.post(`/transfer/${id}/advance`, undefined, config),
  reject: (id: number, reason: string, config?: any) => request.post(`/transfer/${id}/reject`, { reason }, config),
  remove: (id: number) => request.delete(`/transfer/${id}`),
}
export const transferWorkflowApi = {
  list: (params?: { transferType?: string; enabled?: number }) => request.get('/transfer-workflow-meta', { params }),
  create: (data: any) => request.post('/transfer-workflow-meta', data),
  update: (id: number, data: any) => request.put(`/transfer-workflow-meta/${id}`, data),
  remove: (id: number) => request.delete(`/transfer-workflow-meta/${id}`),
}

// ⭐ Q7.1 (2026-06-02): 员工导入字段配置 + CSV 模板 + 上传校验
export const empImportFieldMetaApi = {
  list: (enabled?: number) => request.get('/emp-import-field-meta', { params: { enabled } }),
  create: (data: any) => request.post('/emp-import-field-meta', data),
  update: (id: number, data: any) => request.put(`/emp-import-field-meta/${id}`, data),
  remove: (id: number) => request.delete(`/emp-import-field-meta/${id}`),
}

// ⭐ V152 Step 2 (2026-06-15): 学历字典 + 学习形式字典 + 教育规则配置
export const mdDegreeDictApi = {
  list: () => request.get<any[]>('/md/degree-dict'),
  listEnabled: () => request.get<any[]>('/md/degree-dict/enabled'),
  create: (data: any) => request.post<any>('/md/degree-dict', data),
  update: (id: number, data: any) => request.put<any>(`/md/degree-dict/${id}`, data),
  remove: (id: number) => request.delete<void>(`/md/degree-dict/${id}`),
}
export const mdEducationFormDictApi = {
  list: () => request.get<any[]>('/md/education-form-dict'),
  listEnabled: () => request.get<any[]>('/md/education-form-dict/enabled'),
  create: (data: any) => request.post<any>('/md/education-form-dict', data),
  update: (id: number, data: any) => request.put<any>(`/md/education-form-dict/${id}`, data),
  remove: (id: number) => request.delete<void>(`/md/education-form-dict/${id}`),
}
export const educationRuleApi = {
  get: () => request.get<any>('/md/education-rules'),
  update: (rules: any) => request.put<any>('/md/education-rules', rules),
}

// ⭐ V151 Step 1 MVP: 员工教育经历 + 第一/最高学历派生 (2026-06-15)
export const empEducationApi = {
  /** 按员工列出教育经历 (按入学日期升序) */
  listByEmp: (empId: number) => request.get<any[]>(`/emp-education/by-emp/${empId}`),
  /** 派生第一/最高学历 — 返回 { firstDegree, highestDegree } 各一个完整 EmpEducation 对象或 null */
  derived: (empId: number) => request.get<any>(`/emp-education/derived/${empId}`),
  get: (id: number) => request.get<any>(`/emp-education/${id}`),
  create: (data: any) => request.post<any>('/emp-education', data),
  update: (id: number, data: any) => request.put<any>(`/emp-education/${id}`, data),
  remove: (id: number) => request.delete<void>(`/emp-education/${id}`),
  /** 字典 (degree + form code/label/level) — Step 2 后移到 /md/degree-dict
   *  YGCS024 (2026-07-07): 响应体新增 schools: string[] (源 sys_kv cfg:emp.education.school.list),
   *  编辑员工"毕业学校"下拉数据源. 后端 EmpEducationController.dict() 有 DEFAULT_SCHOOLS 兜底. */
  dict: () => request.get<{ degrees: any[]; forms: any[]; schools?: string[] }>('/emp-education/dict'),
}

// ⭐ V196 (2026-06-21): 员工工作经历 真接 (仿 empEducationApi) — 详情抽屉「工作经历」tab
// 实体字段 empId/company/position/dateFrom/dateTo/leaveReason/witness/remark
export const empWorkExpApi = {
  /** 按员工列出工作经历 (后端按 id 倒序) */
  listByEmp: (empId: number) => request.get<any[]>(`/emp-work-exp/by-emp/${empId}`),
  create: (data: any) => request.post<any>('/emp-work-exp', data),
  update: (id: number, data: any) => request.put<any>(`/emp-work-exp/${id}`, data),
  remove: (id: number) => request.delete<void>(`/emp-work-exp/${id}`),
}

// ⭐ V196 (2026-06-21): 员工家庭成员 + 紧急联系人 真接 (同表 isEmergency 区分 0家庭/1紧急)
// 实体字段 empId/name/relation/age/job/phone/isEmergency/address/remark
export const empFamilyApi = {
  /** 按员工列出 (家庭+紧急混在一起, 前端按 isEmergency 分流) */
  listByEmp: (empId: number) => request.get<any[]>(`/emp-family/by-emp/${empId}`),
  create: (data: any) => request.post<any>('/emp-family', data),
  update: (id: number, data: any) => request.put<any>(`/emp-family/${id}`, data),
  remove: (id: number) => request.delete<void>(`/emp-family/${id}`),
}

export const empImportApi = {
  /** 下载模板 (浏览器自动触发下载, 返回 raw URL) */
  templateUrl: () => '/api/emp-import/template.csv',
  /**
   * YGCS016 (2026-07-01) 组4 B组P2: 加 mode 参数支持 3 种导入模式
   * @param mode INSERT_ONLY (默认) | UPSERT | PATCH_ONLY
   * @param format STANDARD (默认) | BEISEN — 北森导出文件 (后端固定 UPSERT)
   */
  upload: (
    file: File,
    dryRun = true,
    mode: 'INSERT_ONLY' | 'UPSERT' | 'PATCH_ONLY' = 'INSERT_ONLY',
    format: 'STANDARD' | 'BEISEN' = 'STANDARD',
  ) => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('dryRun', String(dryRun))
    fd.append('mode', mode)
    fd.append('format', format)
    return request.post('/emp-import/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// ⭐ P1.5 Q5 (2026-06-02): 离职模块 (6 阶段 + HR面谈跳过 + 协议补偿金 + 财务推送)
export const offboardApi = {
  // UAT P0 (2026-06-11): 全员离职列表 (emp"离职管理" tab 主表)
  list: (params?: { status?: string; limit?: number }) =>
    request.get('/offboard/list', { params: params || {} }),
  byEmp: (empId: number) => request.get(`/offboard/by-emp/${empId}`),
  get: (id: number) => request.get(`/offboard/${id}`),
  mine: () => request.get('/offboard/me'),
  selfApply: (data: { reason: string; expectLeave: string }) => request.post('/offboard/me', data),
  create: (data: any) => request.post('/offboard', data),
  direct: (data: any) => request.post('/offboard/direct', data),
  createDraft: (data: any) => request.post('/offboard/draft', data),
  update: (id: number, data: any) => request.put(`/offboard/${id}`, data),
  submit: (id: number) => request.post(`/offboard/${id}/submit`),
  skipHrInterview: (id: number, reason: string, config?: any) =>
    request.post(`/offboard/${id}/skip-hr-interview`, { reason }, config),
  setCompensation: (id: number, payload: { amount: number; basis: string; agreementUrl?: string; breakdown?: string }) =>
    request.post(`/offboard/${id}/compensation`, payload),
  // P1-4 (2026-06-21): 保存交接清单状态
  saveHandover: (id: number, handoverData: string, relationshipSuccessorEmpId?: number | null) =>
    request.post(`/offboard/${id}/handover`, { handoverData, relationshipSuccessorEmpId }),
  pushFinance: (id: number, config?: any) => request.post(`/offboard/${id}/push-finance`, undefined, config),
  advance: (id: number, config?: any) => request.post(`/offboard/${id}/advance`, undefined, config),
  reject: (id: number, reason: string, config?: any) => request.post(`/offboard/${id}/reject`, { reason }, config),
  revoke: (id: number, reason: string) => request.post(`/offboard/${id}/revoke`, { reason }),
  remove: (id: number) => request.delete(`/offboard/${id}`),
  batch: (data: {
    empIds: number[]; type: string; reason: string; applyDate: string; expectLeave: string;
    compensationMonths?: number; rehirable?: number; procTemplateCode: string;
  }) => request.post('/offboard/batch', data),
  batchJob: (jobId: string) => request.get(`/offboard/batch-job/${jobId}`),

  // W6-b (2026-06-04 凌晨): 离职证明 — markdown 内部预览 + docx 下载打印盖章
  certMarkdown: (id: number) => request.get(`/offboard-cert/${id}/markdown`),
  certDocx: (id: number, config?: any) =>
    request.get(`/offboard-cert/${id}/docx`, { ...config, responseType: 'blob' }),
}

// V67 (2026-06-04): 用户列偏好 (emp 列配置 P1, 后端 commit 77bcbdd 配套)
// V113 (2026-06-08): 加 reset (恢复默认)
// list 返 UserColumnPref[] 数组; save 覆盖式 (后端先 delete 后 insert)
export const userColumnPrefApi = {
  list: (pageKey: string) => request.get('/me/column-pref', { params: { pageKey } }),
  save: (data: { pageKey: string; prefs: { colKey: string; visible: number; sortOrder: number }[] }) =>
    request.post('/me/column-pref', data),
  reset: (pageKey: string) => request.delete('/me/column-pref', { params: { pageKey } }),
}

// V113 (2026-06-08): 员工 HR 自定义字段配置 (列设置 Phase C)
// list ?status=ENABLED — 列设置打开时调; HR 主数据 CRUD 用 create/update/remove
export const empFieldConfigApi = {
  list: (status?: 'ENABLED' | 'DISABLED') =>
    request.get('/emp/field-config', { params: status ? { status } : {} }),
  create: (data: { fieldKey: string; label: string; dataType: string; enumOptions?: string; defaultVisible?: number; sortOrder?: number; status?: string; remark?: string }) =>
    request.post('/emp/field-config', data),
  update: (id: number, data: any) => request.put(`/emp/field-config/${id}`, data),
  remove: (id: number) => request.delete(`/emp/field-config/${id}`),
}

// V300 (2026-07-22): 标准简历 v1 — 员工档案「招聘信息」tab.
// 模板/字段配置走 /onboard-doc-template (code=TPL-STD-RESUME); 这里只管取值/保存.
// 导出 GET /std-resume/{empId}/export 是 blob 下载, 在组件里用 request(responseType:'blob') 直调.
export const stdResumeApi = {
  get: (empId: number) => request.get(`/std-resume/${empId}`),
  save: (empId: number, values: Record<number, any>) =>
    request.put(`/std-resume/${empId}`, { values }),
}

// ⭐ Q3.1 v2.1 (2026-06-01): 补贴免税限额 (HR 按城市 × 补贴 code 配置)
export const subsidyLimitApi = {
  list: (params?: { cityCode?: string; subsidyCode?: string; enabled?: number }) =>
    request.get('/subsidy-limit-meta', { params }),
  lookup: (cityCode: string, subsidyCode: string, refDate?: string) =>
    request.get('/subsidy-limit-meta/lookup', { params: { cityCode, subsidyCode, refDate } }),
  create: (data: any) => request.post('/subsidy-limit-meta', data),
  update: (id: number, data: any) => request.put(`/subsidy-limit-meta/${id}`, data),
  remove: (id: number) => request.delete(`/subsidy-limit-meta/${id}`),
}

// ⭐ A2 v2 (2026-06-01): 算薪累计历史 (国税总局 2019 累计预扣法)
/** 已落库的员工月度工资历史；IMPORTED 行可能只有汇总、没有 itemsJson 逐项明细。 */
export interface PayrollHistoryRow {
  id: number
  empId: number
  year: number
  month: number
  grossTotal: number
  taxableTotal: number
  iitAmount: number
  attDeduction: number
  netTotal: number
  annualBonus: number
  itemsJson?: string | null
  specialDeductions: number
  source: 'CALCULATED' | 'IMPORTED' | string
  batchId?: number | null
  activityId?: number | null
  remark?: string | null
}

export const empAliasGovernanceApi = {
  check: (value: string, excludeEmpId?: number) =>
    request.get('/emp/alias-governance/check', { params: { value, excludeEmpId } }),
  required: (orgId: number) => request.get('/emp/alias-governance/required', { params: { orgId } }),
  rules: () => request.get('/emp/alias-governance/rules'),
  saveRule: (data: any) => request.put('/emp/alias-governance/rules', data),
}

export const payrollHistoryApi = {
  cumulative: (empId: number, year: number, month: number) =>
    request.get('/payroll-history/cumulative', { params: { empId, year, month } }),
  byEmp: (empId: number, year: number) =>
    request.get<PayrollHistoryRow[]>(`/payroll-history/by-emp/${empId}`, {
      params: { year },
      silent: true,
    }),
  importOne: (row: any) => request.post('/payroll-history/import', row),
  importBatch: (rows: any[]) => request.post('/payroll-history/import-batch', rows),
  remove: (id: number) => request.delete(`/payroll-history/${id}`),
}

// ⭐ A1 (2026-06-01): 薪酬项字典 (Q3.2: isTaxable + isInNet)
// XCCS017 (V256): 四档计算类型 calcType(FIXED|RATIO|FORMULA|INPUT) + calcConfig(JSON) + disabled 停用
//   list ?includeDisabled=false(默认, 包编辑选项目) / true(字典管理页全量)
//   POST /{id}/disable /{id}/enable — 停用后新包不可选, 已在包内 (binding 快照) 不受影响
export const salaryItemMetaApi = {
  list: (params?: { category?: string; enabled?: number; includeDisabled?: boolean }) =>
    request.get('/salary-item-meta', { params }),
  get: (id: number) => request.get(`/salary-item-meta/${id}`),
  create: (data: any) => request.post('/salary-item-meta', data),
  update: (id: number, data: any) => request.put(`/salary-item-meta/${id}`, data),
  remove: (id: number) => request.delete(`/salary-item-meta/${id}`),
  disable: (id: number) => request.post(`/salary-item-meta/${id}/disable`),
  enable: (id: number) => request.post(`/salary-item-meta/${id}/enable`),
  // XCCS017 收尾 · 双字典断层: 包编辑"薪资项"下拉合并视图 —
  //   META = salary_item_meta 未停用项 (componentId=同 code pay_component.id, 无镜像为 null)
  //   LEGACY = pay_component 里 code 不在 meta 的启用老项 (旧包沿用)
  componentOptions: () =>
    request.get<any, Array<{ componentId: number | null; metaId: number | null; code: string; name: string; category: string; source: 'META' | 'LEGACY'; sortOrder?: number }>>(
      '/salary-item-meta/component-options',
    ),
  // META 项无 pay_component 镜像时按需桥接 1 行 (幂等); 节点仍存 pay_component.id, 引擎解析零改动
  ensureComponent: (id: number) => request.post(`/salary-item-meta/${id}/ensure-component`),
}

// ⭐ XCCS017 (V257): 薪资档案匹配规则 (自动套薪) — 后端 SalaryMatchRuleController @ /pay/match-rule
//   4 维条件 (empType/level/workCity/positionId, NULL=不限), priority ASC 命中第一条 →
//   建议薪资包 templateId + 建议月薪 suggestTotalSalary (NULL=用包默认).
//   命中结果只进 salary_pending_change 待调整区, 人工确认后生效, 不直接改任何员工工资.
export const payMatchRuleApi = {
  list: (enabled?: number) =>
    request.get<any, any[]>('/pay/match-rule/list', { params: enabled != null ? { enabled } : {} }),
  create: (data: any) => request.post<any>('/pay/match-rule', data),
  /** 更新 (条件 4 维全量替换语义: 传 null = 不限/清空) */
  update: (id: number, data: any) => request.put<any>(`/pay/match-rule/${id}`, data),
  toggle: (id: number) => request.post<any>(`/pay/match-rule/${id}/toggle`),
  /** 批量重排 priority (上/下移保存), body [{id, priority}], 返变更行数 */
  reorder: (items: Array<{ id: number; priority: number }>) =>
    request.put<any, number>('/pay/match-rule/priority', items),
  /** 测试匹配 → {empId, emp{name/empNo/empType/level/workCity/positionId}, matched, hit{...}, trace[{ruleId,name,priority,enabled,matched,reason}]} */
  test: (empId: number) => request.post<any>('/pay/match-rule/test', { empId }),
}

// ⭐ XCCS014 M1 (V259): 发薪方案 — 后端 PayrollPlanController @ /pay/payroll-plan
//   scopeJson = 9 维人员范围 {legalEntityIds,orgIds,empTypes,statuses,positionIds,levels,
//   joinDateFrom,joinDateTo,requireSalaryProfile} · datasetsJson = ["PROFILE","ATTENDANCE","SOCIAL"]
//   items 全量替换语义 (每次保存传完整清单); itemName/category 服务端按字典快照回填.
export const payrollPlanApi = {
  /** 分页列表 (name 模糊 + status=ENABLED|DISABLED 精确) → Page<PayrollPlan> */
  page: (params?: { current?: number; size?: number; name?: string; status?: string }) =>
    request.get<any>('/pay/payroll-plan', { params }),
  /** 详情 → {plan, items[]} */
  detail: (id: number) => request.get<any>(`/pay/payroll-plan/${id}`),
  create: (data: any) => request.post<any>('/pay/payroll-plan', data),
  /** 更新 (plan+items 全量替换; effectiveTo 传 null 清空) */
  update: (id: number, data: any) => request.put<any>(`/pay/payroll-plan/${id}`, data),
  /** 复制 (name 加"副本", 复制体置 DISABLED 防同范围双启用) */
  copy: (id: number) => request.post<any>(`/pay/payroll-plan/${id}/copy`),
  disable: (id: number) => request.post<any>(`/pay/payroll-plan/${id}/disable`),
  enable: (id: number) => request.post<any>(`/pay/payroll-plan/${id}/enable`),
  /** 软删 (被发薪活动引用则拒) */
  remove: (id: number) => request.delete(`/pay/payroll-plan/${id}`),
  /** 人员范围预览 (按已保存 scopeJson 算) → Page<{empId,empNo,name,orgPath,posName,empType,status,level,joinDate,hasProfile}> */
  previewEmployees: (id: number, page = 1, size = 20) =>
    request.post<any>(`/pay/payroll-plan/${id}/preview-employees`, null, { params: { page, size } }),
}

// ⭐ XCCS014 M1 (V260/V261): 发薪活动 — 后端 PayrollActivityController @ /pay/payroll-activity
//   状态机: DRAFT→SYNCED→CALCULATING→CALCULATED→SEALED; 非 SEALED 可 CANCELLED.
//   创建即冻结方案快照 planSnapshotJson, 同步/核算全基于快照.
export const payrollActivityApi = {
  /** 分页列表 (payMonth/planId/status 可选过滤) → Page<PayrollActivity> */
  list: (params?: { current?: number; size?: number; payMonth?: string; planId?: number; status?: string }) =>
    request.get<any>('/pay/payroll-activity', { params }),
  /** 详情 (含 planSnapshotJson) */
  get: (id: number) => request.get<any>(`/pay/payroll-activity/${id}`),
  /** 活动人员明细分页. abnormalOnly=true 只看异常; 默认隐藏 REMOVED (要看传 status=REMOVED) */
  emps: (id: number, params?: { current?: number; size?: number; abnormalOnly?: boolean; status?: string }) =>
    request.get<any>(`/pay/payroll-activity/${id}/emps`, { params }),
  /** 创建 (status=DRAFT); 同 payMonth+planId 非 CANCELLED 已存在则拒 */
  create: (data: { name?: string; payMonth: string; payDate?: string | null; planId: number; remark?: string }) =>
    request.post<any>('/pay/payroll-activity', data),
  /** 数据同步. mode=NEW_ONLY 只补新人 | ALL(默认) 覆盖未手改 | FORCE 全覆盖 (独立审计) */
  sync: (id: number, mode: 'NEW_ONLY' | 'ALL' | 'FORCE' = 'ALL') =>
    request.post<any>(`/pay/payroll-activity/${id}/sync`, null, { params: { mode } }),
  /** 作废 (SEALED 不可作废) */
  cancel: (id: number) => request.post<any>(`/pay/payroll-activity/${id}/cancel`),
  /** 人工移除 (置 REMOVED 留痕, FORCE 同步/补人可复活) */
  removeEmp: (id: number, empId: number) =>
    request.post<any>(`/pay/payroll-activity/${id}/emp/${empId}/remove`),
  /** 人工补人 (REMOVED 行复活并重拍快照) */
  addEmp: (id: number, empId: number) =>
    request.post<any>(`/pay/payroll-activity/${id}/emp/add`, null, { params: { empId } }),
  /** 全员核算 (前置 SYNCED|CALCULATED) */
  calcAll: (id: number) => request.post<any>(`/pay/payroll-activity/${id}/calc`),
  /** 单人重算 */
  calcOne: (id: number, empId: number) => request.post<any>(`/pay/payroll-activity/${id}/calc/${empId}`),
  /** 手工调整单项 (仅方案快照 allowManualEdit=1 项目; 联动项自动重算) */
  manualAdjust: (id: number, empId: number, data: { itemCode: string; amount: number; reason: string }) =>
    request.post<any>(`/pay/payroll-activity/${id}/emp/${empId}/manual-adjust`, data),
  /** 封存前确认汇总 → {empCount, grossSum, deductSum, netSum, blockCount, warnCount} */
  sealSummary: (id: number) => request.get<any>(`/pay/payroll-activity/${id}/seal-summary`),
  /** 封存 (前置 CALCULATED + 无阻断异常; 逐人写 payroll_history, 单事务) */
  seal: (id: number) => request.post<any>(`/pay/payroll-activity/${id}/seal`),
  /** 发布工资条，打开员工自助可见门禁 */
  publish: (id: number) => request.post<any>(`/pay/payroll-activity/${id}/publish`),
  /** 撤回工资条，仅关闭员工自助可见性，不改变封存底册 */
  unpublish: (id: number) => request.post<any>(`/pay/payroll-activity/${id}/unpublish`),
  /**
   * AI 侦探: 月度薪酬分析 (前置 CALCULATED|SEALED) → {markdown, generatedAt, model, cached}.
   * 姓名脱敏喂 AI, 金额原值; 同数据版本走 sys_kv 缓存, force=true 强制重生成.
   * silent: 失败不弹全局 toast — 抽屉内统一渲染错误/未配置引导 (msg 含 "AI 引擎未配置").
   */
  aiAnalyze: (id: number, force = false) =>
    request.post<any>(`/pay/payroll-activity/${id}/ai-analyze`, null, {
      params: { force }, silent: true, timeout: 90000,
    } as any),
}

// ⭐ XCCS014 M1 波3: 发薪结果 — 后端 PayrollResultController @ /pay/payroll-result (只读, 仅 SEALED 活动)
export const payrollResultApi = {
  /** 结果列表 (活动粒度) → Page<{activityId,name,payMonth,planId,planName,empCount,grossSum,deductSum,netSum,sealedAt,sealedByName}> */
  list: (params?: { current?: number; size?: number; payMonth?: string; planId?: number }) =>
    request.get<any>('/pay/payroll-result', { params }),
  /** 活动人员结果分页 (轻量列, 无 snapshot_json/items_json 大字段) */
  emps: (activityId: number, params?: { current?: number; size?: number }) =>
    request.get<any>(`/pay/payroll-result/${activityId}/emps`, { params }),
  /** 单人结果 → {emp, items[], manualAdjusts[], trace{activityName,planName,firstSyncAt,calcDoneAt,sealedAt,sealedByName}} */
  empDetail: (activityId: number, empId: number) =>
    request.get<any>(`/pay/payroll-result/${activityId}/emp/${empId}`),
  /** 已封存活动员工级发薪结果 xlsx，后端按 pay:export + 数据范围鉴权。 */
  exportXlsx: (activityId: number) =>
    request.get<Blob>(`/pay/payroll-result/${activityId}/export.xlsx`, { responseType: 'blob' }),
}

export const socialApi = {
  policies: () => request.get('/social/policies'),
  overview: () => request.get('/social/overview'),
  // P1-A (2026-07-07): SocialAnalysisController @ /social
  //   /social/stats  -> { month, totalCost, yoy, mom, perCapita, headcount, pendingPolicy }
  //   /social/alerts -> [{ id, icon, level, title, value, unit, desc, action }]
  stats: () => request.get<any>('/social/stats'),
  alerts: () => request.get<any[]>('/social/alerts'),
}

// ⭐ V64 (2026-06-03): 招聘 4 张表真后端 — W7-rec2 前端切真接
// 后端 Controller URL 路径 (已读 Controller 源 @RequestMapping 确认, 非任务描述):
//   RecJdTemplateController @ /rec-jd
//   RecCandidateController @ /rec-candidate
//   RecInterviewController @ /rec-interview
//   RecOfferController @ /rec-offer
// 注意:
//   1) 所有写操作 @PreAuthorize hasAuthority('rec'), 写带 @AuditLog
//   2) 候选人 mobile/idNo 走 SM4: list/get 只返 mask, 明文要单独调 mobile()/idNo() (审计 DECRYPT)
//   3) Offer 8 态: DRAFT→PENDING→APPROVED→SENT→ACCEPTED/REJECTED/EXPIRED, 含 bindOnboard
//   4) Interview reschedule: 原行 RESCHEDULED + 新行 SCHEDULED
export const recApi = {
  // 旧 stub (保留兼容, 实际数据走下面 4 子组)
  demands: () => request.get('/rec/demands'),
  candidates: () => request.get('/rec/candidates'),
  offers: () => request.get('/rec/offers'),

  // ─── Offer 主记录 (V64 真后端) ───
  // UAT-FB#94 (2026-06-17) 修路径错: 后端 @RequestMapping("/rec-offer") 单段, 老前端写了 /rec/offer 双段 → 404 "No static resource"
  recOffer: {
    getByNo: (no: string) => request.get<any>(`/rec-offer/by-no/${encodeURIComponent(no)}`),
  },

  // ─── Offer 附件 + 接受 (V136) ───
  offerFile: {
    upload(offerNo: string, file: File): Promise<any> {
      const fd = new FormData()
      fd.append('file', file)
      return request.post(`/rec/offer/${offerNo}/file/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    downloadUrl(offerNo: string): string {
      return `/api/rec/offer/${encodeURIComponent(offerNo)}/file/download`
    },
    previewUrl(offerNo: string): string {
      return `/api/rec/offer/${encodeURIComponent(offerNo)}/file/preview`
    },
    accept(offerNo: string, signatureType: 'ELECTRONIC' | 'OFFLINE_SCAN') {
      return request.post<any>(`/rec/offer/${offerNo}/accept`, { signatureType })
    },
    logs(offerNo: string) {
      return request.get<any[]>(`/rec/offer/${offerNo}/file/logs`)
    },
  },

  // ─── 招聘需求 rec_demand (V147 — 编制→招聘联动真后端) ───
  demand: {
    list: (config?: any) => request.get<any[]>('/rec-demand', config),
    listByOrg: (orgId: number) => request.get<any[]>(`/rec-demand/by-org/${orgId}`),
    listByStatus: (status: string) => request.get<any[]>(`/rec-demand/by-status/${status}`),
    get: (id: number) => request.get<any>(`/rec-demand/${id}`),
    getByNo: (no: string) => request.get<any>(`/rec-demand/by-no/${encodeURIComponent(no)}`),
    create: (data: any) => request.post<any>('/rec-demand', data),
    /** 编制管理"🚀发起招聘"调这个 — 直接进 RECRUITING, OrgHeadcount.recruiting 自动 +headcount */
    createFromHeadcount: (data: {
      orgId: number
      positionId?: number
      posName?: string
      headcount: number
      reasonType?: string
      urgency?: string
      description?: string
    }) => request.post<any>('/rec-demand/from-headcount', data),
    update: (id: number, data: any) => request.put<any>(`/rec-demand/${id}`, data),
    close: (id: number, reason?: string) => request.post<any>(`/rec-demand/${id}/close`, { reason }),
    complete: (id: number) => request.post<any>(`/rec-demand/${id}/complete`),
    // S3: 状态流转 + 操作日志 (对应后端 S1/S2 端点)
    submit: (id: number) => request.post<any>(`/rec-demand/${id}/submit`),
    approve: (id: number) => request.post<any>(`/rec-demand/${id}/approve`),
    reject: (id: number, reason?: string) => request.post<any>(`/rec-demand/${id}/reject`, { reason }),
    start: (id: number) => request.post<any>(`/rec-demand/${id}/start`),
    pause: (id: number, reason?: string, resumeDate?: string) => request.post<any>(`/rec-demand/${id}/pause`, { reason, resumeDate }),
    resume: (id: number) => request.post<any>(`/rec-demand/${id}/resume`),
    remove: (id: number) => request.delete<any>(`/rec-demand/${id}`),
    clone: (id: number) => request.post<any>(`/rec-demand/${id}/clone`),
    logs: (id: number) => request.get<any[]>(`/rec-demand/${id}/logs`),
  },

  // ─── JD 模板 (rec_jd_template) ───
  jd: {
    list: () => request.get<any[]>('/rec-jd'),
    page: (params: { current?: number; size?: number; keyword?: string; status?: string }) =>
      request.get('/rec-jd/page', { params }),
    get: (id: number) => request.get<any>(`/rec-jd/${id}`),
    getByCode: (code: string) => request.get<any>(`/rec-jd/by-code/${code}`),
    create: (data: any) => request.post<any>('/rec-jd', data),
    update: (id: number, data: any) => request.put<any>(`/rec-jd/${id}`, data),
    activate: (id: number) => request.post<any>(`/rec-jd/${id}/activate`),
    archive: (id: number) => request.post<any>(`/rec-jd/${id}/archive`),
    reopen: (id: number) => request.post<any>(`/rec-jd/${id}/reopen`),
  },

  // ─── V132 段 C: 员工内推 (rec_referral_code + reward_rule) ───
  referral: {
    /** 幂等生成内推码 (同 emp+jd 复用) */
    genCode: (empId: number, jdId: number) =>
      request.post<any>('/rec-referral/gen-code', null, { params: { empId, jdId } }),
    listByEmp: (empId: number) =>
      request.get<any[]>(`/rec-referral/by-emp/${empId}`),
    findByCode: (code: string) =>
      request.get<any>(`/rec-referral/by-code/${code}`),
    /** 员工内推汇总: codeCount/totalViews/totalApplies/totalHires */
    statsForEmp: (empId: number) =>
      request.get<any>(`/rec-referral/stats/emp/${empId}`),
    incView: (code: string) => request.post<void>(`/rec-referral/view/${code}`),
    incApply: (code: string) => request.post<void>(`/rec-referral/apply/${code}`),
    incHire: (code: string) => request.post<void>(`/rec-referral/hire/${code}`),
    /** 奖金规则字典 */
    listRules: () => request.get<any[]>('/rec-referral/rules'),
    listEnabledRules: () => request.get<any[]>('/rec-referral/rules/enabled'),
    updateRule: (id: number, data: any) =>
      request.put<any>(`/rec-referral/rules/${id}`, data),
    /** 招聘 P0 接真: 内推佣金报表 (规则驱动金额 + 每人合计 + 全局合计) */
    commission: () => request.get<any>('/rec-referral/commission'),
    /** 招聘 P0 接真: 内推排行 (按 referrer 聚合 提交/录用/转化率, 倒序) */
    ranking: () => request.get<any[]>('/rec-referral/ranking'),
    /** 招聘 P0 接真: 撞单 (同 hash 被 ≥2 不同 referrer 提交) */
    conflicts: () => request.get<any[]>('/rec-referral/conflicts'),
  },

  // ─── 人才库 (rec_talent_pool, V200 第1期: 回收池/被动储备容器) ───
  talentPool: {
    /** 列表 (status/sourceCategory/keyword 可选过滤; DataScope 自动 org 隔离) */
    list: (params?: { status?: string; sourceCategory?: string; keyword?: string }) =>
      request.get<any[]>('/rec-talent-pool', { params: params || {} }),
    get: (id: number) => request.get<any>(`/rec-talent-pool/${id}`),
    create: (body: any) => request.post<any>('/rec-talent-pool', body),
    update: (id: number, body: any) => request.put<any>(`/rec-talent-pool/${id}`, body),
    remove: (id: number) => request.delete<void>(`/rec-talent-pool/${id}`),
    archive: (id: number) => request.post<any>(`/rec-talent-pool/${id}/archive`),
    /** 回收: 从候选人入池 (reason 可选) */
    fromCandidate: (candidateId: number, reason?: string) =>
      request.post<any>(`/rec-talent-pool/from-candidate/${candidateId}`, { reason }),
    /** 再激活: 转回招聘流程 (RECYCLE 复用候选人 reactivate) */
    reactivate: (id: number) => request.post<any>(`/rec-talent-pool/${id}/reactivate`),
    // ─ 第2期: 跟进计划 + 人才地图 ─
    /** 某人才的跟进记录 */
    followups: (id: number) => request.get<any[]>(`/rec-talent-pool/${id}/followups`),
    addFollowup: (body: any) => request.post<any>('/rec-talent-pool/followup', body),
    deleteFollowup: (id: number) => request.delete<void>(`/rec-talent-pool/followup/${id}`),
    /** 跟进提醒: 到期待联系的人才 */
    reminders: () => request.get<any[]>('/rec-talent-pool/reminders'),
    /** 人才地图: byLevel/byRegion/bySource 储备统计 */
    talentMap: () => request.get<any>('/rec-talent-pool/map'),
  },

  // ─── V132 段 B: 平台发布 (rec_jd_publish_log) ───
  // 6 channel: BOSS / ZHILIAN / QIANCHENG / REFERRAL / WEBSITE / WECHAT
  jdPublish: {
    /** 渲染指定平台的文案 (Phase 1 半自动: 用户复制走) */
    preview: (jdId: number, channel: string) =>
      request.get<{ channel: string; channelLabel: string; jdTitle: string; content: string; charCount: number; hint: string }>(
        '/rec-jd-publish/preview', { params: { jdId, channel } }
      ),
    /** 某 JD 的发布记录 */
    listByJd: (jdId: number) =>
      request.get<any[]>(`/rec-jd-publish/by-jd/${jdId}`),
    /** 登记一条发布 */
    create: (data: { jdId: number; channel: string; status?: string; externalId?: string; notes?: string }) =>
      request.post<any>('/rec-jd-publish', data),
    /** 更新投递/查看/面试数 */
    updateStats: (id: number, params: { views?: number; applies?: number; interviews?: number }) =>
      request.put<any>(`/rec-jd-publish/${id}/stats`, null, { params }),
    /** 下线 */
    offline: (id: number) => request.post<any>(`/rec-jd-publish/${id}/offline`),
    /** 按渠道汇总 (jdId 留空 = 全局) */
    stats: (jdId?: number) =>
      request.get<any[]>('/rec-jd-publish/stats', { params: jdId ? { jdId } : {} }),
  },

  // ─── 候选人 (rec_candidate) ───
  candidate: {
    list: () => request.get<any[]>('/rec-candidate'),
    page: (params: { current?: number; size?: number; keyword?: string; status?: string }) =>
      request.get('/rec-candidate/page', { params }),
    get: (id: number) => request.get<any>(`/rec-candidate/${id}`),
    getByNo: (no: string) => request.get<any>(`/rec-candidate/by-no/${no}`),
    decryptMobile: (id: number, reason: string) =>
      request.get<{ mobile: string }>(`/rec-candidate/${id}/mobile`, { params: { reason } }),
    decryptIdNo: (id: number, reason: string) =>
      request.get<{ idNo: string }>(`/rec-candidate/${id}/id-no`, { params: { reason } }),
    create: (data: { entity: any; plainMobile?: string; plainIdNo?: string }) =>
      request.post<any>('/rec-candidate', data),
    update: (id: number, data: { entity: any; plainMobile?: string; plainIdNo?: string }) =>
      request.put<any>(`/rec-candidate/${id}`, data),
    /** move 状态机推进 (toStatus 字符串, query 传). V110: 可选 roundNo (INTERVIEWING 时区分 1/2/3 轮) */
    move: (id: number, toStatus: string, roundNo?: number) =>
      request.post<any>(`/rec-candidate/${id}/move`, null, {
        params: roundNo != null ? { to: toStatus, roundNo } : { to: toStatus },
      }),
    reactivate: (id: number) => request.post<any>(`/rec-candidate/${id}/reactivate`),
    /** V201: 暂缓 (不改 status, 仅置 paused) / 解除暂缓 */
    hold: (id: number, body: { reason?: string; resumeDate?: string; note?: string }) =>
      request.post<any>(`/rec-candidate/${id}/hold`, body),
    unhold: (id: number) => request.post<any>(`/rec-candidate/${id}/unhold`),
    /** V111: 待 Offer 候选人 (终试通过/OFFERED 但还没建过 Offer) — Offer Tab 顶部区块用 */
    listPendingOffer: () => request.get<any[]>('/rec-candidate/pending-offer'),
    /**
     * 加分件 A: 候选人 ↔ 招聘需求 AI 匹配评分.
     * 后端 RecJobMatchController @ POST /rec-match/candidate/{id}?demandId=
     * 返 { available, score, dimensions:[{name,score,comment}], summary, redFlags:[], source, model, msg,
     *      candidateNameMasked, demandPosName }. AI 未配置/失败时 available=false + msg 引导.
     * silent: 失败不弹全局 toast — dialog 内按 available/msg 统一渲染降级引导.
     */
    match: (candidateId: number, demandId: number) =>
      request.post<any>(`/rec-match/candidate/${candidateId}`, null, {
        params: { demandId }, silent: true, timeout: 90000,
      } as any),
  },

  // ─── 面试日程 (rec_interview) ───
  interview: {
    /**
     * 面试日程列表.
     * P1-G (2026-07): 支持 ownerScope 参数 —
     *   MINE_INTERVIEWING (我作为面试官) / MINE_SCHEDULED (我安排的, 后端暂降级同 INTERVIEWING).
     * 空/缺省 = 全量, 走 @DataScope.
     */
    list: (params?: { ownerScope?: 'MINE_SCHEDULED' | 'MINE_INTERVIEWING' }) =>
      request.get<any[]>('/rec-interview', { params }),
    page: (params: { current?: number; size?: number; candidateId?: number; status?: string }) =>
      request.get('/rec-interview/page', { params }),
    get: (id: number) => request.get<any>(`/rec-interview/${id}`),
    getByNo: (no: string) => request.get<any>(`/rec-interview/by-no/${no}`),
    listByCandidate: (id: number) => request.get<any[]>(`/rec-interview/by-candidate/${id}`),
    create: (data: any) => request.post<any>('/rec-interview', data),
    update: (id: number, data: any) => request.put<any>(`/rec-interview/${id}`, data),
    start: (id: number) => request.post<any>(`/rec-interview/${id}/start`),
    complete: (id: number, data?: { feedback?: string; score?: number; nextRoundAdvice?: string }) =>
      request.post<any>(`/rec-interview/${id}/complete`, data || {}),
    cancel: (id: number, reason?: string) =>
      request.post<any>(`/rec-interview/${id}/cancel`, reason ? { reason } : {}),
    reschedule: (id: number, newScheduledAt: string) =>
      request.post<any>(`/rec-interview/${id}/reschedule`, { newScheduledAt }),
  },

  // ─── Offer (rec_offer, 8 态) ───
  offer: {
    list: () => request.get<any[]>('/rec-offer'),
    page: (params: { current?: number; size?: number; candidateId?: number; status?: string }) =>
      request.get('/rec-offer/page', { params }),
    get: (id: number) => request.get<any>(`/rec-offer/${id}`),
    getByNo: (no: string) => request.get<any>(`/rec-offer/by-no/${no}`),
    listByCandidate: (id: number) => request.get<any[]>(`/rec-offer/by-candidate/${id}`),
    create: (data: any) => request.post<any>('/rec-offer', data),
    /**
     * V111: 批量创建 Offer (A 方案 - 批量发 Offer).
     * body: { candidateIds: number[], template: { positionTitle, baseSalary, joinDate, orgUnit?, jdTemplateId?, probationMonths? } }
     */
    batchCreate: (data: { candidateIds: number[]; template: any }) =>
      request.post<any[]>('/rec-offer/batch', data),
    update: (id: number, data: any) => request.put<any>(`/rec-offer/${id}`, data),
    // P2.1 (2026-06-21): 录用扩展信息落档 (不卡 DRAFT)
    saveEmploymentExt: (id: number, employmentExtJson: string) =>
      request.post<any>(`/rec-offer/${id}/employment-ext`, { employmentExtJson }),
    reschedule: (id: number, newJoinDate: string, reason?: string) =>
      request.put<any>(`/rec-offer/${id}/reschedule`, { newJoinDate, reason }),
    cancelAccepted: (id: number, reason: string, statusAction: 'REVOKED' | 'REJECTED' = 'REVOKED') =>
      request.post<any>(`/rec-offer/${id}/cancel-accepted`, { reason, statusAction }),
    submit: (id: number) => request.post<any>(`/rec-offer/${id}/submit`),
    // 招聘 P0 (2026-06-22): approve 可一并落试用期考核标准 (probationDimensionWeights / probationCustomDimensions)
    //   一次原子落库 (PENDING→APPROVED + 权重/KPI 入专用列). 仅传的字段才写, 省略则兼容旧行为.
    approve: (
      id: number,
      approverId?: number,
      probation?: { probationDimensionWeights?: string; probationCustomDimensions?: string },
    ) => {
      const body: Record<string, any> = {}
      if (approverId) body.approverId = approverId
      if (probation?.probationDimensionWeights != null) body.probationDimensionWeights = probation.probationDimensionWeights
      if (probation?.probationCustomDimensions != null) body.probationCustomDimensions = probation.probationCustomDimensions
      return request.post<any>(`/rec-offer/${id}/approve`, body)
    },
    // UAT-FB#62 Phase B.5 (2026-06-16): 后端 Apache POI 渲染 Offer DOCX (按 offer_template_id 拉模板 + 占位符替换)
    render: (id: number) => request.post<any>(`/rec-offer/${id}/render`),
    revoke: (id: number, reason?: string) =>
      request.post<any>(`/rec-offer/${id}/revoke`, reason ? { reason } : {}),
    send: (id: number, validDays?: number) =>
      request.post<any>(`/rec-offer/${id}/send`, validDays ? { validDays } : {}),
    accept: (id: number, response?: string) =>
      request.post<any>(`/rec-offer/${id}/accept`, response ? { response } : {}),
    reject: (id: number, reason?: string) =>
      request.post<any>(`/rec-offer/${id}/reject`, reason ? { reason } : {}),
    expire: (id: number) => request.post<any>(`/rec-offer/${id}/expire`),
    bindOnboard: (id: number, onboardId: number) =>
      request.post<any>(`/rec-offer/${id}/bind-onboard`, { onboardId }),
  },
}

// ═══════════════════════════════════════════════════════════════
// V84 简历批量上传 + 异步解析队列
//   后端: ResumeBatchController @ /rec-batch-resume
//   流程: upload (拿 jobNo) -> 轮询 job (每 2s) -> COMPLETED 后 convert 入候选人池
// ═══════════════════════════════════════════════════════════════
export const batchResumeApi = {
  /** multipart 上传 N 份简历, 返 { jobNo, jobId, total } */
  upload: (files: File[]) => {
    const fd = new FormData()
    files.forEach((f) => fd.append('files', f))
    return request.post<{ jobNo: string; jobId: number; total: number }>(
      '/rec-batch-resume/upload',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, silent: true },
    )
  },
  /** 查询批次进度 + items 列表 */
  getJob: (jobNo: string) =>
    request.get<any>(`/rec-batch-resume/job/${jobNo}`),
  /** 把选中的 item.id 列表批量转候选人池 */
  convert: (jobNo: string, itemIds: number[]) =>
    request.post<{ createdCount: number }>(`/rec-batch-resume/job/${jobNo}/convert`, { itemIds }, { silent: true }),
}

// ═══════════════════════════════════════════════════════════════
// Perf Phase 1 P0: 绩效周期 + 目标 API (V74 真化)
//   后端: PerfCycleController @ /perf/cycles
//        PerfObjectiveController @ /perf/objectives
//   状态机: cycle: GOAL_SETTING → IN_PROGRESS → SELF_EVAL → MGR_EVAL → FEEDBACK → CLOSED
//          objective: DRAFT → REVIEWED → LOCKED
// ═══════════════════════════════════════════════════════════════
export const perfApi = {
  // ─── 周期 ─────────────────────────────────────
  cycle: {
    list: () => request.get<any[]>('/perf/cycles'),
    page: (params: { current?: number; size?: number; cycleType?: string; state?: string }) =>
      request.get<any>('/perf/cycles/page', { params }),
    current: (cycleType?: string) =>
      request.get<any>('/perf/cycles/current', { params: cycleType ? { cycleType } : {} }),
    get: (id: number) => request.get<any>(`/perf/cycles/${id}`),
    create: (data: any) => request.post<any>('/perf/cycles', data),
    update: (id: number, data: any) => request.put<any>(`/perf/cycles/${id}`, data),
    advance: (id: number) => request.post<any>(`/perf/cycles/${id}/advance`),
    close: (id: number) => request.post<any>(`/perf/cycles/${id}/close`),
    remove: (id: number) => request.delete(`/perf/cycles/${id}`),
    scopeOptions: () => request.get<any>('/perf/cycles/scope-options'),
    scopePreview: (data: any) => request.post<any>('/perf/cycles/scope-preview', data),
  },
  // ─── 目标 ─────────────────────────────────────
  objective: {
    byCycleEmp: (cycleId: number, empId: number) =>
      request.get<any[]>('/perf/objectives/by-cycle-emp', { params: { cycleId, empId } }),
    byCycle: (cycleId: number) =>
      request.get<any[]>('/perf/objectives/by-cycle', { params: { cycleId } }),
    get: (id: number) => request.get<any>(`/perf/objectives/${id}`),
    create: (data: any) => request.post<any>('/perf/objectives', data),
    update: (id: number, data: any) => request.put<any>(`/perf/objectives/${id}`, data),
    remove: (id: number) => request.delete(`/perf/objectives/${id}`),
    batchSubmit: (cycleId: number, empId: number, objectives: any[]) =>
      request.post<any[]>('/perf/objectives/batch-submit', objectives, { params: { cycleId, empId } }),
    review: (id: number, body: { managerComment: string; managerId?: number }) =>
      request.post<any>(`/perf/objectives/${id}/review`, body),
    lock: (id: number) => request.post<any>(`/perf/objectives/${id}/lock`),
  },
  // ─── 月度 Check-in (Phase 2 V75) ────────────────
  checkin: {
    byCycleEmp: (cycleId: number, empId: number) =>
      request.get<any[]>('/perf/checkins/by-cycle-emp', { params: { cycleId, empId } }),
    byCycle: (cycleId: number) =>
      request.get<any[]>('/perf/checkins/by-cycle', { params: { cycleId } }),
    get: (id: number) => request.get<any>(`/perf/checkins/${id}`),
    /** 员工首次打开 Check-in: 返已有或新建一条 PENDING */
    getOrCreate: (body: {
      cycleId: number; empId: number; empNo?: string; empName?: string;
      managerId?: number; managerName?: string;
    }) => request.post<any>('/perf/checkins/get-or-create', body),
    empSubmit: (id: number, body: { empProgress: string; empBlockers?: string }) =>
      request.post<any>(`/perf/checkins/${id}/emp-submit`, body),
    mgrSubmit: (id: number, body: { mgrGuidance: string; mgrAdjustment?: string }) =>
      request.post<any>(`/perf/checkins/${id}/mgr-submit`, body),
    close: (id: number) => request.post<any>(`/perf/checkins/${id}/close`),
    remove: (id: number) => request.delete(`/perf/checkins/${id}`),
  },
  // ─── 月度评分 (Phase 3 V76) ────────────────────
  //   状态机: PENDING → SELF_DONE → MGR_DONE → CALIBRATED → CLOSED
  //   等级: S/A/B+/B/C/D
  evaluation: {
    recentByEmp: (empId: number, limit = 4) =>
      request.get<any[]>('/perf/evaluations/by-emp', { params: { empId, limit } }),
    byCycleEmp: (cycleId: number, empId: number) =>
      request.get<any[]>('/perf/evaluations/by-cycle-emp', { params: { cycleId, empId } }),
    byCycle: (cycleId: number) =>
      request.get<any[]>('/perf/evaluations/by-cycle', { params: { cycleId } }),
    get: (id: number) => request.get<any>(`/perf/evaluations/${id}`),
    getOrCreate: (body: {
      cycleId: number; empId: number; empNo?: string; empName?: string;
      dept?: string; managerId?: number; managerName?: string;
    }) => request.post<any>('/perf/evaluations/get-or-create', body),
    selfSubmit: (id: number, body: { selfScore: number; selfGrade: string; selfComment?: string }) =>
      request.post<any>(`/perf/evaluations/${id}/self-submit`, body),
    mgrSubmit: (id: number, body: { mgrScore: number; mgrGrade: string; mgrComment?: string }) =>
      request.post<any>(`/perf/evaluations/${id}/mgr-submit`, body),
    calibrate: (id: number, body: { finalScore: number; finalGrade: string; calibrationReason: string }) =>
      request.post<any>(`/perf/evaluations/${id}/calibrate`, body),
    close: (id: number) => request.post<any>(`/perf/evaluations/${id}/close`),
    remove: (id: number) => request.delete(`/perf/evaluations/${id}`),
  },
  // ─── 反馈会议 + 申诉 (Phase 4 V77) ────────────────
  //   状态机: SCHEDULED → IN_PROGRESS → SIGNED → (APPEAL_PENDING → APPEAL_RESOLVED) → CLOSED
  //   申诉窗口: 员工签字后 24h 内
  feedback: {
    byCycle: (cycleId: number) =>
      request.get<any[]>('/perf/feedbacks/by-cycle', { params: { cycleId } }),
    byEvaluation: (evaluationId: number) =>
      request.get<any>('/perf/feedbacks/by-evaluation', { params: { evaluationId } }),
    byEmp: (empId: number) =>
      request.get<any[]>('/perf/feedbacks/by-emp', { params: { empId } }),
    get: (id: number) => request.get<any>(`/perf/feedbacks/${id}`),
    /** 上级发起会议 */
    schedule: (data: any) => request.post<any>('/perf/feedbacks', data),
    /** 改议题/纪要/actionItems */
    updateContent: (id: number, data: {
      empTopics?: string; mgrTopics?: string;
      meetingMinutes?: string; actionItems?: string;
      scheduledAt?: string;
    }) => request.put<any>(`/perf/feedbacks/${id}/content`, data),
    empSign: (id: number) => request.post<any>(`/perf/feedbacks/${id}/emp-sign`),
    mgrSign: (id: number) => request.post<any>(`/perf/feedbacks/${id}/mgr-sign`),
    appeal: (id: number, body: { reason: string }) =>
      request.post<any>(`/perf/feedbacks/${id}/appeal`, body),
    resolveAppeal: (id: number, body: { resolution: string }) =>
      request.post<any>(`/perf/feedbacks/${id}/resolve-appeal`, body),
    close: (id: number) => request.post<any>(`/perf/feedbacks/${id}/close`),
    remove: (id: number) => request.delete(`/perf/feedbacks/${id}`),
  },
  // ─── 年度 KPI/OKR (Phase A V82, #493) ─────────────
  //   4 套子组: annualCycle (年度周期) / okr (4 层级联) / kr (Key Result) / annualEval (年终评估 S/A/B/C/D)
  annual: {
    cycle: {
      list: () => request.get<any[]>('/perf/annual/cycles'),
      current: () => request.get<any>('/perf/annual/cycles/current'),
      get: (id: number) => request.get<any>(`/perf/annual/cycles/${id}`),
      create: (data: any) => request.post<any>('/perf/annual/cycles', data),
      update: (id: number, data: any) => request.put<any>(`/perf/annual/cycles/${id}`, data),
      advance: (id: number) => request.post<any>(`/perf/annual/cycles/${id}/advance`),
      remove: (id: number) => request.delete(`/perf/annual/cycles/${id}`),
    },
    okr: {
      list: (params: { cycleId?: number; level?: string } = {}) =>
        request.get<any[]>('/perf/annual/okr', { params }),
      byParent: (parentId: number) =>
        request.get<any[]>(`/perf/annual/okr/by-parent/${parentId}`),
      byEmp: (empId: number, cycleId?: number) =>
        request.get<any[]>('/perf/annual/okr/by-emp', { params: { empId, ...(cycleId ? { cycleId } : {}) } }),
      get: (id: number) => request.get<any>(`/perf/annual/okr/${id}`),
      create: (data: any) => request.post<any>('/perf/annual/okr', data),
      update: (id: number, data: any) => request.put<any>(`/perf/annual/okr/${id}`, data),
      remove: (id: number) => request.delete(`/perf/annual/okr/${id}`),
    },
    kr: {
      byOkr: (okrId: number) => request.get<any[]>(`/perf/annual/kr/by-okr/${okrId}`),
      get: (id: number) => request.get<any>(`/perf/annual/kr/${id}`),
      create: (data: any) => request.post<any>('/perf/annual/kr', data),
      update: (id: number, data: any) => request.put<any>(`/perf/annual/kr/${id}`, data),
      remove: (id: number) => request.delete(`/perf/annual/kr/${id}`),
    },
    eval: {
      byCycle: (cycleId: number) =>
        request.get<any[]>(`/perf/annual/eval/by-cycle/${cycleId}`),
      byEmp: (empId: number) =>
        request.get<any[]>(`/perf/annual/eval/by-emp/${empId}`),
      get: (id: number) => request.get<any>(`/perf/annual/eval/${id}`),
      getOrCreate: (seed: any) =>
        request.post<any>('/perf/annual/eval/get-or-create', seed),
      selfSubmit: (id: number, body: { selfScore: number; selfComment?: string }) =>
        request.post<any>(`/perf/annual/eval/${id}/self-submit`, body),
      mgrSubmit: (id: number, body: { managerScore: number; managerComment?: string }) =>
        request.post<any>(`/perf/annual/eval/${id}/mgr-submit`, body),
      calibrate: (id: number, body: { calibrationScore?: number; grade: string; calibrationReason?: string }) =>
        request.post<any>(`/perf/annual/eval/${id}/calibrate`, body),
      close: (id: number) => request.post<any>(`/perf/annual/eval/${id}/close`),
      update: (id: number, data: any) => request.put<any>(`/perf/annual/eval/${id}`, data),
      remove: (id: number) => request.delete(`/perf/annual/eval/${id}`),
      dashboard: (params: { year?: number; cycleId?: number } = {}) =>
        request.get<any>('/perf/annual/eval/dashboard', { params }),
    },
  },
}

// ⭐ V101 (2026-06-08): 绩效指标库 — #Perf Phase 5 真化
export const perfIndicatorApi = {
  list: (params: { category?: string; enabled?: number; keyword?: string } = {}) =>
    request.get<any[]>('/perf-indicator', { params }),
  get: (id: number) => request.get<any>(`/perf-indicator/${id}`),
  create: (data: any) => request.post<any>('/perf-indicator', data),
  update: (id: number, data: any) => request.put<any>(`/perf-indicator/${id}`, data),
  remove: (id: number) => request.delete(`/perf-indicator/${id}`),
  toggle: (id: number) => request.post<any>(`/perf-indicator/toggle/${id}`),
  batchImport: (data: any[]) => request.post<any>('/perf-indicator/batch-import', data),
}

// ⭐ V102 (2026-06-08): 评估表单模板 — #Perf Phase 6 真化
//   后端 PerfFormTemplateController @ /perf-form-template
//   create body: { template, fields } (CreateRequest DTO)
//   clone body: { newCode?, newName? } (CloneRequest DTO)
//   saveFields body: List<PerfFormField>
export const perfFormTemplateApi = {
  list: (params: { mode?: string; status?: string; keyword?: string } = {}) =>
    request.get<any[]>('/perf-form-template', { params }),
  get: (id: number) => request.get<any>(`/perf-form-template/${id}`),
  create: (data: { template: any; fields?: any[] }) =>
    request.post<any>('/perf-form-template', data),
  update: (id: number, data: any) => request.put<any>(`/perf-form-template/${id}`, data),
  saveFields: (id: number, fields: any[]) =>
    request.put<any>(`/perf-form-template/${id}/fields`, fields),
  clone: (id: number, data: { newCode?: string; newName?: string } = {}) =>
    request.post<any>(`/perf-form-template/${id}/clone`, data),
  toggleStatus: (id: number) => request.post<any>(`/perf-form-template/${id}/toggle-status`),
  toggleStar: (id: number) => request.post<any>(`/perf-form-template/${id}/toggle-star`),
  remove: (id: number) => request.delete(`/perf-form-template/${id}`),
}

// ⭐ V103 (2026-06-08): 强制分布校准会 — #Perf Phase 7 真化
//   后端 PerfCalibrationController @ /perf-calibration
//   - GET    /              list (cycleId/status/keyword)
//   - GET    /{id}          { calibration, decisions[] }
//   - GET    /config        { enabled, defaultDistribution }
//   - PUT    /config        body: { enabled?, defaultDistribution? }
//   - POST   /              create PerfCalibration body
//   - POST   /{id}/auto-distribute  按强制分布算法生成 decisions
//   - PUT    /{id}/decisions        批量保存 decisions
//   - POST   /{id}/complete         结束 + 回写 perf_evaluation.final_grade
//   - DELETE /{id}                  软删
export const perfCalibrationApi = {
  list: (params: { cycleId?: number; status?: string; keyword?: string } = {}) =>
    request.get<any[]>('/perf-calibration', { params }),
  get: (id: number) => request.get<any>(`/perf-calibration/${id}`),
  getConfig: () => request.get<any>('/perf-calibration/config'),
  setConfig: (data: { enabled?: boolean; defaultDistribution?: Record<string, number> }) =>
    request.put<any>('/perf-calibration/config', data),
  create: (data: any) => request.post<any>('/perf-calibration', data),
  autoDistribute: (id: number) => request.post<any>(`/perf-calibration/${id}/auto-distribute`),
  saveDecisions: (id: number, decisions: any[]) =>
    request.put<any>(`/perf-calibration/${id}/decisions`, decisions),
  complete: (id: number) => request.post<any>(`/perf-calibration/${id}/complete`),
  remove: (id: number) => request.delete(`/perf-calibration/${id}`),
}

// ⭐ V104 (2026-06-08): 九宫格 — #Perf Phase 8 真化
//   后端 PerfNineBoxController @ /perf-nine-box
//   字段: cycleId/empNo/empName/performanceLevel(LOW/MID/HIGH)/potentialLevel/boxCode/recommendation
//   9 格: STAR / HIGH_POTENTIAL / SOLID_PERFORMER / SOLID_CITIZEN / CORE / PROBLEM_CHILD / UNDERPERFORMER / DEADWOOD / NEW_HIRE
export const perfNineBoxApi = {
  list: (params: { cycleId?: number; boxCode?: string; keyword?: string } = {}) =>
    request.get<any[]>('/perf-nine-box', { params }),
  get: (id: number) => request.get<any>(`/perf-nine-box/${id}`),
  create: (data: any) => request.post<any>('/perf-nine-box', data),
  update: (id: number, data: any) => request.put<any>(`/perf-nine-box/${id}`, data),
  remove: (id: number) => request.delete(`/perf-nine-box/${id}`),
  /** 从 perf_evaluation 批量自动落位 */
  autoClassify: (cycleId: number) =>
    request.post<any>('/perf-nine-box/auto-classify', null, { params: { cycleId } }),
  getDistribution: (cycleId?: number) =>
    request.get<any>('/perf-nine-box/distribution', { params: { cycleId } }),
}

// ⭐ V105 (2026-06-08): PIP 绩效改善 — #Perf Phase 8 真化
//   后端 PerfPipController @ /perf-pip
//   status: INITIATED/IN_PROGRESS/MID_REVIEW/COMPLETED_PASS/COMPLETED_FAIL/CANCELLED
//   GET /{id} 返 { pip, checkins[] }
export const perfPipApi = {
  list: (params: { status?: string; empNo?: string; keyword?: string } = {}) =>
    request.get<any[]>('/perf-pip', { params }),
  get: (id: number) => request.get<any>(`/perf-pip/${id}`),
  create: (data: any) => request.post<any>('/perf-pip', data),
  update: (id: number, data: any) => request.put<any>(`/perf-pip/${id}`, data),
  /** 新增跟进记录 (PerfPipCheckin) */
  addCheckin: (id: number, data: any) =>
    request.post<any>(`/perf-pip/${id}/checkin`, data),
  /** result: PASS | FAIL */
  complete: (id: number, result: 'PASS' | 'FAIL') =>
    request.post<any>(`/perf-pip/${id}/complete`, null, { params: { result } }),
  getStats: () => request.get<any>('/perf-pip/stats'),
}

// ⭐ V106 (2026-06-08): 绩效申诉 — #Perf Phase 8 真化
//   后端 PerfAppealController @ /perf-appeal
//   status: SUBMITTED/IN_REVIEW/IN_HEARING/APPROVED/REJECTED/WITHDRAWN
//   adjust body: { finalResult: SCORE_ADJUSTED/RATING_CHANGED/NO_CHANGE, adjustedScore?, adjustedRating? }
export const perfAppealApi = {
  list: (params: { status?: string; cycleId?: number; empNo?: string; keyword?: string } = {}) =>
    request.get<any[]>('/perf-appeal', { params }),
  get: (id: number) => request.get<any>(`/perf-appeal/${id}`),
  /** 员工自助提交 */
  submit: (data: any) => request.post<any>('/perf-appeal', data),
  /** HR 审核; result: APPROVED | REJECTED */
  review: (id: number, params: { result: 'APPROVED' | 'REJECTED'; comment?: string; reviewer?: string }) =>
    request.post<any>(`/perf-appeal/${id}/review`, null, { params }),
  /** 调分/改级 (反向回写 perf_evaluation) */
  adjust: (id: number, data: { finalResult: string; adjustedScore?: number; adjustedRating?: string }) =>
    request.post<any>(`/perf-appeal/${id}/adjust`, data),
  withdraw: (id: number) => request.post<any>(`/perf-appeal/${id}/withdraw`),
  getStats: (cycleId?: number) =>
    request.get<any>('/perf-appeal/stats', { params: { cycleId } }),
}

export const lrnApi = {
  courses: () => request.get('/lrn/courses'),
  certificates: () => request.get('/lrn/certificates'),
}

// ⭐ V65 (2026-06-03): 流程引擎 (proc) 4 表真后端 — W8-proc2 前端切真接
//   后端 Controller: ProcController @ /proc (ba39944)
//   21 端点: templates(10) + instances(7+audits) + tasks(5)
//   注意 (按后端 ProcController 源码确认, 与蓝图描述有差异):
//     1) /proc/instances/page 是 POST body (InstanceQueryDTO), 不是 GET
//     2) /proc/tasks/my 是 POST body (TaskQueryDTO), 不是 GET
//     3) /proc/tasks/{id}/action body 是 ApprovalActionDTO {action, operatorEmpId, comment, transferToEmpId}
//     4) /proc/instances/{id}/cancel body 是 {cancellerEmpId, reason}
//     5) /proc/templates 没有 delete 端点 (只能 deactivate)
//     6) by-code 路径是 /by-code/{code}, active 是 /by-code/{code}/active
//   状态机:
//     ProcInstance: RUNNING → COMPLETED/REJECTED/CANCELLED/TERMINATED
//     ProcTask:     PENDING → CLAIMED → COMPLETED/REJECTED/TRANSFERRED/SKIPPED
//   所有读/写操作均 @PreAuthorize hasAuthority('proc'), 写带 @AuditLog
//
// ⚠️ 兼容策略 (W8-proc2): 与 mock seed (字符串 id 'PROC-2026-XXXX' / 'TPL-XXX') 共存,
//   真接返回的实例/模板 backendId(number) 单独存, 不冲突

/** 流程 SLA 超时策略 (2026-07-17 · 企业可配, 存 sys_kv cfg:proc_sla_policy)。字段与后端 ProcSlaPolicy 对齐。 */
export interface ProcSlaPolicy {
  /** SLA 提醒总开关 */
  enabled: boolean
  /** 是否同时提醒发起人 */
  remindInitiator: boolean
  /** 是否升级 */
  escalateEnabled: boolean
  /** 超时满多少小时升级 */
  escalateHours: number
  /** 升级对象: MANAGER(审批人直属上级) / ROLE:xxx(指定角色) / INITIATOR_MANAGER(发起人上级) */
  escalateTarget: string
  /** MANAGER 取不到上级时的兜底角色 code */
  escalateFallbackRole: string
  /** 超时自动跳过审批 (合规敏感, 默认关) */
  autoSkipEnabled: boolean
  /** 自动跳过阈值 (小时) */
  autoSkipHours: number
  /** 允许自动跳过的模板 code 白名单；空列表表示全部禁止 */
  autoSkipTemplateCodes: string[]
  /** 提醒渠道 (预留) */
  channels: string[]
}

export interface ProcCommonRoleMemberVO {
  empId: number
  empNo: string
  empName: string
  orgName?: string
  empStatus?: string
}

export interface ProcCommonRoleVO {
  id: number
  code: string
  name: string
  description?: string
  enabled: boolean
  sortOrder: number
  memberCount: number
  members: ProcCommonRoleMemberVO[]
}

export interface ProcCommonRoleSavePayload {
  code?: string
  name: string
  description?: string
  enabled: boolean
  sortOrder?: number
  memberEmpIds: number[]
}

export interface ProcTemplateContractAudit {
  templateId: number
  code: string
  name: string
  status: string
  selfStartEnabled: boolean
  executable: boolean
  formFieldCount: number
  callbackMode: 'FLOW_ONLY' | 'BUSINESS_BOUND' | 'COMPLETION_ACTION' | 'PARTIAL_CALLBACK'
  callbackEvents: Array<'COMPLETED' | 'REJECTED' | 'ABORTED'>
  issues: string[]
}

export const attWorkflowPolicyApi = {
  get: () => request.get<{
    policy: {
      leaveTemplateCode: string
      leaveCancellationTemplateCode: string
      overtimeTemplateCode: string
      punchCorrectionTemplateCode: string
      shiftSwapTemplateCode: string
    }
    version: number | null
  }>('/att/workflow-policy'),
  save: (policy: any, version: number | null) => request.post('/att/workflow-policy', { policy, version }),
}

export const procApprovalDelegateApi = {
  list: () => request.get<any[]>('/proc/approval-delegates'),
  create: (data: any) => request.post<any>('/proc/approval-delegates', data),
  cancel: (id: number) => request.post<void>(`/proc/approval-delegates/${id}/cancel`),
}

export const procApi = {
  // 旧 stub 保留 (避免外部引用断), 实际走 4 子组
  instances: () => request.get('/proc/instances/page', { method: 'POST' }),
  tasks: () => request.post('/proc/tasks/my', {}),

  // ─── 流程模板 (proc_template) ───
  template: {
    list: () => request.get<any[]>('/proc/templates'),
    /** 员工自助安全清单：仅 ACTIVE 模板。 */
    listActive: () => request.get<any[]>('/proc/templates/active'),
    /** 管理员只读体检：模板、表单可执行性与业务状态回调覆盖。 */
    contractAudit: () => request.get<ProcTemplateContractAudit[]>('/proc/templates/contract-audit'),
    page: (params: { current?: number; size?: number; keyword?: string; status?: string; category?: string }) =>
      request.get('/proc/templates/page', { params }),
    get: (id: number) => request.get<any>(`/proc/templates/${id}`),
    /** 按 code 取最新版本 (不限状态) */
    getByCode: (code: string) => request.get<any>(`/proc/templates/by-code/${code}`),
    /** 按 code 取 ACTIVE 版本 (启动实例用) */
    getActiveByCode: (code: string) => request.get<any>(`/proc/templates/by-code/${code}/active`),
    create: (data: any) => request.post<any>('/proc/templates', data),
    duplicate: (id: number, name?: string) => request.post<any>(`/proc/templates/${id}/duplicate`, { name }),
    remove: (id: number) => request.delete<void>(`/proc/templates/${id}`),
    update: (id: number, data: any) => request.put<any>(`/proc/templates/${id}`, data),
    deactivate: (id: number, reason: string) => request.post<any>(`/proc/templates/${id}/deactivate`, { reason }),
  },

  // ─── 公共审批角色：跨流程职责目录，与 RBAC 权限角色解耦 ───
  commonRole: {
    list: (enabledOnly = false) =>
      request.get<ProcCommonRoleVO[]>('/proc/common-roles', { params: enabledOnly ? { enabledOnly: true } : {} }),
    eligibleEmployees: () =>
      request.get<ProcCommonRoleMemberVO[]>('/proc/common-roles/eligible-employees'),
    create: (data: ProcCommonRoleSavePayload) =>
      request.post<ProcCommonRoleVO>('/proc/common-roles', data),
    update: (id: number, data: ProcCommonRoleSavePayload) =>
      request.put<ProcCommonRoleVO>(`/proc/common-roles/${id}`, data),
    remove: (id: number) => request.delete<void>(`/proc/common-roles/${id}`),
  },

  // ─── 流程实例 (proc_instance) ───
  instance: {
    /** 启动实例 body: {templateCode, initiatorEmpId, formData?} */
    start: (data: { templateCode: string; initiatorEmpId: number; formData?: string }) =>
      request.post<any>('/proc/instances', data),
    /** 前端"我发起"去mock: 当前登录用户发起的实例 (empId 后端按 token 解析). 返回 {empId, instances}. */
    mine: () => request.get<{ empId: number | null; instances: any[] }>('/proc/instances/mine'),
    /** 当前登录用户发起的实例分页；statuses 为逗号分隔的实例状态。 */
    minePage: (current = 1, size = 20, statuses?: string) =>
      request.get<{ empId: number | null; total: number; current: number; size: number; instances: any[] }>(
        '/proc/instances/mine/page',
        { params: { current, size, ...(statuses ? { statuses } : {}) } },
      ),
    get: (id: number) => request.get<any>(`/proc/instances/${id}`),
    // UAT-FB#29 (2026-06-15): 透传 config — openHcAppDetail 用 silent:true 静默 404
    getByBizNo: (bizNo: string, config?: any) => request.get<any>(`/proc/instances/by-biz-no/${bizNo}`, config),
    /** 分页 (POST body InstanceQueryDTO) */
    page: (q: {
      initiatorEmpId?: number
      statuses?: string[]
      templateCode?: string
      dateFrom?: string
      dateTo?: string
      current?: number
      size?: number
    }) => request.post<{ records: any[]; total: number; current: number; size: number }>('/proc/instances/page', q),
    cancel: (id: number, cancellerEmpId: number, reason?: string) =>
      request.post<any>(`/proc/instances/${id}/cancel`, { cancellerEmpId, reason }),
    terminate: (id: number, cancellerEmpId: number, reason: string) =>
      request.post<any>(`/proc/instances/${id}/terminate`, { cancellerEmpId, reason }),
    listTasks: (id: number) => request.get<any[]>(`/proc/instances/${id}/tasks`),
    listTasksPage: (id: number, current = 1, size = 50) =>
      request.get<{ records: any[]; total: number; current: number; size: number }>(
        `/proc/instances/${id}/tasks/page`, { params: { current, size } }),
    listAudits: (id: number) => request.get<any[]>(`/proc/instances/${id}/audits`),
    listAuditsPage: (id: number, current = 1, size = 50) =>
      request.get<{ records: any[]; total: number; current: number; size: number }>(
        `/proc/instances/${id}/audits/page`, { params: { current, size } }),
    /** ②审批留痕去mock: 实例详情富化 (instance+tasks+audits, 带操作人姓名), 详情抽屉用.
     * instance 块含 formSchema (form_schema 原样 JSON 串, 自行 parse) + currentNodeName; tasks[].nodeName 保证有值 */
    detail: (id: number) => request.get<any>(`/proc/instances/${id}/detail`),
    /** 该实例所用模板 bpmn 的全部节点 (按 bpmn 声明顺序, 含 CONDITION/CC 路由节点, 下拉按需过滤); bpmn 缺失/解析失败返 [] */
    nodes: (id: number) =>
      request.get<{ code: string; name: string }[]>(`/proc/instances/${id}/nodes`),
    /** cc 抄送: 把实例抄送给若干人 (fromEmpId 后端按 token 解析) */
    cc: (id: number, body: { ccEmpIds: number[]; comment?: string }, config?: any) =>
      request.post<number>(`/proc/instances/${id}/cc`, body, config),
  },

  // ─── 流程统计看板 (①看板全真接) ───
  stats: {
    overview: () => request.get<any>('/proc/stats/overview'),
    sla: () => request.get<any>('/proc/stats/sla'),
    analytics: () => request.get<any>('/proc/stats/analytics'),
    audits: () => request.get<any[]>('/proc/stats/audits'),
    report: (body: { dimensions: string[]; measure: string; dateFrom?: string; dateTo?: string }) =>
      request.post<any[]>('/proc/stats/report', body),
  },

  // ─── SLA 超时策略 (企业可配, 存 sys_kv cfg:proc_sla_policy) ───
  slaPolicy: {
    /** 读当前策略 + 版本 (缺配置返默认值, version=null) */
    get: () => request.get<{ policy: ProcSlaPolicy; version: number | null }>('/proc/sla-policy'),
    /** 保存策略 (proc:admin, version 回传做乐观锁, 首次传 null) */
    save: (policy: ProcSlaPolicy, version: number | null) =>
      request.post<KvVO>('/proc/sla-policy', { policy, version }),
  },

  // ─── 流程任务 (proc_task) ───
  task: {
    /** 我的待办 (POST body TaskQueryDTO) */
    my: (q: { empId: number; statuses?: string[]; instanceId?: number; current?: number; size?: number }) =>
      request.post<{ records: any[]; total: number; current: number; size: number }>('/proc/tasks/my', q),
    /** 前端待办去mock: 当前登录用户的"我的任务"(富化 join 实例/模板/发起人). empId 由后端 token 解析, 返回 {empId, tasks}. statuses 逗号分隔 (默认 PENDING,CLAIMED). */
    mine: (statuses?: string) =>
      request.get<{ empId: number | null; tasks: any[] }>('/proc/tasks/mine', { params: statuses ? { statuses } : {} }),
    /** cc 抄送给我: 被抄送的实例 (empId 后端按 token 解析) */
    ccMine: () => request.get<{ empId: number | null; ccList: any[] }>('/proc/cc/mine'),
    get: (id: number) => request.get<any>(`/proc/tasks/${id}`),
    claim: (id: number, claimerEmpId: number, config?: any) =>
      request.post<any>(`/proc/tasks/${id}/claim`, { claimerEmpId }, config),
    /** action: APPROVE/REJECT/TRANSFER/SIGN_OFF/WITHDRAW/RETURN (RETURN 需带 returnToNodeCode) */
    action: (id: number, data: {
      action: string; operatorEmpId: number; comment?: string;
      transferToEmpId?: number; returnToNodeCode?: string
    }, config?: any) =>
      request.post<any>(`/proc/tasks/${id}/action`, data, config),

    /**
     * 该任务可退回的目标 (2026-07-17)。
     *
     * nodes = 本单据**实际走过**的审批节点 (按审计足迹算, 不是 bpmn 前置节点 ——
     * 有条件分支时两者不一样); initiatorValue = 退回发起人的特殊值 (__INITIATOR__)。
     */
    returnTargets: (id: number, config?: any) =>
      request.get<{ nodes: { code: string; name: string }[]; initiatorValue: string }>(
        `/proc/tasks/${id}/return-targets`, config
      ),
    /** 表单统一 M5 (2026-07-17): 退回发起人的修改重提任务改表单 (仅任务持有人=发起人本人可调) */
    updateForm: (id: number, formData: string, config?: any) =>
      request.post<void>(`/proc/tasks/${id}/update-form`, { formData }, config),
  },
}

// ─────────────────────────────────────────────────────────────────
// 流程概览布局 (proc overview layout) — "流程管理 → 概览" 各块显示/隐藏 + 排序, 存后端.
// 后端: GET /proc/overview-layout → 已保存布局 JSON (或 null=没配过)
//       PUT /proc/overview-layout  body={blocks:[{key,enabled,order}]} → 保存
// block.key 见 ProcOverviewPanel.vue 的 DEFAULT_LAYOUT (6 KPI + 5 板块).
// ─────────────────────────────────────────────────────────────────
export interface ProcOverviewBlock {
  key: string
  enabled: boolean
  order: number
}
export interface ProcOverviewLayout {
  blocks: ProcOverviewBlock[]
}
export const procOverviewLayoutApi = {
  /** 拉已保存布局; 后端没配过返回 null */
  get: () => request.get<ProcOverviewLayout | null>('/proc/overview-layout'),
  /** 保存布局 */
  save: (blocks: ProcOverviewBlock[]) =>
    request.put<ProcOverviewLayout>('/proc/overview-layout', { blocks }),
}

// ─────────────────────────────────────────────────────────────────
// 流程分类 (proc_category) — 流程模板 category 改成数据库驱动 (替原写死中文常量).
// proc_template.category 存分类 code (RECRUIT/ONBOARD/.../FINANCE), 显示名是 name.
// 后端: ProcCategoryController @ /proc/category
//   字段: { id, code, name, color, sortOrder, enabled, isSystem }
//   - list(enabledOnly): GET /proc/category?enabledOnly=true 只返启用
//   - create: POST (code 新增可填)
//   - update: PUT /proc/category/{id} (code 锁定, 仅改 name/color/sortOrder/enabled)
//   - remove: DELETE /proc/category/{id} (isSystem 的会被后端拦)
// ─────────────────────────────────────────────────────────────────
export interface ProcCategoryVO {
  id: number
  code: string
  name: string
  color?: string
  sortOrder?: number
  enabled?: boolean
  isSystem?: boolean
}
export const procCategoryApi = {
  /** 列出分类; enabledOnly=true 只返回启用的 (设计器/流程库下拉用) */
  list: (enabledOnly?: boolean) =>
    request.get<ProcCategoryVO[]>('/proc/category', { params: enabledOnly ? { enabledOnly: true } : {} }),
  create: (data: { code: string; name: string; color?: string; sortOrder?: number; enabled?: boolean }) =>
    request.post<ProcCategoryVO>('/proc/category', data),
  /** code 锁定, 仅改 name/color/sortOrder/enabled */
  update: (id: number, data: { name?: string; color?: string; sortOrder?: number; enabled?: boolean }) =>
    request.put<ProcCategoryVO>(`/proc/category/${id}`, data),
  remove: (id: number) => request.delete<void>(`/proc/category/${id}`),
}

// ─────────────────────────────────────────────────────────────────
// V115: 流程运行时监控 + 6 大干预动作 (Admin only)
// 后端: ProcInterventionController
//   GET  /proc-instance/active                  → 活跃实例列表
//   GET  /proc-instance/{id}/timeline           → 实例时间线
//   GET  /proc-intervention-log                 → 干预历史 (proc:admin)
//   POST /proc-task/{taskId}/transfer           → 转交
//   POST /proc-task/{taskId}/force-pass         → 强制流转 (proc:admin)
//   POST /proc-instance/{id}/withdraw           → 撤回实例
//   POST /proc-instance/{id}/terminate          → 终止实例 (proc:admin) — V115 端点, 不同于旧 /proc/instances/{id}/terminate
//   POST /proc-task/{taskId}/add-node           → 加签 (mode: PARALLEL/SERIES)
//   POST /proc-task/{taskId}/remind             → 催办
// ─────────────────────────────────────────────────────────────────
export const procMonitorApi = {
  /** 活跃实例列表 (含 SLA + 当前节点); status 可选 RUNNING(默认)|COMPLETED|REJECTED|CANCELLED|TERMINATED|ALL (大小写不敏感, 未知值回落 RUNNING).
   * RUNNING 行带 currentAssignees: {empId,name}[] (会签/加签多人去重, 无活跃任务=[]), 终态行为 null;
   * currentAssigneeName 多人时"、"分隔; 终态行 completedAt 有值, waitMillis/slaTotalMinutes/slaStatus=null, 按完成时间倒序最多 500 条.
   * waitDays/slaStatus 过滤仅作用于 RUNNING 行, procType 全局有效 */
  listActive: (params: {
    waitDays?: number; procType?: string; slaStatus?: string;
    status?: 'RUNNING' | 'COMPLETED' | 'REJECTED' | 'CANCELLED' | 'TERMINATED' | 'ALL'
  } = {}) =>
    request.get<any[]>('/proc-instance/active', { params }),
  /** 分页监控契约；旧页面继续使用 listActive，迁移页面时优先使用此接口。 */
  pageActive: (params: {
    waitDays?: number; procType?: string; slaStatus?: string;
    status?: 'RUNNING' | 'COMPLETED' | 'REJECTED' | 'CANCELLED' | 'TERMINATED' | 'ALL';
    current?: number; size?: number;
  } = {}) =>
    request.get<{ records: any[]; total: number; current: number; size: number; truncated: boolean }>(
      '/proc-instance/active/page', { params }),
  /** 实例时间线 */
  timeline: (instanceId: number) =>
    request.get<any[]>(`/proc-instance/${instanceId}/timeline`),
  /** 干预历史 */
  interventionLog: (params: { instanceId?: number; type?: string } = {}) =>
    request.get<any[]>('/proc-intervention-log', { params }),
  outboxSummary: () => request.get<Record<'PENDING' | 'PROCESSING' | 'DONE' | 'DEAD', number>>('/proc/outbox/summary'),
  outboxPage: (params: { status?: string; current?: number; size?: number } = {}) =>
    request.get<{ records: any[]; total: number; current: number; size: number }>('/proc/outbox', { params }),
  retryOutbox: (id: number) => request.post<void>(`/proc/outbox/${id}/retry`),

  // ─── 6 大干预动作 ───
  transfer: (taskId: number, data: { toAssignee: number; reason: string }) =>
    request.post<any>(`/proc-task/${taskId}/transfer`, data),
  /** 强制流转; targetNodeCode 空=按 bpmn 路由推进下一节点, 非空=流转到指定审批节点
   * (须在该实例模板 bpmn 节点集合内且非 START/END/CONDITION/MERGE/CC/apply/done, 否则 400 报可选节点集合) */
  forcePass: (taskId: number, data: { reason: string; targetNodeCode?: string }, config?: any) =>
    request.post<any>(`/proc-task/${taskId}/force-pass`, data, config),
  withdraw: (instanceId: number, data: { reason: string }) =>
    request.post<any>(`/proc-instance/${instanceId}/withdraw`, data),
  /** ⚠️ V115 端点, 与旧 procApi.instance.terminate (/proc/instances/{id}/terminate) 同名但不同路径前缀 */
  terminate: (instanceId: number, data: { reason: string }) =>
    request.post<any>(`/proc-instance/${instanceId}/terminate`, data),
  addNode: (taskId: number, data: { toAssignee: number; mode: 'PARALLEL' | 'SERIES'; reason: string }) =>
    request.post<any>(`/proc-task/${taskId}/add-node`, data),
  remind: (taskId: number, data: { message?: string }) =>
    request.post<any>(`/proc-task/${taskId}/remind`, data),
}

// ─────────────────────────────────────────────────────────────────
// V117: 流程模板生命周期版本管理 (A+B+C+D+E)
// 后端: ProcTemplateVersionController
// ─────────────────────────────────────────────────────────────────
export const procVersionApi = {
  /** 版本历史 (按 code, 倒序) */
  listVersions: (templateCode: string) =>
    request.get<any[]>('/proc-template-version/list', { params: { templateCode } }),
  /** 当前激活版本 */
  active: (templateCode: string) =>
    request.get<any>('/proc-template-version/active', { params: { templateCode } }),
  /** 待我审核草稿 */
  pendingReview: (reviewerAssigned?: string, config?: any) =>
    request.get<any[]>('/proc-template-version/pending-review',
      { ...config, params: reviewerAssigned ? { reviewerAssigned } : {} }),
  /** 管理员查看全部待审版本；可选按审核人过滤 */
  pendingReviewAdmin: (reviewerAssigned?: string, config?: any) =>
    request.get<any[]>('/proc-template-version/pending-review-admin',
      { ...config, params: reviewerAssigned ? { reviewerAssigned } : {} }),
  /** 当前提交人可选择的合法审核账号（启用且具 proc:admin） */
  reviewers: (submitter?: string) =>
    request.get<Array<{ id: number; username: string; displayName?: string; empNo?: string }>>(
      '/proc-template-version/reviewers', { params: submitter ? { submitter } : {} }),
  /** 创建草稿 (从 baseVersion 复制) */
  createDraft: (data: {
    templateCode: string; baseVersion: number; changeLog: string
    bpmnXml?: string; formSchema?: string; slaConfig?: string
    formCode?: string; formVersion?: number
  }) => request.post<any>('/proc-template-version/draft', data),
  /** DRAFT/REJECTED → IN_REVIEW */
  submit: (id: number, data: { reviewerAssigned: string; submittedBy?: string }, config?: any) =>
    request.post<any>(`/proc-template-version/${id}/submit`, data, config),
  /** 修复历史卡单：管理员重新指派合法审核人 */
  reassignReviewer: (id: number, reviewerAssigned: string, config?: any) =>
    request.post<any>(`/proc-template-version/${id}/reassign-reviewer`, { reviewerAssigned }, config),
  /** IN_REVIEW → ACTIVE (proc:admin) */
  approve: (id: number, data?: { reviewedBy?: string; reviewComment?: string }, config?: any) =>
    request.post<any>(`/proc-template-version/${id}/approve`, data || {}, config),
  /** IN_REVIEW → REJECTED (proc:admin) */
  reject: (id: number, data: { reviewedBy?: string; reviewComment: string }, config?: any) =>
    request.post<any>(`/proc-template-version/${id}/reject`, data, config),
  /** 回滚到老版本 (proc:admin) */
  rollback: (data: { templateCode: string; toVersion: number; reason: string }) =>
    request.post<any>('/proc-template-version/rollback', data),
  /** 强制升级跑中实例 (proc:admin) */
  forceUpgrade: (data: { templateCode: string; toVersion: number; reason: string }) =>
    request.post<number>('/proc-template-version/force-upgrade-instances', data),
  /** 版本 diff */
  diff: (templateCode: string, versionA: number, versionB: number) =>
    request.get<any>('/proc-template-version/diff',
      { params: { templateCode, versionA, versionB } }),
  /** 变更审计 */
  changeLog: (templateCode: string) =>
    request.get<any[]>('/proc-template-version/change-log', { params: { templateCode } }),

  // 表单统一 M7 (2026-07-17): proc_form_version 表单版本引擎软废弃 —— 从建成起零消费,
  // 表单唯一权威 = proc_template.form_schema (docs/PROC-FORM-SCHEMA.md), 4 个死 API 已删。
}

// ⭐ #491 D+30 lrn 学习培训 (V81): 4 子资源 + 统计
// 后端: LrnController @ /lrn/{courses,sessions,enrollments,certificates,stats}
// 查 isAuthenticated, 改 hasAuthority('lrn')
export const trainingApi = {
  // 课程
  listCourses: (category?: string) =>
    request.get('/lrn/courses', { params: category ? { category } : {} }),
  getCourse: (id: number) => request.get(`/lrn/courses/${id}`),
  createCourse: (data: any) => request.post('/lrn/courses', data),
  updateCourse: (id: number, data: any) => request.put(`/lrn/courses/${id}`, data),
  removeCourse: (id: number) => request.delete(`/lrn/courses/${id}`),
  // 场次
  listSessions: (params: { courseId?: number; status?: string } = {}) =>
    request.get('/lrn/sessions', { params }),
  getSession: (id: number) => request.get(`/lrn/sessions/${id}`),
  createSession: (data: any) => request.post('/lrn/sessions', data),
  updateSession: (id: number, data: any) => request.put(`/lrn/sessions/${id}`, data),
  removeSession: (id: number) => request.delete(`/lrn/sessions/${id}`),
  // 报名
  listEnrollments: (params: { empNo?: string; sessionId?: number } = {}) =>
    request.get('/lrn/enrollments', { params }),
  createEnrollment: (data: any) => request.post('/lrn/enrollments', data),
  updateEnrollment: (id: number, data: any) => request.put(`/lrn/enrollments/${id}`, data),
  removeEnrollment: (id: number) => request.delete(`/lrn/enrollments/${id}`),
  // 证书 (食安证业务核心)
  listCertificates: (empNo?: string) =>
    request.get('/lrn/certificates', { params: empNo ? { empNo } : {} }),
  listExpiringCertificates: (category?: string, days = 30) =>
    request.get('/lrn/certificates/expiring', { params: { ...(category ? { category } : {}), days } }),
  createCertificate: (data: any) => request.post('/lrn/certificates', data),
  updateCertificate: (id: number, data: any) => request.put(`/lrn/certificates/${id}`, data),
  removeCertificate: (id: number) => request.delete(`/lrn/certificates/${id}`),
  // 统计
  stats: () => request.get('/lrn/stats'),
}

// ⭐ Phase C Tier A: 员工证书子档 + Dashboard BI 公式
export const certApi = {
  listAll: () => request.get('/cert'),
  byEmp: (empNo: string) => request.get(`/cert/by-emp/${empNo}`),
  expiring: (type: string, days: number) => request.get('/cert/expiring', { params: { type, days } }),
  create: (data: any) => request.post('/cert', data),
  update: (id: number, data: any) => request.put(`/cert/${id}`, data),
  remove: (id: number) => request.delete(`/cert/${id}`),
  // ⭐ V98 #D+30 新增
  coverage: () => request.get('/cert/coverage'),
  recalcRenewal: () => request.post('/cert/recalc-renewal'),
}

// ⭐ V98 #D+30: 证书类型字典
export const certTypeMetaApi = {
  listAll: () => request.get('/cert-type-meta'),
  getByCode: (code: string) => request.get(`/cert-type-meta/${code}`),
  create: (data: any) => request.post('/cert-type-meta', data),
  update: (id: number, data: any) => request.put(`/cert-type-meta/${id}`, data),
  remove: (id: number) => request.delete(`/cert-type-meta/${id}`),
}

// ⭐ V99 #D+30: 证书续证培训计划
export const certTrainingPlanApi = {
  listAll: (status?: string) =>
    request.get('/cert-training-plan', { params: status ? { status } : {} }),
  get: (id: number) => request.get(`/cert-training-plan/${id}`),
  create: (data: any) => request.post('/cert-training-plan', data),
  update: (id: number, data: any) => request.put(`/cert-training-plan/${id}`, data),
  remove: (id: number) => request.delete(`/cert-training-plan/${id}`),
}

export const formulaApi = {
  getAll: () => request.get('/dashboard/formula'),
  selectFormula: (kpiKey: string, formulaId: string, updatedBy?: string) =>
    request.put(`/dashboard/formula/${kpiKey}`, { formulaId, updatedBy: updatedBy || 'HR' }),
}

// ⭐ Phase C Tier B: 员工薪资覆盖 (Q1 L3 员工级)
export const overrideApi = {
  listAll: () => request.get('/override'),
  byEmp: (empNo: string) => request.get(`/override/by-emp/${empNo}`),
  byStructure: (structureId: string) => request.get(`/override/by-structure/${structureId}`),
  effective: (empNo: string, structureId: string, refDate?: string) =>
    request.get('/override/effective', { params: { empNo, structureId, ...(refDate ? { refDate } : {}) } }),
  create: (data: any) => request.post('/override', data),
  update: (id: number, data: any) => request.put(`/override/${id}`, data),
  remove: (id: number) => request.delete(`/override/${id}`),
  toggle: (id: number) => request.put(`/override/${id}/toggle`),
}

// ⭐ Phase C Tier B (2/3): 薪酬结构方案 + 条件类型
// 方案 B 简化: components/applyScope 作为 JSON 字符串整块读写
export const structureApi = {
  listAll: () => request.get('/structure'),
  getById: (id: number) => request.get(`/structure/${id}`),
  getByExtId: (extId: string) => request.get(`/structure/by-ext/${extId}`),
  create: (data: any) => request.post('/structure', data),
  update: (id: number, data: any) => request.put(`/structure/${id}`, data),
  remove: (id: number) => request.delete(`/structure/${id}`),
  toggle: (id: number) => request.put(`/structure/${id}/toggle`),
  // 条件类型
  listConditions: () => request.get('/structure/condition'),
  createCondition: (data: any) => request.post('/structure/condition', data),
  updateCondition: (id: number, data: any) => request.put(`/structure/condition/${id}`, data),
  removeCondition: (id: number) => request.delete(`/structure/condition/${id}`),
}

// ⭐ P1.1 劳动合同 (Step5 状态机扩, 7 工作流端点)
export const contractApi = {
  // UAT P0 (2026-06-11): 全员合同列表 (emp"合同管理" tab 主表)
  list: (params?: { status?: string; workflowStatus?: string; limit?: number }) =>
    request.get('/contract/list', { params: params || {} }),
  mine: () => request.get('/contract/mine'),
  selfPreview: (id: number) => request.get(`/contract/${id}/self-preview`),
  sealWorkQueue: (limit = 200) => request.get('/contract/seal-work-queue', { params: { limit } }),
  listByEmp: (empId: number) => request.get(`/contract/by-emp/${empId}`),
  attachmentIntegrity: () => request.get('/contract/attachment-integrity'),
  repairAttachment: (id: number, data: { fileKey: string; fileName: string; reason: string }) =>
    request.post(`/contract/${id}/attachment-repair`, data),
  attachmentRepairHistory: (id: number) => request.get(`/contract/${id}/attachment-repairs`),
  get: (id: number) => request.get(`/contract/${id}`),
  effective: (empId: number, refDate?: string) =>
    request.get('/contract/effective', { params: { empId, ...(refDate ? { refDate } : {}) } }),
  create: (data: any) => request.post('/contract', data),
  update: (id: number, data: any) => request.put(`/contract/${id}`, data),
  remove: (id: number) => request.delete(`/contract/${id}`),
  // 7.2 YGCS053: Excel 批量导入 (历史/纸质合同补录)
  importTemplate: () =>
    request.get('/contract/import/template', { responseType: 'blob' as any }),
  importExcel: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post('/contract/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  // Step5 状态机
  submit: (id: number) => request.post(`/contract/${id}/submit`),
  approveHr: (id: number) => request.post(`/contract/${id}/approve-hr`),
  applySeal: (id: number) => request.post(`/contract/${id}/apply-seal`),
  approveSeal: (id: number) => request.post(`/contract/${id}/approve-seal`),
  empSign: (id: number, pdfFileKey: string, pdfFileName?: string) =>
    request.post(`/contract/${id}/emp-sign`, { pdfFileKey, pdfFileName }),
  reject: (id: number, reason: string) => request.post(`/contract/${id}/reject`, { reason }),
  // Step5 模板渲染 (方案A: POI 填充 docx 真数据)
  renderPreview: (id: number) => request.get(`/contract/${id}/render-preview`),
  renderDownload: (id: number) =>
    request.get(`/contract/${id}/render-download`, { responseType: 'blob' }),
  // 2026-07-04 P1: 授权版渲染 (含完整身份证号). 后端 @PreAuthorize('emp:export') + reason ≥5 字 + audit 落 RENDER_FULL_ID.
  renderPreviewFull: (id: number, reason: string) =>
    request.get(`/contract/${id}/render-preview-full`, { params: { reason } }),
  renderDownloadFull: (id: number, reason: string) =>
    request.get(`/contract/${id}/render-download-full`, { params: { reason }, responseType: 'blob' }),
}

// P1-7 (2026-06-21): 合同批量操作 — 真后端 batch-job 引擎 (替换 batchSignStore 本地 mock)
// 后端: EmpContractBatchController @ /contract-batch
export const contractBatchApi = {
  /** 创建批次；批量变更提交统一审批，其他类型进入异步处理队列 */
  create: (payload: any) => request.post<any>('/contract-batch', payload),
  /** 历史批次列表 */
  list: (status?: string) => request.get<any[]>('/contract-batch', { params: status ? { status } : {} }),
  /** 单批次详情 */
  get: (jobNo: string) => request.get<any>(`/contract-batch/${jobNo}`),
  /** 取消处理中批次 */
  cancel: (jobNo: string) => request.post<any>(`/contract-batch/${jobNo}/cancel`),
  /** 审批已完成但合同自动创建失败时重试 */
  retryApply: (jobNo: string) => request.post<any>(`/contract-batch/${jobNo}/retry-apply`),
}

// ⭐ #268 Phase 1 法人公司 CRUD (替代原 SystemController 原生 JDBC /system/legal-entities)
// 后端: LegalEntityController @ /legal-entity
// 写操作需 integration 权限, 走 @AuditLog(module=integration)
export const legalEntityApi = {
  list: () => request.get('/legal-entity'),
  getById: (id: number) => request.get(`/legal-entity/${id}`),
  getByCode: (code: string) => request.get(`/legal-entity/by-code/${code}`),
  create: (data: any) => request.post('/legal-entity', data),
  update: (id: number, data: any) => request.put(`/legal-entity/${id}`, data),
  toggle: (id: number) => request.put(`/legal-entity/${id}/toggle`),
  remove: (id: number) => request.delete(`/legal-entity/${id}`),
}

// ⭐ #268 Phase 2 (V57): 外部系统调用日志 真后端
// 后端: IntegrationCallLogController @ /integration/call-log
// 端点: GET 分页 (targetSystem? status? from? to? page? size?), GET /{id}, POST 上报
// 全部要求 integration 权限. 写操作走 @AuditLog(module=integration, opType=CALL_LOG)
// log_no 格式: ICL-yyyyMMdd-XXXX (UK), status: SUCCESS/FAIL/TIMEOUT
export const callLogApi = {
  list: (params: {
    targetSystem?: string
    status?: 'SUCCESS' | 'FAIL' | 'TIMEOUT'
    from?: string
    to?: string
    page?: number
    size?: number
  }) => request.get('/integration/call-log', { params }),
  get: (id: number) => request.get(`/integration/call-log/${id}`),
  create: (data: {
    targetSystem: string
    apiPath: string
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    requestParams?: string
    responseCode?: number
    responseBody?: string
    durationMs?: number
    status?: 'SUCCESS' | 'FAIL' | 'TIMEOUT'
    errorMessage?: string
  }) => request.post('/integration/call-log', data),
}

// ⭐ #268 Phase 2 (V57): 法人 ↔ 外部系统关联 真后端
// 后端: LegalEntitySystemLinkController @ /legal-entity-system-link
// UK (legal_entity_id, system_code): 同一法人对同一系统只挂一次, 重复 create 报错
// sync(): mock 同步 → sync_status=SUCCESS + 落一条 /mock/sync/... 调用日志到 integration_call_log
// 全部要求 integration 权限, 写操作走 @AuditLog(module=integration)
export const legalEntitySystemLinkApi = {
  list: (legalEntityId: number) =>
    request.get('/legal-entity-system-link', { params: { legalEntityId } }),
  get: (id: number) => request.get(`/legal-entity-system-link/${id}`),
  create: (data: {
    legalEntityId: number
    systemCode: string
    externalId?: string
    syncEnabled?: 0 | 1
  }) => request.post('/legal-entity-system-link', data),
  update: (id: number, data: {
    externalId?: string
    syncEnabled?: 0 | 1
    syncStatus?: 'NEVER' | 'SUCCESS' | 'FAIL'
    syncError?: string
  }) => request.put(`/legal-entity-system-link/${id}`, data),
  remove: (id: number) => request.delete(`/legal-entity-system-link/${id}`),
  sync: (id: number) => request.put(`/legal-entity-system-link/${id}/sync`),
}

// ⭐ P1.1 Phase2 Step1 合同类型字典 (8 内置 + HR 可自配新增)
export const contractTypeApi = {
  listAll: () => request.get('/contract-type'),
  getById: (id: number) => request.get(`/contract-type/${id}`),
  getByCode: (code: string) => request.get(`/contract-type/by-code/${code}`),
  create: (data: any) => request.post('/contract-type', data),
  update: (id: number, data: any) => request.put(`/contract-type/${id}`, data),
  remove: (id: number) => request.delete(`/contract-type/${id}`),
  toggle: (id: number) => request.put(`/contract-type/${id}/toggle`),
  // Q6.1 SPEC 2026-06-02: 按合同类型 + 期限解析试用期上限
  resolveProbation: (typeCode: string, termMonths?: number) =>
    request.get('/contract-type/resolve-probation', { params: { typeCode, termMonths } }),
}

// ⭐ P1.1 Phase2 Step4 合同审批流配置 (单例 + 按类型覆盖)
export const contractWorkflowApi = {
  listAll: () => request.get('/contract-workflow-config'),
  getById: (id: number) => request.get(`/contract-workflow-config/${id}`),
  resolve: (contractTypeCode?: string) =>
    request.get('/contract-workflow-config/resolve', { params: contractTypeCode ? { contractTypeCode } : {} }),
  create: (data: any) => request.post('/contract-workflow-config', data),
  update: (id: number, data: any) => request.put(`/contract-workflow-config/${id}`, data),
  remove: (id: number) => request.delete(`/contract-workflow-config/${id}`),
}

// ⭐ P1.1 Phase2 Step3 合同模板 (DOCX 上传 + 版本管理 + 占位符)
export const contractTemplateApi = {
  listAll: () => request.get('/contract-template'),
  getById: (id: number) => request.get(`/contract-template/${id}`),
  create: (data: any) => request.post('/contract-template', data),
  update: (id: number, data: any) => request.put(`/contract-template/${id}`, data),
  remove: (id: number) => request.delete(`/contract-template/${id}`),
  toggle: (id: number) => request.put(`/contract-template/${id}/toggle`),
  activate: (id: number) => request.put(`/contract-template/${id}/activate`),
  newVersion: (id: number) => request.post(`/contract-template/${id}/new-version`),
  upload: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post(`/contract-template/${id}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  /** 带登录态下载模板文件；附件接口要求 Authorization，不能使用裸 a.href。 */
  download: (id: number) =>
    request.get<Blob>(`/contract-template/${id}/download`, { responseType: 'blob' }),
}

// ⭐ UAT-FB#62 Phase B (2026-06-16): Offer 模板 (DOCX + 占位符)
// 镜像 contractTemplateApi, URL 前缀改 /offer-template, 字段 contractTypeCode → jobCategory
export const offerTemplateApi = {
  listAll: () => request.get('/offer-template'),
  getById: (id: number) => request.get(`/offer-template/${id}`),
  create: (data: any) => request.post('/offer-template', data),
  update: (id: number, data: any) => request.put(`/offer-template/${id}`, data),
  remove: (id: number) => request.delete(`/offer-template/${id}`),
  toggle: (id: number) => request.put(`/offer-template/${id}/toggle`),
  activate: (id: number) => request.put(`/offer-template/${id}/activate`),
  newVersion: (id: number) => request.post(`/offer-template/${id}/new-version`),
  upload: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post(`/offer-template/${id}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  /** 下载 URL (浏览器直接打开走 nginx 透传到后端) */
  downloadUrl: (id: number) => `/api/offer-template/${id}/download`,
  uploadUrl: (id: number) => `/api/offer-template/${id}/upload`,
}

// ⭐ P1.2 入职办理 (骨架, 7 阶段状态机扭转等 SPEC 确认后扩展)
export const onboardApi = {
  list: (status?: string, filters?: { expectJoin?: string; activeOnly?: boolean }) =>
    request.get('/onboard', { params: { ...(status ? { status } : {}), ...(filters || {}) } }),
  board: (filters?: { expectJoin?: string; activeOnly?: boolean }) =>
    request.get<any[]>('/onboard/board', { params: filters || {} }),
  /** 跨标准入职、直招和普工通道的已入职审计记录。 */
  completed: () => request.get<any[]>('/onboard/completed'),
  get: (id: number) => request.get(`/onboard/${id}`),
  byCandidate: (candidateId: number) => request.get(`/onboard/by-candidate/${candidateId}`),
  attachments: (id: number) => request.get<any[]>(`/onboard/${id}/attachments`),
  uploadAttachment: (id: number, itemId: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post(`/onboard/${id}/attachment/${itemId}/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  removeAttachment: (id: number, itemId: number) =>
    request.delete(`/onboard/${id}/attachment/${itemId}`),
  downloadAttachment: (id: number, itemId: number) =>
    request.get<Blob>(`/onboard/${id}/attachment/${itemId}/download`, { responseType: 'blob' }),
  finishStage: (id: number, stageCode: string, data?: { dataSnapshot?: string }) =>
    request.post(`/onboard/${id}/stage/${stageCode}/finish`, data || {}),
  saveStageDraft: (id: number, stageCode: string, dataSnapshot: string) =>
    request.post(`/onboard/${id}/stage/${stageCode}/draft`, { dataSnapshot }),
  create: (data: any) => request.post('/onboard', data),
  update: (id: number, data: any) => request.put(`/onboard/${id}`, data),
  remove: (id: number) => request.delete(`/onboard/${id}`),
  // 7.1 YGCS005 (2026-07-02): 入职 KPI 统计 (今日应入职/已取消/pending/inprogress/done)
  stats: () => request.get<{
    todayJoinCount: number; cancelledCount: number;
    pendingCount?: number; inProgressCount?: number; doneCount?: number;
  }>('/onboard/stats'),
  // V345: 取消入职由后端生成取消时间并持久化原因，避免只改状态导致审计信息丢失。
  cancel: (id: number, data: { reasonCode?: string; reason: string }) =>
    request.post(`/onboard/${id}/cancel`, data),
}

export const integrationSystemApi = {
  status: () => request.get('/integration/system-status'),
}

export const businessIntegrationApi = {
  probe: (systemCode: 'MES' | 'WMS' | 'CRM') =>
    request.get(`/integration/connector/${systemCode}/probe`),
  pushEmployee: (systemCode: 'MES' | 'WMS' | 'CRM', payload: Record<string, unknown>) =>
    request.post(`/integration/connector/${systemCode}/employees`, payload),
  pullChanges: (systemCode: 'MES' | 'WMS' | 'CRM', cursor?: string) =>
    request.get(`/integration/connector/${systemCode}/changes`, { params: cursor ? { cursor } : {} }),
}

export interface OnboardTemplateItem {
  code: string
  label: string
  desc: string
  color: string
  minTerminalSteps: number
  minCompletedSteps: number
  blockFailedSteps: boolean
}

export interface OnboardTemplateConfigSnapshot {
  schemaVersion: number
  templates: OnboardTemplateItem[]
  version: number
}

/** 配置型入职模板：与入职单 templateCode 共用后端权威配置。 */
export const onboardTemplateApi = {
  get: () => request.get<OnboardTemplateConfigSnapshot>('/onboard/templates'),
  save: (version: number, templates: OnboardTemplateItem[]) =>
    request.put<OnboardTemplateConfigSnapshot>('/onboard/templates', { version, templates }),
  reset: (version: number) =>
    request.post<OnboardTemplateConfigSnapshot>('/onboard/templates/reset', { version }),
}

// 7.1 YGCS009 (2026-07-02): 转正评估模板真接后端主数据 (原前端 kv 硬编 4 条假数据 · 跟主数据不同源)
// 后端表: probation_template (code/name/status=ACTIVE/version 等)
export const probationTemplateApi = {
  // 透传 config — 业务弹窗自行展示模板加载错误并阻断提交，避免全局重复 toast。
  list: (config?: any) => request.get<any[]>('/probation-template', config),
  get: (id: number) => request.get<any>(`/probation-template/${id}`),
  byCodeActive: (code: string) => request.get<any>(`/probation-template/by-code/${code}/active`),
}

// ⭐ B-1 (2026-06-01): 转正述职 (Q6 SPEC 已确认)
// 7 维评估 + 3 方评 (直属上级/HR/部门负责人) + 状态机 DRAFT→SUBMITTED→APPROVED/REJECTED
export const probationReviewApi = {
  listByEmp: (empId: number) => request.get(`/probation-review/by-emp/${empId}`),
  // UAT-FB#58 Phase 2 (2026-06-16): 按 status 列表 (DRAFT/SUBMITTED/APPROVED/REJECTED) + enriched 员工字段
  // 性能重构: 改服务端分页 + 筛选下推, 返 PageR<{records,total,page,size}>
  listByStatus: (params?: {
    status?: string; statuses?: string; page?: number; size?: number; keyword?: string
    orgId?: string | number; posName?: string; effectiveDateFrom?: string; effectiveDateTo?: string
  }) => request.get('/probation-review/list-by-status', { params }),
  getByProbation: (probationId: number) => request.get(`/probation-review/by-probation/${probationId}`),
  get: (id: number) => request.get(`/probation-review/${id}`),
  salaryContext: (empId: number) => request.get(`/probation-review/salary-context/${empId}`),
  /** 发起评估: probationId 可选 (缺省时后端按 empId 反查/派生 emp_probation 并回填); empId/reviewDate/effectiveDate 必填 */
  create: (data: any) => request.post('/probation-review', data),
  update: (id: number, data: any) => request.put(`/probation-review/${id}`, data),
  submit: (id: number) => request.post(`/probation-review/${id}/submit`),
  repairSignatureChain: (id: number) => request.post(`/probation-review/${id}/repair-signature-chain`),
  finalize: (id: number, conclusion: string, remark?: string, probationSalary?: number,
    regularSalary?: number, extensionEndDate?: string) =>
    request.post(`/probation-review/${id}/finalize`, {
      conclusion, remark, probationSalary, regularSalary, extensionEndDate,
    }),
  remove: (id: number) => request.delete(`/probation-review/${id}`),
  // 7.1 YGCS016 (2026-07-02): 一键无审批批量转正 (权限 emp:approve, 每人独立事务)
  batchQuickRegular: (empIds: number[], effectiveDate: string, reason: string,
    probationSalary?: number, regularSalary?: number) =>
    request.post<{ success: number; failed: number; empIds: number[]; errors: string[] }>(
      '/probation-review/batch-quick-regular',
      { empIds, effectiveDate, reason, probationSalary, regularSalary },
    ),
  directRegular: (data: any) => request.post('/probation-review/direct-regular', data),
  updateProbationInfo: (empId: number, data: any) => request.put(`/probation-review/probation-info/${empId}`, data),
}

// V137 转正自动推送 单例配置 + 立即扫描
export const probationAutoReviewApi = {
  getConfig: () => request.get<any>('/probation-auto-review/config'),
  update: (patch: any) => request.put<any>('/probation-auto-review/config', patch),
  resetScan: () => request.post<void>('/probation-auto-review/reset-scan'),
  runNow: () => request.post<{ triggered: number; message: string }>('/probation-auto-review/run-now'),
}

// ⭐ P1.2 试用期 (骨架, 转正考核流程等 Q6 SPEC 确认后扩展)
// V180 Phase 3 (2026-06-17): 加 withTemplate + submitScores 两端点
export const probationApi = {
  listByEmp: (empId: number) => request.get(`/probation/by-emp/${empId}`),
  get: (id: number) => request.get(`/probation/${id}`),
  expiring: (days = 14) => request.get('/probation/expiring', { params: { days } }),
  /** V180 Phase 3: 评估单 + 模板上下文一次性返回 (含 viewerRole + visibility) */
  getWithTemplate: (id: number, config?: any) => request.get<any>(`/probation/${id}/with-template`, config),
  /** V180 Phase 3: 提交某 viewer 的评分. body: { scoresByDimension: {dimensionId: score} } */
  submitScores: (id: number, scoresByDimension: Record<number, number | string>, config?: any) =>
    request.post<any>(`/probation/${id}/submit-scores`, { scoresByDimension }, config),
  create: (data: any) => request.post('/probation', data),
  update: (id: number, data: any) => request.put(`/probation/${id}`, data),
  remove: (id: number) => request.delete(`/probation/${id}`),
  // UAT-FB#61 Phase 6 (2026-06-17): 员工自助"我的试用期" 聚合 dashboard
  myProgress: () => request.get<MyProbationProgressVO>('/probation/my-progress'),
}

// UAT-FB#61 Phase 6 (2026-06-17): "我的试用期" 聚合 VO (与后端 MyProbationProgressDTO 对齐)
export interface MyProbationProgressVO {
  empId: number
  empName?: string
  joinDate?: string
  employmentStatus?: string
  employmentStatusLabel?: string
  probationMonths?: number
  probationEndDate?: string
  daysLeft?: number | null
  currentStage: 'PROBATION' | 'REVIEWING' | 'SIGNING' | 'ACKED' | 'PENDING_EFFECTIVE' | 'REGULAR'
  currentStageLabel: string
  stageProgress: number
  currentReview?: {
    id: number
    scheduledDate?: string
    status?: string
    totalScore?: number
    light?: string
    result?: string
  } | null
  signatureProgress: Array<{
    signerType: string
    signerTypeLabel: string
    signerName?: string
    status: 'PENDING' | 'PASS' | 'FAIL'
    signedAt?: string
    sequence?: number
  }>
  milestones: Array<{
    name: string
    date: string
    done: boolean
    scheduled?: boolean
  }>
  tasks: Array<{
    type: 'ACK' | 'SIGN' | 'SCORE'
    desc: string
    dueDate?: string
    priority: 'HIGH' | 'MEDIUM' | 'LOW'
    reviewId?: number
  }>
}

// ⭐ Phase C Tier B (3/3 收官): 薪酬模式 (compMode)
export const compModeApi = {
  listAll: () => request.get('/comp-mode'),
  getById: (id: number) => request.get(`/comp-mode/${id}`),
  getByCode: (code: string) => request.get(`/comp-mode/by-code/${code}`),
  create: (data: any) => request.post('/comp-mode', data),
  update: (id: number, data: any) => request.put(`/comp-mode/${id}`, data),
  remove: (id: number) => request.delete(`/comp-mode/${id}`),
  toggle: (id: number) => request.put(`/comp-mode/${id}/toggle`),
}

// ⭐ V43 (2026-06-02): 假勤 - 请假 + 加班 状态机 (DRAFT→PENDING→APPROVED/REJECTED, *→CANCELLED)
// request_no 格式: LR-yyyyMMdd-XXXX (请假) / OT-yyyyMMdd-XXXX (加班)
export const leaveApi = {
  listByEmp: (empId: number, status?: string) =>
    request.get('/att/leaves', { params: { empId, ...(status ? { status } : {}) } }),
  // 2026-07-22 修: 必须带 applyType=LEAVE — 后端 applyType 空返所有类型,
  // 否则请假待审列表会混入 TRIP/FIELD_WORK 行 (审批中心同一出差单显示两次, 细类显原始码 TRIP)
  listPending: () => request.get('/att/leaves', { params: { status: 'PENDING', applyType: 'LEAVE' } }),
  listReviewed: () => request.get('/att/leaves/reviewed', { params: { applyType: 'LEAVE' } }),
  pagePending: (current = 1, size = 20) =>
    request.get('/att/leaves/page', { params: { current, size, applyType: 'LEAVE' } }),
  get: (id: number) => request.get(`/att/leaves/${id}`),
  create: (data: any) => request.post('/att/leaves', data),
  submit: (id: number) => request.post(`/att/leaves/${id}/submit`),
  approve: (id: number) => request.post(`/att/leaves/${id}/approve`),
  reject: (id: number, reason: string) => request.post(`/att/leaves/${id}/reject`, { reason }),
  cancel: (id: number) => request.post(`/att/leaves/${id}/cancel`),
  requestCancellation: (id: number, reason: string) =>
    request.post(`/att/leaves/${id}/cancellation`, { reason }),
}

// V197: 请假类型配置 (deduct_ratio 驱动请假扣款进算薪). /att/leave-type CRUD.
export const leaveTypeApi = {
  list: () => request.get<any[]>('/att/leave-type'),
  create: (data: any) => request.post<any>('/att/leave-type', data),
  update: (id: number, data: any) => request.put<any>(`/att/leave-type/${id}`, data),
  remove: (id: number) => request.delete(`/att/leave-type/${id}`),
}

export const overtimeApi = {
  listByEmp: (empId: number, status?: string) =>
    request.get('/att/overtime', { params: { empId, ...(status ? { status } : {}) } }),
  listPending: () => request.get('/att/overtime', { params: { status: 'PENDING' } }),
  listReviewed: () => request.get('/att/overtime/reviewed'),
  pagePending: (current = 1, size = 20) =>
    request.get('/att/overtime/page', { params: { current, size } }),
  get: (id: number) => request.get(`/att/overtime/${id}`),
  create: (data: any) => request.post('/att/overtime', data),
  submit: (id: number) => request.post(`/att/overtime/${id}/submit`),
  approve: (id: number) => request.post(`/att/overtime/${id}/approve`),
  reject: (id: number, reason: string) => request.post(`/att/overtime/${id}/reject`, { reason }),
  cancel: (id: number) => request.post(`/att/overtime/${id}/cancel`),
}

// ⭐ V53 (2026-06-02): 补卡申请 (独立表, 与 leave_request 解耦)
// request_no 格式: PC-yyyyMMdd-XXXX
// correct_type: FORGOT_IN/FORGOT_OUT/BOTH (漏上班/漏下班/双漏)
// 状态机: DRAFT → PENDING → APPROVED/REJECTED; ANY(非终态) → CANCELLED
// UAT-FB#122 #5 (2026-06-18): 异常工单 (V190 att_exception_ticket)
export const attExceptionTicketApi = {
  list: (params?: Record<string, any>) =>
    request.get<any[]>('/att/exception-ticket/list', { params: params || {} }),
  create: (data: any) => request.post<any>('/att/exception-ticket', data),
  update: (id: number, data: any) => request.put<any>(`/att/exception-ticket/${id}`, data),
  assign: (id: number, assigneeId: number) =>
    request.post<string>(`/att/exception-ticket/${id}/assign`, { assigneeId }),
  resolve: (id: number, resolution: string) =>
    request.post<string>(`/att/exception-ticket/${id}/resolve`, { resolution }),
  reject: (id: number, resolution: string) =>
    request.post<string>(`/att/exception-ticket/${id}/reject`, { resolution }),
  close: (id: number) => request.post<string>(`/att/exception-ticket/${id}/close`),
  batchResolve: (ids: number[], resolution: string) =>
    request.post<{ updated: number }>('/att/exception-ticket/batch-resolve', { ids, resolution }),
  scan: (startDate: string, endDate: string) =>
    request.post<{ created: number; startDate: string; endDate: string }>('/att/exception-ticket/scan', { startDate, endDate }),
}

// UAT-FB#122 (2026-06-18): 考勤组 (V189 att_group) — 员工 → 组 → 扣款规则枢纽
export const attGroupApi = {
  list: () => request.get<any[]>('/att/group'),
  empCount: (id: number) => request.get<number>(`/att/group/${id}/emp-count`),
  create: (data: any) => request.post<any>('/att/group', data),
  update: (id: number, data: any) => request.put<any>(`/att/group/${id}`, data),
  delete: (id: number) => request.delete<string>(`/att/group/${id}`),
  setDefault: (id: number) => request.post<string>(`/att/group/${id}/set-default`),
  // UAT-FB#122 V189 gap #1 (2026-06-18): 按 scope 自动匹配员工挂组
  autoAssign: (id: number, overwrite = false) =>
    request.post<{ matched: number; updated: number; message: string }>(
      `/att/group/${id}/auto-assign`, null, { params: { overwrite } }),
  autoAssignAll: (overwrite = false) =>
    request.post<{ matched: number; updated: number; message: string }>(
      `/att/group/auto-assign-all`, null, { params: { overwrite } }),
}

// UAT-FB#122 (2026-06-18): 算薪扣款规则 (V188 att_deduct_rule) — DeductionPanel 用
// 字段: code/name/latePenaltyPerOccurrence/lateMultiplier/lateGraceMinutes/
//      earlyPenaltyPerOccurrence/earlyMultiplier/missChargePerOccurrence/missGracePerMonth/
//      absentChargeMultiplier/perfectAttendanceBonus/perfectAttendanceMaxAbnormal/
//      legalMonthDays/defaultDailyRateFallback/isDefault/isSystem/enabled
export const attDeductRuleApi = {
  list: () => request.get<any[]>('/att/deduct-rule'),
  getDefault: () => request.get<any>('/att/deduct-rule/default'),
  create: (data: any) => request.post<any>('/att/deduct-rule', data),
  update: (id: number, data: any) => request.put<any>(`/att/deduct-rule/${id}`, data),
  delete: (id: number) => request.delete<string>(`/att/deduct-rule/${id}`),
  setDefault: (id: number) => request.post<string>(`/att/deduct-rule/${id}/set-default`),
}

// 2026-07-16 打卡明细: 原始流水查询 + 导出 (zip 含照片) — PunchRecordDrawer 用
export const attPunchRecordApi = {
  /** 原始流水: 每笔一行, 含照片/GPS/WiFi/设备 */
  records: (params: { empId?: number; keyword?: string; status?: 'NORMAL' | 'ABNORMAL' | 'REJECTED'; dateFrom: string; dateTo: string; limit?: number }) =>
    request.get<any[]>('/att/punch/records', { params }),
  /** 导出 zip (明细 Excel + photos/ 照片), 返回 blob */
  exportZip: (params: { empId?: number; keyword?: string; dateFrom: string; dateTo: string }) =>
    request.get('/att/punch/records/export', { params, responseType: 'blob' }) as unknown as Promise<Blob>,
  approve: (id: number, comment: string) => request.post(`/att/punch/${id}/approve`, { comment }),
  reject: (id: number, comment: string) => request.post(`/att/punch/${id}/reject`, { comment }),
}

export const attTeamApi = {
  directReports: () => request.get<Array<{ empId: number; empNo: string; name: string; orgId?: number }>>('/att/team/direct-reports'),
}

// V279 假期余额引擎: 假期额度规则 (leave_policy) — LeavePolicyPanel 用
// 发放方式/工龄口径/阶梯/折算/有效期/结转/透支/适用范围, 由 LeaveBalanceService 发放/冻结时消费
export const leavePolicyApi = {
  list: (leaveType?: string) =>
    request.get<any[]>('/leave-policy', { params: leaveType ? { leaveType } : {} }),
  get: (id: number) => request.get<any>(`/leave-policy/${id}`),
  create: (data: any) => request.post<any>('/leave-policy', data),
  update: (id: number, data: any) => request.put<any>(`/leave-policy/${id}`, data),
  delete: (id: number) => request.delete<string>(`/leave-policy/${id}`),
  /** 运维: 手工发放/补发某假种某年度额度 (定时任务外) */
  grantAll: (leaveType: string, year?: number) =>
    request.post<any>('/leave-balance/grant-all', null, { params: { leaveType, ...(year ? { year } : {}) } }),
}

// UAT-FB#122 V191: 加班规则 (att_overtime_rule) — AttOvertimeRulePanel 用
// 字段: code/name/weekdayMultiplier/weekendMultiplier/holidayMultiplier/
//      defaultSettle(PAY|COMP_OFF)/allowCompOff/minHours/isDefault/isSystem/enabled
export const attOvertimeRuleApi = {
  list: () => request.get<any[]>('/att/overtime-rule'),
  getDefault: () => request.get<any>('/att/overtime-rule/default'),
  create: (data: any) => request.post<any>('/att/overtime-rule', data),
  update: (id: number, data: any) => request.put<any>(`/att/overtime-rule/${id}`, data),
  delete: (id: number) => request.delete<string>(`/att/overtime-rule/${id}`),
  setDefault: (id: number) => request.post<string>(`/att/overtime-rule/${id}/set-default`),
}

// UAT-FB#122 (2026-06-18): 打卡数据 Excel 批量导入 — 兜底 / 历史数据迁移用
export const attPunchImportApi = {
  downloadTemplate: () =>
    request.get('/att/punch-import/template.xlsx', { responseType: 'blob' }),
  upload: (formData: FormData) =>
    request.post('/att/punch-import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
}

// att 首页「打卡记录」逐日名册：排班 ∪ 打卡，两个来源都受 @DataScope 过滤。
//   返回行: {empId,empNo,name,dept,location,position,punchIn,punchOut,workHours,
//            status(NORMAL/ABNORMAL/MISS/LEAVE/TRIP/FIELD_WORK/REST),scheduleStatus,effectiveStatus,
//            abnormalReason,source,gpsAddress,gpsLat,gpsLng,ipAddress,photoUrl,punchCount}
export const attPunchApi = {
  daily: (date?: string, keyword?: string) =>
    request.get<any[]>('/att/punch/daily', {
      params: { ...(date ? { date } : {}), ...(keyword ? { keyword } : {}) },
    }),
}

export const punchCorrectionApi = {
  // 通用 list — 任意筛选参数 (empId/status/date 区间等), PunchCorrectionPanel 用
  list: (params?: Record<string, any>) =>
    request.get('/att/punch-correction', { params: params || {} }),
  listByEmp: (empId: number, status?: string) =>
    request.get('/att/punch-correction', { params: { empId, ...(status ? { status } : {}) } }),
  listPending: () => request.get('/att/punch-correction', { params: { status: 'PENDING' } }),
  listReviewed: () => request.get('/att/punch-correction/reviewed'),
  pagePending: (current = 1, size = 20) =>
    request.get('/att/punch-correction/page', { params: { current, size } }),
  create: (data: any) => request.post('/att/punch-correction', data),
  submit: (id: number) => request.post(`/att/punch-correction/${id}/submit`),
  approve: (id: number) => request.post(`/att/punch-correction/${id}/approve`),
  reject: (id: number, reason: string) => request.post(`/att/punch-correction/${id}/reject`, { reason }),
  cancel: (id: number) => request.post(`/att/punch-correction/${id}/cancel`),
  // 补签管理: HR 全员全部状态 + 多维筛选 (status/empId/orgId/dateFrom/dateTo), 回填姓名/部门/岗位/审批人
  manage: (params?: Record<string, any>) =>
    request.get('/att/punch-correction/manage', { params: params || {} }),
  // 批量审批 → { total, success, failed, failures:[{id, reason}] }
  batchApprove: (ids: number[]) => request.post('/att/punch-correction/batch-approve', { ids }),
  batchReject: (ids: number[], reason: string) =>
    request.post('/att/punch-correction/batch-reject', { ids, reason }),
}

// ⭐ V44 (2026-06-02): 假勤配置 - 排班模式 + 节假日 (AttConfigController 真后端)
// work_pattern: code/name/patternType (FIXED/ROTATING/FLEX) / workDaysPerWeek / weekdayConfig (JSON str) / shiftRotation (JSON str)
// holiday_meta: year/holidayDate/name/holidayType (STATUTORY/COMPANY/CULTURAL) / isWorkday (0=放假 1=调休上班) / payRate
// 注意 1: 写操作要求 @PreAuthorize hasAuthority('att')
// 注意 2: toggle 用 POST (后端实际是 @PostMapping, 不是 PUT)
// 注意 3: list 用 /work-pattern/all 拿全部 (含 enabled=0), /work-pattern 只返回 enabled=1
export const attConfigApi = {
  listAuditHistory: (limit = 100) => request.get('/att/config-audit', { params: { limit } }),
  listWorkPatterns: () => request.get('/att/work-pattern/all'),
  listWorkPatternsEnabled: () => request.get('/att/work-pattern'),
  getWorkPattern: (id: number) => request.get(`/att/work-pattern/${id}`),
  createWorkPattern: (data: any) => request.post('/att/work-pattern', data),
  updateWorkPattern: (id: number, data: any) => request.put(`/att/work-pattern/${id}`, data),
  deleteWorkPattern: (id: number) => request.delete(`/att/work-pattern/${id}`),
  toggleWorkPattern: (id: number) => request.post(`/att/work-pattern/${id}/toggle`),
  listHolidays: (year: number) => request.get('/att/holiday', { params: { year } }),
  getHoliday: (id: number) => request.get(`/att/holiday/${id}`),
  createHoliday: (data: any) => request.post('/att/holiday', data),
  updateHoliday: (id: number, data: any) => request.put(`/att/holiday/${id}`, data),
  batchImportHolidays: (data: any[]) => request.post('/att/holiday/batch', data),
  deleteHoliday: (id: number) => request.delete(`/att/holiday/${id}`),
  // ⭐ V46 (2026-06-02): 员工×排班模式绑定 (assign/current/历史)
  // 端点定义: AttController.java (work-pattern/emp/{id}/current, work-pattern/emp/{id}, work-pattern/{patternId}/assign)
  // assign 写入后旧版本 current_flag 自动清 0, 不删除 (审计保留)
  getEmpCurrentPattern: (empId: number) =>
    request.get(`/att/work-pattern/emp/${empId}/current`),
  listEmpPatterns: (empId: number) =>
    request.get(`/att/work-pattern/emp/${empId}`),
  assignPattern: (patternId: number, empId: number, startDate: string, endDate?: string, remark?: string) =>
    request.post(`/att/work-pattern/${patternId}/assign`, null, {
      params: { empId, startDate, ...(endDate ? { endDate } : {}), ...(remark ? { remark } : {}) },
    }),
}

// ⭐ V55 (2026-06-02): 背调厂商配置 (BgCheckVendor) — 真后端 /bg-check/vendor CRUD
//   后端: EmpBgCheckController (commit 9dd3da9), 写操作需 @PreAuthorize hasAuthority('emp')
//   entity 字段: id/code(UK)/name/category(BG_CHECK/IDENTITY/CRIMINAL/EDUCATION/EMPLOYMENT)/
//                apiEndpoint/apiKey/contact/slaDays/enabled/sortOrder/remark/orgId
//   注意:
//     - GET /bg-check/vendor 只返 enabled=1; HR 配置页须用 /vendor/all 拿全部 (含禁用)
//     - code 是业务键 (订单 vendor_code 引用), 创建后不可改
//     - apiKey v1 明文存储, 后续与 employee.id_card 同期升 SM4 加密
export const bgCheckVendorApi = {
  /** 启用厂商 (前端发起背调下拉用) */
  list: () => request.get('/bg-check/vendor'),
  /** 全部厂商 (含禁用), HR 配置页用 */
  listAll: () => request.get('/bg-check/vendor/all'),
  create: (data: any) => request.post('/bg-check/vendor', data),
  update: (id: number, data: any) => request.put(`/bg-check/vendor/${id}`, data),
  toggle: (id: number) => request.put(`/bg-check/vendor/${id}/toggle`),
  remove: (id: number) => request.delete(`/bg-check/vendor/${id}`),
}

// ⭐ V56 (2026-06-02): 员工档案变更审批 (EmpChangeRequest) — 真后端 8 端点
//   后端: EmpChangeRequestController (V56), 写操作需 @PreAuthorize hasAuthority('emp')
//   request_no 格式: ECR-yyyyMMdd-XXXX (UK)
//   状态机: DRAFT → PENDING → APPROVED → [apply: applied=1] / REJECTED
//          * (非终态且 applied=0) → CANCELLED
//   字段白名单 (v1): mobile / email / bankCard / hukouCity / emergencyPhone
//   双步设计:
//     - approve() 仅置 status=APPROVED, 不真改 employee
//     - apply()   才真改 employee + applied=1 (mobile/bankCard/emergencyPhone 同步 SM4 加密)
//   V308: oldValue/newValue 全字段均 SM4 密文落库，受权读取时由后端解密，接口字段保持不变
//   listByEmp(empId, status?) 用于员工详情时间线; workQueue() 用于 HR 双步工作队列
//   workQueue 同时返回 PENDING 与 APPROVED/applied=0，避免审批通过后从待应用区消失
export const empChangeApi = {
  listByEmp: (empId: number, status?: string) =>
    request.get('/emp-change', { params: { empId, ...(status ? { status } : {}) } }),
  listPending: () => request.get('/emp-change', { params: { status: 'PENDING' } }),
  workQueue: () => request.get('/emp-change/work-queue'),
  overview: () => request.get('/emp-change/overview'),
  pagePending: (current = 1, size = 20) =>
    request.get('/emp-change/page', { params: { current, size } }),
  listAll: () => request.get('/emp-change'),
  get: (id: number) => request.get(`/emp-change/${id}`),
  create: (data: any) => request.post('/emp-change', data),
  submit: (id: number) => request.post(`/emp-change/${id}/submit`),
  approve: (id: number) => request.post(`/emp-change/${id}/approve`),
  reject: (id: number, reason: string) =>
    request.post(`/emp-change/${id}/reject`, { reason }),
  cancel: (id: number) => request.post(`/emp-change/${id}/cancel`),
  apply: (id: number) => request.post(`/emp-change/${id}/apply`),
}

// ⭐ V55 (2026-06-02): 背调订单 (EmpBgCheck) — 真后端 9 端点
//   后端: EmpBgCheckController (commit 9dd3da9)
//   order_no 格式: BG-yyyyMMdd-XXXX (UK)
//   状态机: PENDING → IN_PROGRESS → COMPLETED | FAILED; PENDING → CANCELLED
//   注意:
//     - submit body: { empId?, candId?, vendorCode }, 二选一可空 (入职前 cand, 在职 emp)
//     - complete 用 query params (riskLevel/reportUrl), body 为 null
//     - 真生产 status 流转由后端推 webhook /bg-check/callback/{orderNo}
//     - 前端轮询 GET /bg-check?empId= 看 status 更新
//   写操作要求 @PreAuthorize hasAuthority('emp')
export const bgCheckApi = {
  listVendors: () => request.get('/bg-check/vendor'),
  list: (params: { empId?: number; candId?: number; status?: string }) =>
    request.get('/bg-check', { params }),
  get: (id: number) => request.get(`/bg-check/${id}`),
  submit: (data: { empId?: number; candId?: number; vendorCode: string }) =>
    request.post('/bg-check/submit', data),
  markInProgress: (orderNo: string) => request.post(`/bg-check/${orderNo}/in-progress`),
  complete: (orderNo: string, riskLevel: string, reportUrl?: string) =>
    request.post(`/bg-check/${orderNo}/complete`, null, {
      params: { riskLevel, ...(reportUrl ? { reportUrl } : {}) },
    }),
  fail: (orderNo: string, reason: string) =>
    request.post(`/bg-check/${orderNo}/fail`, { reason }),
  cancel: (orderNo: string) => request.post(`/bg-check/${orderNo}/cancel`),
}

// ⭐ V60 (#268 Phase 3): SSO Provider + SSO Login Log + API Key — Wave Y1 真接后端
//   后端: SsoProviderController / SsoLoginLogController / ApiKeyController (commit e836f21)
//   注意:
//     - SsoProvider 字段: code/name/providerType(OAUTH2/SAML/LDAP/CUSTOM)/clientId/clientSecret(写)/clientSecretMask(读)/loginUrl/callbackUrl/scope/enabled
//     - clientSecret 写入时填明文, 后端 SM4 加密入 enc + 写 mask; 读不返明文
//     - toggle 是 PUT (不是 POST)
//     - sso-login-log 后端返 PageR<SsoLoginLog>, 不是裸 List
//     - api-key 创建/重生成响应里 apiKey 字段是一次性明文, 之后 GET 只返 apiKeyMask
export const ssoProviderApi = {
  list: () => request.get<any[]>('/sso-provider'),
  listEnabled: () => request.get<any[]>('/sso-provider/enabled'),
  get: (id: number) => request.get(`/sso-provider/${id}`),
  create: (data: any) => request.post('/sso-provider', data),
  update: (id: number, data: any) => request.put(`/sso-provider/${id}`, data),
  toggle: (id: number) => request.put(`/sso-provider/${id}/toggle`),
  remove: (id: number) => request.delete(`/sso-provider/${id}`),
  // V208 用户同步
  testConnection: (id: number) =>
    request.post<{ ok: boolean; message: string; latencyMs: number }>(`/sso-provider/${id}/test-connection`),
  syncUsers: (id: number) =>
    request.post<{ status: string; total: number; created: number; updated: number; deprovisioned: number; skipped: number; failed: number; message: string }>(`/sso-provider/${id}/sync-users`),
  syncLogs: (providerId?: number) =>
    request.get<any[]>('/sso-provider/sync-logs', { params: { providerId } }),
}

export const ssoLoginLogApi = {
  list: (params?: { providerCode?: string; userId?: number; status?: string; page?: number; size?: number }) =>
    request.get<{ total: number; page: number; size: number; records: any[] }>('/sso-login-log', { params }),
  get: (id: number) => request.get(`/sso-login-log/${id}`),
}

export const ssoMockLoginApi = (data: { providerCode: string; externalId: string; externalName?: string; externalEmail?: string }) =>
  request.post<{ token: string; logNo: string; provider: string; user: any; mock: boolean }>('/sso/mock-login', data)

export const apiKeyApi = {
  list: () => request.get<any[]>('/api-key'),
  get: (id: number) => request.get(`/api-key/${id}`),
  create: (data: { name: string; permissions?: string; remark?: string; expiresAt?: string }) =>
    request.post<any>('/api-key', data),
  update: (id: number, data: any) => request.put(`/api-key/${id}`, data),
  toggle: (id: number) => request.put(`/api-key/${id}/toggle`),
  regenerate: (id: number) => request.post<any>(`/api-key/${id}/regenerate`),
  remove: (id: number) => request.delete(`/api-key/${id}`),
}

// ⭐ V61 (2026-06-02): emp 入职 8 阶段状态机 (W2 / Y2) — 真后端
//   后端: OnboardStageMetaController + EmpOnboardStepController (commit e836f21)
//   8 阶段 seed: CANDIDATE_CONFIRM / OFFER_ACCEPTED / PERSONAL_INFO / CONTRACT /
//                MEDICAL / ACCOUNT / REPORT_FIRST_DAY / PROBATION_START
//   状态机: PENDING ⇄ IN_PROGRESS → COMPLETED | SKIPPED | FAILED
//   ⚠️ 重复 init 同一 onboardId 后端返 400 BizException
export const onboardStageMetaApi = {
  list: () => request.get<any[]>('/onboard-stage-meta'),
  create: (data: any) => request.post('/onboard-stage-meta', data),
  update: (id: number, data: any) => request.put(`/onboard-stage-meta/${id}`, data),
  toggle: (id: number) => request.post(`/onboard-stage-meta/${id}/toggle`),
}

export const empOnboardStepApi = {
  init: (onboardId: number) => request.post<any[]>(`/emp-onboard-step/init/${onboardId}`),
  listByOnboard: (onboardId: number) => request.get<any[]>(`/emp-onboard-step/by-onboard/${onboardId}`),
  start: (stepId: number) => request.post(`/emp-onboard-step/${stepId}/start`, {}),
  complete: (stepId: number, dataSnapshot?: string) =>
    request.post(`/emp-onboard-step/${stepId}/complete`, dataSnapshot ? { dataSnapshot } : {}),
  skip: (stepId: number, reason: string) => request.post(`/emp-onboard-step/${stepId}/skip`, { reason }),
  fail: (stepId: number, reason: string) => request.post(`/emp-onboard-step/${stepId}/fail`, { reason }),
  reopen: (stepId: number) => request.post(`/emp-onboard-step/${stepId}/reopen`, {}),
}

// ⭐ V62 社保 Phase A (ZA) — Phase C 前端真接 (BC 2026-06-03)
//   后端: SocialPolicyController / EmpSocialArchiveController / SocialChangeController (commit b32970e)
//   路径: /social-policy + /emp-social-archive + /social-change (与旧 stub /social/policies 独立)
//   注意 1: toggle 是 POST (后端 @PostMapping("/{id}/toggle"))
//   注意 2: SocialChangeController 没有分页/listAll 端点, 只有 list/pending/by-emp/process
//   注意 3: 所有写操作要求 @PreAuthorize hasAuthority('social')
//   字段差距详见 utils/socialPolicyAdapter.ts (legalEntity / baseBrackets / DRAFT 降维等)
export const socialPolicyApi = {
  list: (enabledOnly?: boolean) =>
    request.get<any[]>('/social-policy', { params: enabledOnly ? { enabledOnly: true } : {} }),
  get: (id: number) => request.get(`/social-policy/${id}`),
  create: (data: any) => request.post('/social-policy', data),
  update: (id: number, data: any) => request.put(`/social-policy/${id}`, data),
  toggle: (id: number) => request.post(`/social-policy/${id}/toggle`),
}

export const empSocialArchiveApi = {
  listByEmp: (empId: number) => request.get<any[]>(`/emp-social-archive/by-emp/${empId}`),
  get: (id: number) => request.get(`/emp-social-archive/${id}`),
  create: (data: any) => request.post('/emp-social-archive', data),
  update: (id: number, data: any) => request.put(`/emp-social-archive/${id}`, data),
  inactivate: (id: number) => request.post(`/emp-social-archive/${id}/inactivate`),
  // 去 mock 批3(social, 2026-07-14): 补 listAll/page/batchAdjust 封装(后端 EmpSocialArchiveController
  //   早有, 前端漏封装)。listAll/page 返 VO(含 empName/empNo/dept, 后端 join employee 回填)。
  //   batchAdjust: 5 险种独立基数(养老/医疗/失业/工伤/生育)+ 公积金独立(口径已定, 走 5 字段路径)。
  listAll: () => request.get<any[]>('/emp-social-archive'),
  page: (params: { current?: number; size?: number; status?: string; policyCode?: string; orgId?: number }) =>
    request.get('/emp-social-archive/page', { params }),
  batchAdjust: (payload: {
    empIds: number[]
    pensionBase?: number; medicalBase?: number; unemploymentBase?: number
    injuryBase?: number; maternityBase?: number
    housingBase?: number
    effectiveFrom: string; reason: string
  }) => request.post<number>('/emp-social-archive/batch-adjust', payload),
}

export const socialChangeApi = {
  list: () => request.get<any[]>('/social-change'),
  create: (data: any) => request.post('/social-change', data),
  listPending: () => request.get<any[]>('/social-change/pending'),
  listByEmp: (empId: number) => request.get<any[]>(`/social-change/by-emp/${empId}`),
  process: (id: number) => request.post(`/social-change/${id}/process`),
  approve: (id: number) => request.post(`/social-change/${id}/approve`),
  reject: (id: number, reason: string) => request.post(`/social-change/${id}/reject`, { reason }),
  revoke: (id: number) => request.post(`/social-change/${id}/revoke`),
}

// ═══════════════════════════════════════════════════════════════
// XCCS034 (2026-07-07): 月度社保核算 · social_calc_record 真接
//   后端: SocialCalcController @ /social/calc
//   - POST /run?period=YYYY-MM        · HR 手动触发, 幂等 (CONFIRMED+ 跳过, DRAFT 覆盖)
//   - GET  /list?period&empId&status&type&current&size · 分页
//   - GET  /{id}                       · 单条明细
//   - GET  /by-period?period=          · 周期全量 (给 banner 汇总)
//   - POST /{id}/confirm               · DRAFT → CONFIRMED
//   - POST /confirm-by-period?period=  · 批量 DRAFT → CONFIRMED
// ═══════════════════════════════════════════════════════════════
export interface SocialCalcRunResult {
  inserted: number
  updated: number
  skipped: number
  period: string
}

export interface SocialCalcRecordVO {
  id: number
  period: string
  empId: number
  empNo?: string
  empName?: string
  archiveId: number
  policyId: number
  type: string           // STATUTORY | SUPPLEMENT
  pensionBase: number; pensionEmp: number; pensionComp: number
  medicalBase: number; medicalEmp: number; medicalComp: number
  unemploymentBase: number; unemploymentEmp: number; unemploymentComp: number
  injuryBase: number; injuryEmp: number; injuryComp: number
  maternityBase: number; maternityEmp: number; maternityComp: number
  housingBase: number; housingEmp: number; housingComp: number
  totalEmp: number; totalComp: number; totalAll: number
  status: string         // DRAFT | CONFIRMED | DECLARED | PAID
  orgId?: number
  orgName?: string
  createdByOrgId?: number
  createdAt?: string
  updatedAt?: string
}

export const socialCalcApi = {
  /** D3: HR 手动触发月度核算 · 幂等. */
  run: (period: string) =>
    request.post<SocialCalcRunResult>('/social/calc/run', null, { params: { period } }),

  /** 分页查明细. period / empId / status / type 均可选. */
  list: (params: {
    period?: string
    empId?: number
    status?: string
    type?: string
    current?: number
    size?: number
  } = {}) =>
    request.get<{ records: SocialCalcRecordVO[]; total: number; current: number; size: number }>(
      '/social/calc/list', { params },
    ),

  /** 单条明细. */
  get: (id: number) => request.get<SocialCalcRecordVO>(`/social/calc/${id}`),

  /** 周期全量 (不分页) · 给 banner 汇总用. */
  byPeriod: (period: string) =>
    request.get<SocialCalcRecordVO[]>('/social/calc/by-period', { params: { period } }),

  /** 单条确认: DRAFT → CONFIRMED. */
  confirm: (id: number) => request.post<SocialCalcRecordVO>(`/social/calc/${id}/confirm`),

  /** 批量确认: 按 period 一次全转. */
  confirmByPeriod: (period: string) =>
    request.post<number>('/social/calc/confirm-by-period', null, { params: { period } }),

  /** XCCS036: HR 手动触发补充险种月度核算 (type=SUPPLEMENT). 幂等. */
  runSupplement: (period: string) =>
    request.post<SocialCalcRunResult>('/social/calc/run-supplement', null, { params: { period } }),
}

// ═══════════════════════════════════════════════════════════════
// XCCS036 (2026-07-07): 社保申报单 · social_declaration 真接
//   后端: SocialDeclarationController @ /social/declaration
//   - POST /generate?period=YYYY-MM       · 按 (法人+城市) 聚合 CONFIRMED+ 状态生成申报单
//   - GET  /list?period&legalEntityCode&city&status&current&size · 分页
//   - GET  /{id}                          · 单条详情
//   - POST /{id}/submit                   · PENDING → SUBMITTED
//   - POST /{id}/pay                      · SUBMITTED → PAID
//   - POST /{id}/cancel   { reason? }     · PENDING/SUBMITTED → CANCELLED
//   - GET  /{id}/export.xlsx              · 员工级明细 xlsx 下载
// ═══════════════════════════════════════════════════════════════
export interface SocialDeclarationVO {
  id: number
  period: string
  legalEntityCode: string
  city: string
  empCount: number
  totalEmp: number
  totalComp: number
  totalAll: number
  status: string           // PENDING | SUBMITTED | PAID | CANCELLED
  submittedAt?: string
  submittedBy?: number
  submittedByName?: string
  paidAt?: string
  fileUrl?: string
  remark?: string
  orgId?: number
  createdByOrgId?: number
  createdAt?: string
  updatedAt?: string
}

export interface SocialDeclarationGenResult {
  inserted: number
  updated: number
  skipped: number
  period: string
}

export const socialDeclarationApi = {
  /** 按 period 聚合生成申报单 (幂等 · PENDING 覆盖, SUBMITTED+ 跳过). */
  generate: (period: string) =>
    request.post<SocialDeclarationGenResult>('/social/declaration/generate', null, { params: { period } }),

  /** 分页查列表. */
  list: (params: {
    period?: string
    legalEntityCode?: string
    city?: string
    status?: string
    current?: number
    size?: number
  } = {}) =>
    request.get<{ records: SocialDeclarationVO[]; total: number; current: number; size: number }>(
      '/social/declaration/list', { params },
    ),

  /** 单条详情. */
  get: (id: number) => request.get<SocialDeclarationVO>(`/social/declaration/${id}`),

  /** PENDING → SUBMITTED. */
  submit: (id: number) => request.post<SocialDeclarationVO>(`/social/declaration/${id}/submit`),

  /** SUBMITTED → PAID. */
  pay: (id: number) => request.post<SocialDeclarationVO>(`/social/declaration/${id}/pay`),

  /** PENDING/SUBMITTED → CANCELLED. */
  cancel: (id: number, reason?: string) =>
    request.post<SocialDeclarationVO>(`/social/declaration/${id}/cancel`, { reason: reason ?? '' }),

  /** 导出员工级明细 xlsx (返 blob). */
  exportXlsx: (id: number) =>
    request.get<Blob>(`/social/declaration/${id}/export.xlsx`, { responseType: 'blob' }),

  /** 便利: 拼下载 URL (若前端要 a.href 直下方式, token 从 header 走不了; 走 API + blob 更稳). */
  exportUrl: (id: number) => `/api/social/declaration/${id}/export.xlsx`,
}

// ═══════════════════════════════════════════════════════════════
// V66 notify-1 + V69 RBAC (BUG-003 上线 P0 修)
//   后端: NotifyController @ /notify (commit 5ed0fe4)
//   端点:
//     HR 模板管理 (notify perm):
//       GET    /notify/templates           列表
//       GET    /notify/templates/page      分页
//       POST   /notify/templates           建
//       PUT    /notify/templates/{id}      改
//       DELETE /notify/templates/{id}      删
//     员工自己 (isAuthenticated):
//       GET    /notify/me/list?status=     我的通知 page
//       GET    /notify/me/unread-count     未读数
//       POST   /notify/me/{id}/read        标已读
//       POST   /notify/me/read-all         全已读
//       POST   /notify/me/{id}/archive     归档
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// Stage 3 P0 真化: 月度算薪总览 KPI (取代 hard-code MONTHLY_PAYROLL_OVERVIEW)
//   后端: PayController.overview() @ /pay/overview?month=YYYY-MM
//   返: { month, totalEmployees, totalGrossSalary, totalNetSalary, totalTax,
//        totalSocial, totalBonus, totalOvertime, totalBenefits, avgGross, avgNet, status }
// ═══════════════════════════════════════════════════════════════
export const payOverviewApi = {
  /** month: YYYY-MM, 默认当月 */
  get: (month?: string) => request.get<any>('/pay/overview', { params: month ? { month } : {} }),
}

// ═══════════════════════════════════════════════════════════════
// Stage 4 P0 真化: 月度员工薪资单 (取代 hard-code PAYROLLS 20 员工)
//   后端: PayController.payrolls() @ /pay/payrolls?month=YYYY-MM
//   返: List<{id, empNo, name, dept, pos, level, center, month, empType, joinDate, status,
//             base, position, ..., gross, tax, net, lastAdjustDate, releaseStatus}>
//   空数据返 []. 前端展示真实空态，不使用 SEED 兜底。
// ═══════════════════════════════════════════════════════════════
export const payPayrollsApi = {
  /** month: YYYY-MM, 默认当月 */
  list: (month?: string) => request.get<any[]>('/pay/payrolls', { params: month ? { month } : {} }),
}

export const empOnboardChecklistApi = {
  listByOnboard: (onboardId: number) =>
    request.get<any[]>(`/emp-onboard-checklist/by-onboard/${onboardId}`),
  applyTemplate: (onboardId: number, templateCode: string) =>
    request.post<any[]>(`/emp-onboard-checklist/apply/${onboardId}`, { templateCode }),
  setCompleted: (itemId: number, completed: boolean) =>
    request.put<any>(`/emp-onboard-checklist/${itemId}/completed`, { completed }),
}

// ═══════════════════════════════════════════════════════════════
// Stage 7 payCostApi 在文件底部定义 (跟后端 @RequestParam("months") 对齐)
// 这里删除 agent 早期的旧版本 (signature startMonth/endMonth 跟后端不一致)
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// V70 职级宽带 (Wide-band Pay) — 企业自配 P0
//   后端: SalaryGradeBandController @ /salary-grade-band
//   之前: frontend/src/views/pay/meta.ts:PAY_BANDS hard-code
// ═══════════════════════════════════════════════════════════════
export const salaryGradeBandApi = {
  list: () => request.get<any[]>('/salary-grade-band'),
  get: (id: number) => request.get<any>(`/salary-grade-band/${id}`),
  create: (data: any) => request.post<any>('/salary-grade-band', data),
  update: (id: number, data: any) => request.put<any>(`/salary-grade-band/${id}`, data),
  remove: (id: number) => request.delete(`/salary-grade-band/${id}`),
  toggle: (id: number) => request.post<any>(`/salary-grade-band/${id}/toggle`),
}

export const notifyApi = {
  categories: {
    list: (enabledOnly = false) => request.get<any[]>('/notify/categories', { params: { enabledOnly } }),
    create: (data: any) => request.post('/notify/categories', data),
    update: (id: number, data: any) => request.put(`/notify/categories/${id}`, data),
    remove: (id: number) => request.delete(`/notify/categories/${id}`),
  },
  publishRecords: (params: any = {}) => request.get<any[]>('/notify/publish-records', { params }),
  revokePublish: (id: number) => request.post<{ recordId: number; revoked: number }>(`/notify/publish-records/${id}/revoke`),
  // 去 mock 批3(lrn): HR 批量给一组员工发即时提醒 (真写 notify_message, 员工通知中心可见)。
  //   越 org / 不存在的 empNo 计入 skipped。返回 { sent, skipped, total }。
  adhoc: (empNos: string[], message: string, title?: string, category?: string) =>
    request.post<{ sent: number; skipped: number; total: number }>('/notify/adhoc',
      { empNos, message, ...(title ? { title } : {}), ...(category ? { category } : {}) }),
  // ─── HR 模板管理 ───
  templates: {
    list: () => request.get<any[]>('/notify/templates'),
    page: (params: { current?: number; size?: number; keyword?: string; category?: string; enabled?: number }) =>
      request.get('/notify/templates/page', { params }),
    create: (data: any) => request.post('/notify/templates', data),
    update: (id: number, data: any) => request.put(`/notify/templates/${id}`, data),
    remove: (id: number) => request.delete(`/notify/templates/${id}`),
  },
  // ─── 员工自己的通知 ───
  me: {
    /** status: 0=UNREAD 1=READ 2=ARCHIVED, 不传查全 */
    list: (params: { status?: number; current?: number; size?: number }) =>
      request.get('/notify/me/list', { params }),
    unreadCount: (config?: RequestConfig) => request.get<{ count: number }>('/notify/me/unread-count', config),
    markRead: (id: number) => request.post(`/notify/me/${id}/read`),
    markAllRead: () => request.post<{ rows: number }>('/notify/me/read-all'),
    archive: (id: number) => request.post(`/notify/me/${id}/archive`),
  },
}

// ═══════════════════════════════════════════════════════════════
// Stage 5 P0: 员工调薪记录 API (V71 真化)
//   后端: EmpSalaryAdjustmentController @ /pay/adjustments
//   之前: frontend/src/views/pay/meta.ts:SALARY_ADJUSTMENTS hard-code 5 条
//   状态机: DRAFT → APPROVED/REJECTED, 终态保护
// ═══════════════════════════════════════════════════════════════
export const salaryAdjustmentApi = {
  list: () => request.get<any[]>('/pay/adjustments'),
  page: (params: { current?: number; size?: number; status?: string; empNo?: string }) =>
    request.get<any>('/pay/adjustments/page', { params }),
  listByEmp: (empId: number) => request.get<any[]>(`/pay/adjustments/emp/${empId}`),
  /** V116: 按工号查所有调薪历史 (档案时间线) */
  listByEmpNo: (empNo: string) => request.get<any[]>(`/pay/adjustments/by-emp/${empNo}`),
  get: (id: number) => request.get<any>(`/pay/adjustments/${id}`),
  create: (data: any) => request.post<any>('/pay/adjustments', data),
  update: (id: number, data: any) => request.put<any>(`/pay/adjustments/${id}`, data),
  /** V240 (2026-07-05 P0-2): 显式 DRAFT → PENDING 提交, 走双眼原则前置. HR 建完调薪必须先 submit 才能被人审批. */
  submit: (id: number) => request.post<any>(`/pay/adjustments/${id}/submit`),
  approve: (id: number, body: { approverId?: number; approverName?: string }) =>
    request.post<any>(`/pay/adjustments/${id}/approve`, body || {}),
  reject: (id: number, body: { reason: string; approverId?: number; approverName?: string }) =>
    request.post<any>(`/pay/adjustments/${id}/reject`, body),
  remove: (id: number) => request.delete(`/pay/adjustments/${id}`),
  // ─── V116 调薪闭环 ───────────────────────────────────────
  /** V116: 节点审批 MGR/HRBP/CFO */
  approveNode: (id: number, role: 'MGR'|'HRBP'|'CFO', body?: { approverName?: string }) =>
    request.post<any>(`/pay/adjustments/${id}/approve-node?role=${role}`, body || {}),
  /** V116: 驳回 */
  rejectV116: (id: number, body: { reason: string; approverName?: string }) =>
    request.post<any>(`/pay/adjustments/${id}/reject-v116`, body),
  /** V116: 生效 */
  effect: (id: number) => request.post<any>(`/pay/adjustments/${id}/effect`),
  /** V116: 整批审批 */
  approveBatchAll: (batchId: number, role: 'MGR'|'HRBP'|'CFO', body?: { approverName?: string }) =>
    request.post<any>(`/pay/adjustments/batch/${batchId}/approve-all?role=${role}`, body || {}),
  /** V116: 整批生效 */
  effectBatchAll: (batchId: number) =>
    request.post<any>(`/pay/adjustments/batch/${batchId}/effect-all`),
  /** V116: 按批次查记录 */
  listByBatch: (batchId: number) =>
    request.get<any[]>(`/pay/adjustments/batch/${batchId}`),
}

// ═══════════════════════════════════════════════════════════════
// V116: 调薪批次 API
// 后端: SalaryAdjustBatchController @ /pay/adjust-batches
// ═══════════════════════════════════════════════════════════════
export const salaryAdjustBatchApi = {
  list: () => request.get<any[]>('/pay/adjust-batches'),
  page: (params: { current?: number; size?: number; status?: string }) =>
    request.get<any>('/pay/adjust-batches/page', { params }),
  get: (id: number) => request.get<any>(`/pay/adjust-batches/${id}`),
  /** 预览影响 (不入库, 返回 List<{empNo, name, oldBase, newBase, delta, changePct}>) */
  preview: (req: {
    ruleType: 'PERCENT'|'AMOUNT'|'BY_LEVEL'|'BY_PERFORMANCE'|'CUSTOM',
    ruleConfig: any,
    scopeEmpIds: number[],
  }) => request.post<any[]>('/pay/adjust-batches/preview', req),
  /** 创建批次 (1 + N) — V118: 加 CUSTOM 规则 + empAdjustments 个人覆盖 */
  initiate: (req: {
    name: string,
    adjustType: 'ANNUAL'|'PROMOTION'|'PERFORMANCE'|'SPECIAL',
    ruleType: 'PERCENT'|'AMOUNT'|'BY_LEVEL'|'BY_PERFORMANCE'|'CUSTOM',
    ruleConfig: any,
    scopeType?: 'ALL'|'ORG'|'POSITION'|'CUSTOM',
    scopeEmpIds?: number[],
    /** V118: 每人具体数值 — CUSTOM 必填, 其他规则可覆盖按规则算的结果 */
    empAdjustments?: Array<{
      empNo: string,
      newSalary: number,
      perPersonReason?: string,
      perPersonNote?: string,
    }>,
    /** 换薪资包时必须同时提供；由后端严格校验并随调薪记录保存，审批生效时重建 binding */
    afterTemplateId?: number,
    afterItems?: Array<{ code: string; name?: string; amount: number }>,
    effectiveDate: string,
    reason?: string,
  }) => request.post<any>('/pay/adjust-batches/initiate', req),
  cancel: (id: number) => request.post<any>(`/pay/adjust-batches/${id}/cancel`),
  /** V118: Excel 导入 (multipart, 返回 {validRows, failedRows, errors, items}) */
  importExcel: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post<{
      totalRows: number,
      validRows: number,
      failedRows: number,
      errors: Array<{row: number, error: string}>,
      items: Array<any>,
    }>('/pay/adjust-batches/import-excel', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  /** V118: 下载 Excel 模板 (浏览器直跳, 后端 attachment 头触发下载) */
  downloadTemplateUrl: () => '/api/pay/adjust-batches/import-template',
}

// ═══════════════════════════════════════════════════════════════
// Stage 6 P0: 员工奖金记录 API (V72 真化)
//   后端: EmpSalaryBonusController @ /pay/bonuses
//   之前: frontend/src/views/pay/meta.ts:BONUSES hard-code 5 条
//   状态机: DRAFT → APPROVED/REJECTED, APPROVED → 标已发放
// ═══════════════════════════════════════════════════════════════
export const salaryBonusApi = {
  list: () => request.get<any[]>('/pay/bonuses'),
  page: (params: { current?: number; size?: number; status?: string; empNo?: string; paid?: number }) =>
    request.get<any>('/pay/bonuses/page', { params }),
  listByEmp: (empId: number) => request.get<any[]>(`/pay/bonuses/emp/${empId}`),
  get: (id: number) => request.get<any>(`/pay/bonuses/${id}`),
  create: (data: any) => request.post<any>('/pay/bonuses', data),
  update: (id: number, data: any) => request.put<any>(`/pay/bonuses/${id}`, data),
  approve: (id: number, body: { approverId?: number; approverName?: string }) =>
    request.post<any>(`/pay/bonuses/${id}/approve`, body || {}),
  reject: (id: number, body: { reason: string; approverId?: number; approverName?: string }) =>
    request.post<any>(`/pay/bonuses/${id}/reject`, body),
  markPaid: (id: number) => request.post<any>(`/pay/bonuses/${id}/pay`),
  remove: (id: number) => request.delete(`/pay/bonuses/${id}`),
}

// ═══════════════════════════════════════════════════════════════
// XCCS001: 薪资档案"待调整区" API
//   后端: SalaryPendingChangeController @ /pay/pending-change
//   转正 (REGULARIZATION) / 调动 (TRANSFER) 完成 → 落 PENDING,
//   薪酬管理员确认后走 emp_salary_profile 版本链生效.
//   状态机: PENDING → CONFIRMED (confirm) | DISCARDED (discard, 不变薪)
//   权限: 读 pay:read / 确认+放弃 pay:update
// ═══════════════════════════════════════════════════════════════
export const salaryPendingApi = {
  /** 分页列表 (status: PENDING/CONFIRMED/DISCARDED 不传=全部, empKw: 姓名/工号关键字) */
  list: (params: { status?: string; empKw?: string; page?: number; size?: number }) =>
    request.get<any>('/pay/pending-change/list', { params }),
  get: (id: number) => request.get<any>(`/pay/pending-change/${id}`),
  /** 确认调薪 → 版本链生效. body 对齐后端 PendingConfirmReq: {effDate: 'yyyy-MM-dd', totalSalary} */
  confirm: (id: number, data: { effDate: string; totalSalary: number }) =>
    request.post<any>(`/pay/pending-change/${id}/confirm`, data),
  /** 放弃 (不变薪). 后端 body {reason?: string} 可选 */
  discard: (id: number, reason?: string) =>
    request.post<any>(`/pay/pending-change/${id}/discard`, reason ? { reason } : {}),
}

// ═══════════════════════════════════════════════════════════════
// XCCS001: 全部薪资档案 API (薪资档案"全部档案" tab)
//   后端: SalaryProfileQueryController @ /pay/salary-profiles
//   emp_salary_profile 全版本平铺 (version/currentFlag/effDate/totalSalary/remark
//   + enrich empName/empNo), @DataScope + totalSalary @SensitiveField 脱敏
// ═══════════════════════════════════════════════════════════════
export const salaryProfileApi = {
  /** 全版本平铺分页 (empKw 按姓名/工号过滤) */
  listAll: (params: { empKw?: string; page?: number; size?: number }) =>
    request.get<any>('/pay/salary-profiles', { params }),
  /** 批量查询当前生效档案；未建档员工不返回，调用方不得补默认薪资 */
  current: (empIds: number[]) =>
    request.get<any[]>('/pay/salary-profiles/current', {
      params: { empIds: empIds.join(',') },
      silent: true,
    }),
}

export const payEmployeeRosterApi = {
  /** 通用批量选人名册：仅非敏感员工字段，后端按当前角色数据范围过滤。 */
  page: (params: { keyword?: string; page?: number; size?: number }) =>
    request.get<any>('/pay/employee-roster', { params }),
}

// ═══════════════════════════════════════════════════════════════
// Stage 7 P0: 市场薪酬对标 API (V73 真化)
//   后端: MarketBenchmarkController @ /pay/benchmarks
//   之前: frontend/src/views/pay/meta.ts:MARKET_BENCHMARK hard-code 8 条
// ═══════════════════════════════════════════════════════════════
export const marketBenchmarkApi = {
  list: () => request.get<any[]>('/pay/benchmarks'),
  get: (id: number) => request.get<any>(`/pay/benchmarks/${id}`),
  create: (data: any) => request.post<any>('/pay/benchmarks', data),
  update: (id: number, data: any) => request.put<any>(`/pay/benchmarks/${id}`, data),
  remove: (id: number) => request.delete(`/pay/benchmarks/${id}`),
}

// ═══════════════════════════════════════════════════════════════
// Stage 7 P0: 薪酬聚合 API (替代 MONTHLY_COST_TREND + CENTER_COST_DIST hard-code)
//   后端: PayController.costTrend / costDist
// ═══════════════════════════════════════════════════════════════
export const payCostApi = {
  /** 月度成本趋势 (过去 N 个月, 默认 12) */
  trend: (months?: number) =>
    request.get<any[]>('/pay/cost-trend', { params: months ? { months } : {} }),
  /** 成本中心分布 (按指定月, 默认当月) */
  dist: (month?: string) =>
    request.get<any[]>('/pay/cost-dist', { params: month ? { month } : {} }),
}

// ═══════════════════════════════════════════════════════════════
// PAY-BAND-GOVERNANCE T3 (V78): 职位族 API
//   后端: JobFamilyController @ /md/job-families
//   权限: 查公开 / 改 family:edit (COMP_DIRECTOR + GROUP_HR + SUPER)
//   8 族 seed: TECH/PRODUCT/SALES/HR/FIN/OPS/GA/MFG
// ═══════════════════════════════════════════════════════════════
export const jobFamilyApi = {
  list: () => request.get<any[]>('/md/job-families'),
  /** 只启用 (下拉用) */
  listEnabled: () => request.get<any[]>('/md/job-families/enabled'),
  get: (id: number) => request.get<any>(`/md/job-families/${id}`),
  getByCode: (code: string) => request.get<any>('/md/job-families/by-code', { params: { code } }),
  create: (data: any) => request.post<any>('/md/job-families', data),
  update: (id: number, data: any) => request.put<any>(`/md/job-families/${id}`, data),
  toggle: (id: number) => request.post<any>(`/md/job-families/${id}/toggle`),
  remove: (id: number) => request.delete(`/md/job-families/${id}`),
  /** B 专项 (2026-07-15): Excel 导入. 返 {ok, failed, errors[]} */
  importExcel: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post<any>('/md/job-families/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  /** 导出全部职族 Excel (blob; 认证走 header 故不能用 a.href 直下) */
  exportExcel: () => request.get<Blob>('/md/job-families/export', { responseType: 'blob' }),
  /** 下载导入模板 (空表 + 1 行示例, blob) */
  templateExcel: () => request.get<Blob>('/md/job-families/template', { responseType: 'blob' }),
}

// ═══════════════════════════════════════════════════════════════
// PAY-BAND-GOVERNANCE T3 Phase 2: 职位 + 职级档位薪资带 API
//   后端: PositionController @ /md/positions
//        PositionTierBandController @ /md/tier-bands
//   权限: 查公开 / 改 position:edit + pay:band:edit (COMP_DIRECTOR + GROUP_HR)
// ═══════════════════════════════════════════════════════════════
export const positionApi = {
  /** status: 1=启用/0=停用/2=冻结 (不传=全部); orgId: 只返该组织下岗位, 精确匹配不含子部门 (入职弹窗按部门查启用岗位: {status:1, orgId}) */
  list: (params: { status?: number; orgId?: number } = {}) =>
    request.get<any[]>('/md/positions', { params }),
  page: (params: { current?: number; size?: number; sequence?: string;
                   level?: string; jobFamilyId?: number; status?: number; orgId?: number }) =>
    request.get<any>('/md/positions/page', { params }),
  /** 按职位族查 (员工档案/招聘需求下拉用) */
  listByFamily: (jobFamilyId: number) =>
    request.get<any[]>(`/md/positions/by-family/${jobFamilyId}`),
  get: (id: number) => request.get<any>(`/md/positions/${id}`),
  getByCode: (code: string) => request.get<any>('/md/positions/by-code', { params: { code } }),
  create: (data: any) => request.post<any>('/md/positions', data),
  update: (id: number, data: any) => request.put<any>(`/md/positions/${id}`, data),
  toggle: (id: number) => request.post<any>(`/md/positions/${id}/toggle`),
  setStatus: (id: number, status: number) => request.put<any>(`/md/positions/${id}/status`, null, { params: { status } }),
  remove: (id: number) => request.delete(`/md/positions/${id}`),
  /** UAT-FEEDBACK #5/#19: Excel 导入. 返 {ok, failed, errors[]} */
  importExcel: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post<any>('/md/positions/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  /** 导出全部职位 Excel (浏览器直接下载) */
  exportAllUrl: () => '/api/md/positions/export',
  /** 下载导入模板 (空表 + 1 行示例) */
  templateUrl: () => '/api/md/positions/template',
  /** B 专项 (2026-07-15): 导出全部职位 Excel (blob; 认证走 header 故不能用 a.href 直下) */
  exportExcel: () => request.get<Blob>('/md/positions/export', { responseType: 'blob' }),
  /** B 专项 (2026-07-15): 下载导入模板 (blob) */
  templateExcel: () => request.get<Blob>('/md/positions/template', { responseType: 'blob' }),
}

// V310: 编制申请真数据 + TPL-HC-APPLY 统一审批
export interface HeadcountApplicationRow {
  id: number
  appNo: string
  orgId: number
  orgName?: string
  positionId?: number | null
  positionName?: string
  targetPositionId?: number | null
  targetPositionName?: string | null
  changeType: 'ADD' | 'REDUCE' | 'TRANSFER' | 'FREEZE' | 'RELEASE'
  headcountYear?: number | null
  currentCount: number
  deltaCount: number
  targetCount: number
  targetCurrentCount?: number | null
  targetAfterCount?: number | null
  reason: string
  effectiveDate?: string
  urgency?: 'LOW' | 'MID' | 'HIGH'
  budgetSrc?: 'ANNUAL' | 'EXTRA'
  costPerHead?: number | null
  impactsJson?: string | null
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'APPLY_FAILED' | 'REJECTED' | 'CANCELLED' | string
  initiatorEmpId: number
  initiatorName?: string
  procInstanceId?: number | null
  procBizNo?: string | null
  procStatus?: string | null
  procCurrentNodeCode?: string | null
  appliedAt?: string | null
  applyError?: string | null
  createdAt?: string
}

export const headcountApplicationApi = {
  page: (params: {
    current?: number
    size?: number
    status?: string
    orgId?: number
    changeType?: string
    keyword?: string
  } = {}) => request.get<{ records: HeadcountApplicationRow[]; total: number; current: number; size: number }>(
    '/org/headcount-apps', { params, silent: true }
  ),
  stats: () => request.get<{ total: number; pending: number; approved: number; rejected: number; cancelled: number; applyFailed: number }>(
    '/org/headcount-apps/stats', { silent: true }
  ),
  get: (id: number) => request.get<HeadcountApplicationRow>(`/org/headcount-apps/${id}`),
  getByAppNo: (appNo: string) => request.get<HeadcountApplicationRow>(
    `/org/headcount-apps/by-app-no/${encodeURIComponent(appNo)}`
  ),
  createAndSubmit: (data: {
    orgId: number
    positionId?: number
    targetPositionId?: number
    changeType: 'ADD' | 'REDUCE' | 'TRANSFER' | 'FREEZE' | 'RELEASE'
    headcountYear?: number
    deltaCount: number
    reason: string
    effectiveDate: string
    urgency: 'LOW' | 'MID' | 'HIGH'
    budgetSrc: 'ANNUAL' | 'EXTRA'
    costPerHead?: number
    impacts?: string[]
  }) => request.post<HeadcountApplicationRow>('/org/headcount-apps/create-submit', data),
  cancel: (id: number) => request.post<HeadcountApplicationRow>(`/org/headcount-apps/${id}/cancel`),
  retryApply: (id: number) => request.post<HeadcountApplicationRow>(`/org/headcount-apps/${id}/retry-apply`),
}

export const tierBandApi = {
  list: () => request.get<any[]>('/md/tier-bands'),
  /** 按序列 M/P/S/O 查 */
  listBySequence: (sequence: string) =>
    request.get<any[]>(`/md/tier-bands/by-sequence/${sequence}`),
  get: (id: number) => request.get<any>(`/md/tier-bands/${id}`),
  /** ⭐ 算薪桥接入口: 按 level+tier 查带宽 (员工档案/算薪都用) */
  getByLevelTier: (level: string, tier: number) =>
    request.get<any>('/md/tier-bands/by-level-tier', { params: { level, tier } }),
  create: (data: any) => request.post<any>('/md/tier-bands', data),
  update: (id: number, data: any) => request.put<any>(`/md/tier-bands/${id}`, data),
  approve: (id: number, body: { approverEmpId?: number }) =>
    request.post<any>(`/md/tier-bands/${id}/approve`, body || {}),
  remove: (id: number) => request.delete(`/md/tier-bands/${id}`),
  /** V80 Phase 5: 带宽历史快照 (审计/复盘) */
  history: (id: number) => request.get<any[]>(`/md/tier-bands/${id}/history`),
}

// ═══════════════════════════════════════════════════════════════
// ⭐ V83 简历存档 + 解析后预览
//   后端: ResumeFileController @ /rec-resume-file
//   端点:
//     POST  /upload-and-parse              一步上传 + 解析 + 存档, 返 {...fields, _resumeFileId, _text, _filename}
//     POST  /{id}/bind?candidateId=        绑定到候选人 (创建完候选人后调)
//     GET   /{id}/download                 下载原件 (a 标签 href 直用)
//     GET   /{id}/preview                  预览 (PDF 二进制 inline, DOCX/TXT 返 text)
//     GET   /by-candidate/{candidateId}    列出某候选人的所有简历
// ═══════════════════════════════════════════════════════════════
export const resumeFileApi = {
  uploadAndParse: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post<any>('/rec-resume-file/upload-and-parse', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  bind: (id: number, candidateId: number) =>
    request.post<any>(`/rec-resume-file/${id}/bind`, null, { params: { candidateId } }),
  /** 直接给 a 标签 href / window.open (Authorization 走 cookie/无鉴权 — 浏览器会自动加 JWT 走 axios interceptor 拿不到, 需要 token 在 query) */
  downloadUrl: (id: number) => `/api/rec-resume-file/${id}/download`,
  previewUrl: (id: number) => `/api/rec-resume-file/${id}/preview`,
  listByCandidate: (candidateId: number) =>
    request.get<any[]>(`/rec-resume-file/by-candidate/${candidateId}`),
}

// 行14: 候选人入职资料实例 — 员工详情[招聘信息]页签用 emp→招聘来源反查
//   后端: CandOnboardDocController @ /cand-onboard-doc
//   byEmp: 由 empId 反查 { candidateId, docId, docStatus } (emp:read); candidateId 可空(无招聘来源)
export const candOnboardDocApi = {
  byEmp: (empId: number) =>
    request.get<{ candidateId: number | null; docId: number | null; docStatus: string | null }>(
      `/cand-onboard-doc/by-emp/${empId}`,
    ),
}

// ═══════════════════════════════════════════════════════════════
// V85 - AI Provider 真接 (简历解析 Claude / DeepSeek / OpenAI)
//   后端 AiProviderController @ /ai/provider, 单例配置.
//   API Key 走 SM4 加密入库, decryptKey 单独审计.
// ═══════════════════════════════════════════════════════════════
// AI 业务能力端点; ai_provider_config 配通时返真 LLM 结果, 否则 enabled=false + 空数据。
export const aiApi = {
  interviewSummary: (payload: any) => request.post<any>('/ai/interview/summary', payload),
  // JD 智能撰写 → { enabled, reason?, summary, responsibilities[], requirements[], niceToHave[], benefits[], keywords[] }
  jdGenerate: (payload: any) => request.post<any>('/ai/jd/generate', payload),
  // 简历质量评分 → { enabled, reason?, score, summary, strengths[], concerns[] }
  resumeScore: (payload: any) => request.post<any>('/ai/resume/score', payload),
}

// 通用文件上传 (对应后端 FileController): 白名单+10MB 上限, 返 {key,name,size,url}。
export const fileApi = {
  upload: (file: File, config?: RequestConfig) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post<{ key: string; name: string; size: number; url: string }>(
      '/file/upload', fd, { ...config, headers: { 'Content-Type': 'multipart/form-data', ...(config?.headers || {}) } },
    )
  },
}

// 员工附件 (V229): 上传流程 → 先 fileApi.upload 拿 url, 再 empAttachmentApi.create 存元数据
export const empAttachmentApi = {
  byEmp: (empId: number) => request.get<any[]>(`/emp-attachment/by-emp/${empId}`),
  create: (data: any) => request.post<any>('/emp-attachment', data),
  remove: (id: number) => request.delete(`/emp-attachment/${id}`),
}
// 附件类型枚举与后端 EmpAttachmentService.ALLOWED_TYPES 对齐
export const EMP_ATTACHMENT_TYPES: { code: string; label: string }[] = [
  { code: 'ID_CARD_FRONT',  label: '身份证正面' },
  { code: 'ID_CARD_BACK',   label: '身份证反面' },
  { code: 'EDUCATION',      label: '毕业证/学历证' },
  { code: 'HEALTH_CERT',    label: '健康证' },
  { code: 'MEDICAL_REPORT', label: '体检报告' },
  { code: 'CREDIT_REPORT',  label: '征信报告' },
  { code: 'NON_CRIMINAL',   label: '无犯罪记录证明' },
  { code: 'OTHER',          label: '其他' },
  { code: 'PROBATION_REVIEW', label: '转正述职材料' },
]

export const aiProviderApi = {
  /** 取当前配置 (API Key 只返 mask) */
  get: (config?: RequestConfig) => request.get<any>('/ai/provider', config),
  /**
   * 更新配置.
   * @param data { provider, plainApiKey?, baseUrl?, model?, temperature?, maxTokens?, enabled }
   *   plainApiKey 不传/空串 = 不改; "CLEAR" = 清空
   */
  update: (data: any) => request.put<any>('/ai/provider', data),
  /** 一次性解密 API Key 明文 (走 @AuditLog) */
  decryptKey: () => request.get<{ apiKey: string }>('/ai/provider/decrypt-key'),
  /** 用固定测试简历跑一次, 返 {ok, latencyMs, fields, parser} */
  test: () => request.post<any>('/ai/provider/test'),
}

// ═══════════════════════════════════════════════════════════════
// V86 候选人画像 360 (新模块, 后端 CandidateProfileController @ /rec-profile)
//   聚合 candidate + interviews + offers + resumes + contacts(audit) + statusHistory.
//   不动 V64/V83/V84/V85 schema, 仅 join 现有表 + audit_log.
// ═══════════════════════════════════════════════════════════════
export const candidate360Api = {
  /** 拉聚合数据. 返 { candidate, kpis, interviews, offers, contacts, resumes, statusHistory } */
  get: (id: number) => request.get<any>(`/rec-profile/${id}`),
}

// ═══════════════════════════════════════════════════════════════
// V92 候选人画像 — 简历独立 5 表 (B 阶段)
//   后端 CandidateProfileController 在原 /rec-profile/{cid} 基础上加 6 个查询端点.
//   读权限 'rec' (与 360 一致), 写在 Service 替换式, 暂未暴露独立写 API.
// ═══════════════════════════════════════════════════════════════
export const candidateProfileApi = {
  educations: (cid: number) => request.get<any[]>(`/rec-profile/${cid}/education`),
  works: (cid: number) => request.get<any[]>(`/rec-profile/${cid}/work`),
  projects: (cid: number) => request.get<any[]>(`/rec-profile/${cid}/projects`),
  skills: (cid: number) => request.get<any[]>(`/rec-profile/${cid}/skills`),
  certs: (cid: number) => request.get<any[]>(`/rec-profile/${cid}/certificates`),
  languages: (cid: number) => request.get<any[]>(`/rec-profile/${cid}/languages`),
  links: (cid: number) => request.get<any[]>(`/rec-profile/${cid}/links`),
}

// ═══════════════════════════════════════════════════════════════
// V87 OCR Provider 配置 (手写/扫描简历照片识别)
//   后端 OcrProviderController @ /ocr/provider, 权限 ai:edit (复用 V85).
//   支持 BAIDU / TENCENT / ALI / MOCK. API Key + Secret 走 SM4 加密入库.
// ═══════════════════════════════════════════════════════════════
export const ocrProviderApi = {
  /** 取当前配置 (api_key / secret_key 只返 mask) */
  get: () => request.get<any>('/ocr/provider'),
  /**
   * 更新配置.
   * @param data { provider, plainApiKey?, plainSecretKey?, apiUrl?, enabled }
   *   plainApiKey / plainSecretKey 不传/空串 = 不改; "CLEAR" = 清空
   */
  update: (data: any) => request.put<any>('/ocr/provider', data),
  /** 一次性解密 API Key 明文 (走 @AuditLog) */
  decryptApiKey: () => request.get<{ apiKey: string }>('/ocr/provider/decrypt-api-key'),
  /** 一次性解密 Secret Key 明文 (走 @AuditLog) */
  decryptSecretKey: () => request.get<{ secretKey: string }>('/ocr/provider/decrypt-secret-key'),
  /** 用 1x1 测试图调一次, 返 {ok, latencyMs, textPreview, textLength, service} */
  test: () => request.post<any>('/ocr/provider/test'),
}

// ═══════════════════════════════════════════════════════════════
// V86 - 企业微信群机器人 (HR 状态变化推群)
//   后端 WechatBotController @ /wechat-bot, webhook_url SM4 加密.
//   候选人 Offer / 入职 / 调岗 / 试用期到期 4 类事件自动推 HR 群.
// ═══════════════════════════════════════════════════════════════
export interface WechatBotView {
  id: number
  name: string
  webhookMask: string
  eventFilter: string[]
  enabled: 0 | 1
  createdBy?: string
  createdAt?: string
  updatedAt?: string
}
export interface WechatMessageLogView {
  id: number
  botId: number
  botName?: string
  eventType: string
  payload?: string
  content?: string
  status: 'SENT' | 'FAILED'
  errorMsg?: string
  sentAt: string
}
export const wechatBotApi = {
  /** 列表 (不返 webhook 明文) */
  list: () => request.get<WechatBotView[]>('/wechat-bot'),
  /** 详情 */
  get: (id: number) => request.get<WechatBotView>(`/wechat-bot/${id}`),
  /** 创建 */
  create: (data: { name: string; webhookUrl: string; eventFilter: string[]; enabled?: 0 | 1 }) =>
    request.post<WechatBotView>('/wechat-bot', data),
  /** 更新 (webhookUrl 留空 = 不改) */
  update: (id: number, data: { name?: string; webhookUrl?: string; eventFilter?: string[]; enabled?: 0 | 1 }) =>
    request.put<WechatBotView>(`/wechat-bot/${id}`, data),
  /** 删除 */
  remove: (id: number) => request.delete<void>(`/wechat-bot/${id}`),
  /** 测试发送 — 给该 bot 发条 TEST 消息 */
  test: (id: number) => request.post<{ logId: number; botId: number; botName: string }>(`/wechat-bot/${id}/test`),
  /** 发送历史 — 默认近 100 条 */
  logs: (limit = 100) => request.get<WechatMessageLogView[]>('/wechat-bot/logs', { params: { limit } }),
}

// ═══════════════════════════════════════════════════════════════
// V259 - 企微自建应用对话网关 · 白名单管理
//   后端 WecomBotUserController @ /wecom-bot-user (权限复用 wechat-bot:manage).
//   白名单成员才能跟 HRMS 企微助手对话; rbacUserId 绑定系统账号 (二期 AI 按其权限查数).
// ═══════════════════════════════════════════════════════════════
export interface WecomBotUserView {
  id: number
  wecomUserid: string
  name?: string
  rbacUserId: number
  rbacUsername?: string
  rbacNickname?: string
  enabled: 0 | 1
  remark?: string
  createdAt?: string
  updatedAt?: string
}
export interface WecomRbacUserOption {
  id: number
  username: string
  name?: string
}
export const wecomBotUserApi = {
  /** 列表 (联查 rbac_user 账号名) */
  list: () => request.get<WecomBotUserView[]>('/wecom-bot-user'),
  /** 创建 */
  create: (data: { wecomUserid: string; name?: string; rbacUserId: number; enabled?: 0 | 1; remark?: string }) =>
    request.post<WecomBotUserView>('/wecom-bot-user', data),
  /** 更新 (wecomUserid 留空 = 不改) */
  update: (id: number, data: { wecomUserid?: string; name?: string; rbacUserId?: number; enabled?: 0 | 1; remark?: string }) =>
    request.put<WecomBotUserView>(`/wecom-bot-user/${id}`, data),
  /** 删除 (物理删) */
  remove: (id: number) => request.delete<void>(`/wecom-bot-user/${id}`),
  /** 系统账号下拉搜索 (限 20 条) */
  rbacUserOptions: (keyword?: string) =>
    request.get<WecomRbacUserOption[]>('/wecom-bot-user/rbac-user-options', { params: { keyword } }),
}

// ═══════════════════════════════════════════════════════════════
// V268 - 企微自建应用接入配置 (5 密钥界面化 + 加密入库 + 热生效)
//   后端 WecomConfigController @ /wecom-config, 权限复用 wechat-bot:manage.
//   corpId/agentId/token 明文; secret/aesKey SM4 加密, GET 只返 mask.
//   兼容 .env: 库未配 (enabled=0 或缺项) 时后端回退读环境变量.
// ═══════════════════════════════════════════════════════════════
export const wecomConfigApi = {
  /** 取当前配置 (secret/aesKey 只返 mask; source: DB/ENV/NONE) */
  get: () => request.get<any>('/wecom-config'),
  /**
   * 更新配置.
   * @param data { corpId, agentId, token, plainSecret?, plainAesKey?, enabled }
   *   plainSecret / plainAesKey 不传/空串 = 不改; "CLEAR" = 清空
   */
  update: (data: any) => request.put<any>('/wecom-config', data),
  /**
   * 测试连接 — 用表单 corpId + secret 调企微 gettoken, 不落库.
   * secret 留空则用库里已存的. 返 {ok, msg?, errcode?, errmsg?, latencyMs}
   */
  test: (data: any) => request.post<any>('/wecom-config/test', data),
}

// ═══════════════════════════════════════════════════════════════
// V88 AI 面试官 Phase A (骨架: 题库 + Session + 文字问答 + AI 评分)
//   后端 AiInterviewController @ /ai-interview, 复用 V85 AI Provider 配置.
//   Phase A 文字问答 (HR 内部演示), Phase B 真语音 (Azure / 火山).
// ═══════════════════════════════════════════════════════════════
export const aiInterviewApi = {
  questionBank: {
    /** 题库列表, 可按 category(TECH/BEHAVIOR/LANGUAGE) + level(JUNIOR/MID/SENIOR) + enabled 过滤 */
    list: (params: any = {}) => request.get<any[]>('/ai-interview/question-bank', { params }),
    get: (id: number) => request.get<any>(`/ai-interview/question-bank/${id}`),
    create: (data: any) => request.post<any>('/ai-interview/question-bank', data),
    update: (id: number, data: any) => request.put<any>(`/ai-interview/question-bank/${id}`, data),
    remove: (id: number) => request.delete(`/ai-interview/question-bank/${id}`),
  },
  session: {
    /** session 列表 */
    list: () => request.get<any[]>('/ai-interview/session'),
    /** 单 session 详情 (含 qa 列表) */
    get: (sessionNo: string) => request.get<any>(`/ai-interview/session/${sessionNo}`),
    /** 创建 session, 返 {sessionNo, totalQuestions, ...} */
    create: (data: { candidateId?: number; candidateName?: string; jdTemplateId?: number; jdTitle?: string; questionCount?: number; category?: string }) =>
      request.post<any>('/ai-interview/session', data),
    /** PLANNED → RUNNING */
    start: (sessionNo: string) => request.post<any>(`/ai-interview/session/${sessionNo}/start`),
    /** 答题 + AI 评分 */
    answer: (sessionNo: string, data: { questionId: number; answer: string }) =>
      request.post<any>(`/ai-interview/session/${sessionNo}/answer`, data),
    /** 报告 (含 dimensionScores / overallComment / qa) */
    report: (sessionNo: string) => request.get<any>(`/ai-interview/session/${sessionNo}/report`),
  },
}

// ═══════════════════════════════════════════════════════════════
// V90 城市字典 (盲选脱敏 + 城市标准化)
// 后端 DictCityController @ /dict/city
// level: T1 一线 / T2 新一线 / T3 二线 / T4 三四线
// ═══════════════════════════════════════════════════════════════
export const dictCityApi = {
  list: () => request.get<any[]>('/dict/city'),
  search: (keyword?: string) =>
    request.get<any[]>('/dict/city/search', { params: keyword ? { keyword } : {} }),
  byCode: (code: string) => request.get<any>('/dict/city/by-code', { params: { code } }),
  byLevel: (level: string) => request.get<any[]>(`/dict/city/by-level/${level}`),
  create: (data: any) => request.post<any>('/dict/city', data),
  update: (id: number, data: any) => request.put<any>(`/dict/city/${id}`, data),
  toggle: (id: number) => request.post<any>(`/dict/city/${id}/toggle`),
  remove: (id: number) => request.delete(`/dict/city/${id}`),
}

// ═══════════════════════════════════════════════════════════════
// V91 学校字典 (985/211/双一流)
// 后端 DictSchoolController @ /dict/school
// tags: "985" / "211" / "DOUBLE_FIRST_CLASS" / "C9"
// ═══════════════════════════════════════════════════════════════
export const dictSchoolApi = {
  list: () => request.get<any[]>('/dict/school'),
  search: (keyword?: string) =>
    request.get<any[]>('/dict/school/search', { params: keyword ? { keyword } : {} }),
  byCode: (code: string) => request.get<any>('/dict/school/by-code', { params: { code } }),
  byName: (name: string) => request.get<any>('/dict/school/by-name', { params: { name } }),
  byTag: (tag: string) => request.get<any[]>(`/dict/school/by-tag/${tag}`),
  /** name → {matched, tags: "[...]", school} (候选人 chip 用) */
  inferTag: (name: string) =>
    request.get<any>('/dict/school/infer-tag', { params: { name } }),
  create: (data: any) => request.post<any>('/dict/school', data),
  update: (id: number, data: any) => request.put<any>(`/dict/school/${id}`, data),
  toggle: (id: number) => request.post<any>(`/dict/school/${id}/toggle`),
  remove: (id: number) => request.delete(`/dict/school/${id}`),
}

// ═══════════════════════════════════════════════════════════════
// V93 公司库字典 (公司名标准化)
// 后端 DictCompanyController @ /dict/company
// industry: BAT_INTERNET/FB_RESTAURANT/FB_BEVERAGE/RETAIL/MANUFACTURING/
//           FINANCE/REAL_ESTATE/EDUCATION/HEALTHCARE/LOGISTICS/TELECOM/
//           ENERGY/AUTO/MEDIA/TRAVEL/GAMING/EC_PLATFORM
// scale: TOP50/TOP100/TOP500/UNICORN/SOE/SME
// confidence: EXACT / ALIAS / FUZZY / NONE
// ═══════════════════════════════════════════════════════════════
export const dictCompanyApi = {
  list: () => request.get<any[]>('/dict/company'),
  search: (keyword?: string) =>
    request.get<any[]>('/dict/company/search', { params: keyword ? { keyword } : {} }),
  /** D2: 服务端分页 (current/size/keyword/industryCode/scale) → Page */
  page: (params: { current?: number; size?: number; keyword?: string; industryCode?: string; scale?: string }) =>
    request.get<any>('/dict/company/page', { params }),
  /** D2: 全表精确统计 → {total, byIndustry:{code:cnt}, byScale:{code:cnt}} */
  stats: () => request.get<any>('/dict/company/stats'),
  byName: (name: string) => request.get<any>('/dict/company/by-name', { params: { name } }),
  byIndustry: (code: string) => request.get<any[]>(`/dict/company/by-industry/${code}`),
  byScale: (scale: string) => request.get<any[]>(`/dict/company/by-scale/${scale}`),
  /** 推断 → {matched, confidence, standardName, company} */
  inferCompany: (name: string) =>
    request.get<any>('/dict/company/infer', { params: { name } }),
  aliases: (id: number) => request.get<any[]>(`/dict/company/${id}/aliases`),
  create: (data: any) => request.post<any>('/dict/company', data),
  update: (id: number, data: any) => request.put<any>(`/dict/company/${id}`, data),
  toggle: (id: number) => request.post<any>(`/dict/company/${id}/toggle`),
  remove: (id: number) => request.delete(`/dict/company/${id}`),
}

// ═══════════════════════════════════════════════════════════════
// V94 技能图谱字典 (技能标准化)
// 后端 DictSkillController @ /dict/skill
// category: LANG/FRAMEWORK/DB/CLOUD/BIGDATA/AI/TOOL/PM/SOFT/DOMAIN/CERT
// domain:   BACKEND/FRONTEND/MOBILE/DEVOPS/DATA/AI/PM/HR/SALES/
//           MARKETING/FINANCE/OPERATION/FB_RESTAURANT
// confidence: EXACT / ALIAS / FUZZY / NONE
// ═══════════════════════════════════════════════════════════════
export const dictSkillApi = {
  list: () => request.get<any[]>('/dict/skill'),
  search: (keyword?: string) =>
    request.get<any[]>('/dict/skill/search', { params: keyword ? { keyword } : {} }),
  /** D2: 服务端分页 (current/size/keyword/category/domain) → Page */
  page: (params: { current?: number; size?: number; keyword?: string; category?: string; domain?: string }) =>
    request.get<any>('/dict/skill/page', { params }),
  /** D2: 全表精确统计 → {total, byCategory:{code:cnt}, byDomain:{code:cnt}} */
  stats: () => request.get<any>('/dict/skill/stats'),
  byName: (name: string) => request.get<any>('/dict/skill/by-name', { params: { name } }),
  byCategory: (code: string) => request.get<any[]>(`/dict/skill/by-category/${code}`),
  byDomain: (code: string) => request.get<any[]>(`/dict/skill/by-domain/${code}`),
  /** 推断 → {matched, confidence, standardName, category, domain, skill} */
  inferSkill: (name: string) =>
    request.get<any>('/dict/skill/infer', { params: { name } }),
  aliases: (id: number) => request.get<any[]>(`/dict/skill/${id}/aliases`),
  create: (data: any) => request.post<any>('/dict/skill', data),
  update: (id: number, data: any) => request.put<any>(`/dict/skill/${id}`, data),
  toggle: (id: number) => request.post<any>(`/dict/skill/${id}/toggle`),
  remove: (id: number) => request.delete(`/dict/skill/${id}`),
}

// ═══════════════════════════════════════════════════════════════
// V93+V94 简历字段标准化 (合体调用 4 个字典)
// 后端 StandardizeController @ /standardize
// 用于 A (ResumeParserService 升级) / B (候选人 5 表写入前)
// ═══════════════════════════════════════════════════════════════
export const standardizeApi = {
  /** 整简历: parsed JSON → parsed + _standardized 字段 */
  resume: (parsed: any) => request.post<any>('/standardize/resume', parsed),
  city: (raw: string) => request.post<any>('/standardize/city', { raw }),
  company: (raw: string) => request.post<any>('/standardize/company', { raw }),
  skill: (raw: string) => request.post<any>('/standardize/skill', { raw }),
}

// ═══════════════════════════════════════════════════════════════
// V89 重复简历检测 + 多版本对比
//   后端 ResumeDedupController @ /rec-dedup
//   - check    : POST {mobile?, email?} → 返 DedupResult { duplicate, type, existingCandidates, message }
//   - versions : GET /by-candidate/{id}/versions → 返 RecResumeFile[] (version_no 倒序)
//   - diff     : POST {fileIdA, fileIdB} → 返 { added[], removed[], unifiedDiff, ... }
//
//   候选人创建命中查重时, 后端返 code=409 + msg. 前端拦截器虽弹一次 toast,
//   但 store.create 也接 409 弹"仍创建/取消" confirm, 用户确认走 force=true.
// ═══════════════════════════════════════════════════════════════
export interface DedupResult {
  duplicate: boolean
  type: 'NONE' | 'MOBILE' | 'EMAIL' | 'ID_NO' | 'FILE' | 'MULTIPLE' | string
  existingCandidates?: any[]
  existingFiles?: any[]
  message?: string
  // V156 (UAT-FB#65 P0.1): 保护期摘要
  inProtection?: boolean
  protectedBy?: string
  protectedUntil?: string
  protectionDaysRemaining?: number
  firstContactAt?: string
}
export const resumeDedupApi = {
  /** V156: 查重 — 三因子 mobile/email/idNo */
  check: (mobile?: string, email?: string, idNo?: string) =>
    request.post<DedupResult>('/rec-dedup/check', { mobile, email, idNo }, { silent: true } as any),
  /** 候选人简历版本列表 (version 倒序) */
  versions: (candidateId: number) =>
    request.get<any[]>(`/rec-dedup/by-candidate/${candidateId}/versions`),
  /** 两份简历 unified diff */
  diff: (fileIdA: number, fileIdB: number) =>
    request.post<any>('/rec-dedup/diff', { fileIdA, fileIdB }),
}

// ═══════════════════════════════════════════════════════════════
// V157 (UAT-FB#65 P0.2): 面试多人评分卡 — Greenhouse Scorecard 风格
//   后端 RecInterviewEvalController @ /rec-interview-eval
// ═══════════════════════════════════════════════════════════════
export interface InterviewEval {
  id?: number
  interviewId: number
  interviewerEmpId: number
  interviewerName: string
  score?: number
  competencyScores?: string  // JSON {"沟通":88,"技术":92,...}
  passAdvice?: 'PASS' | 'REJECT' | 'HOLD'
  highlights?: string
  concerns?: string
  comment?: string
  submittedAt?: string
}
export interface InterviewEvalAgg {
  interviewId: number
  evalCount: number
  submittedCount: number
  avg: number
  median: number
  min: number
  max: number
  divergence: number
  flagged: boolean
  passCount: number
  rejectCount: number
  holdCount: number
  recommendation: 'PASS' | 'REJECT' | 'HOLD' | 'PENDING'
  competencyAvg: Record<string, number>
}
export const interviewEvalApi = {
  list: (interviewId: number) =>
    request.get<InterviewEval[]>(`/rec-interview-eval/by-interview/${interviewId}`),
  aggregate: (interviewId: number) =>
    request.get<InterviewEvalAgg>(`/rec-interview-eval/by-interview/${interviewId}/agg`),
  upsert: (e: InterviewEval) =>
    request.post<InterviewEval>('/rec-interview-eval', e),
  submit: (id: number) =>
    request.post<InterviewEval>(`/rec-interview-eval/${id}/submit`, {}),
}

// ═══════════════════════════════════════════════════════════════
// V158 (UAT-FB#65 P0.3): 候选人通知中心 (邮件真发 + 短信 mock)
//   后端 NotifyOutboundController @ /notify-outbound
//   notify_template 8 个 REC 模板: REC_CAND_INVITED/INTERVIEW_SCHEDULED/...
// ═══════════════════════════════════════════════════════════════
export interface NotifyOutboundLog {
  id?: number
  templateCode: string
  channel: 'EMAIL' | 'SMS'
  recipient: string
  subject?: string
  body: string
  relatedEntity?: string
  relatedId?: number
  status: 'PENDING' | 'SENT' | 'FAILED'
  provider?: string
  providerMsgId?: string
  errorMsg?: string
  sentAt?: string
  createdAt?: string
}
export const notifyOutboundApi = {
  /** 按模板 code + vars 渲染并发送 (邮件/短信/双发) */
  send: (
    templateCode: string,
    email: string | undefined,
    mobile: string | undefined,
    vars: Record<string, any>,
    relatedEntity?: string,
    relatedId?: number,
  ) =>
    request.post<NotifyOutboundLog[]>(
      '/notify-outbound/send',
      { templateCode, email, mobile, vars, relatedEntity, relatedId },
      { silent: true } as any,
    ),
  /** 列出外发日志 */
  logs: (params?: { status?: string; relatedEntity?: string; relatedId?: number }) =>
    request.get<NotifyOutboundLog[]>('/notify-outbound/logs', { params }),
  /** 去mock批3 sys-P2: 通道本月真统计 (INAPP/EMAIL/SMS, settings 通知 tab) */
  stats: () =>
    request.get<Array<{ channel: string; enabled: boolean; provider: string; monthSent: number; monthFailed: number }>>(
      '/notify-outbound/stats',
    ),
  config: () => request.get<Record<string, any>>('/notify-outbound/config'),
  saveConfig: (channel: 'mail' | 'sms', value: Record<string, any>, version?: number) =>
    request.put<any>(`/notify-outbound/config/${channel}`, { value, version }),
}

// ═══════════════════════════════════════════════════════════════
// V159 (UAT-FB#65 P0.4): Offer 电子签 (Mock provider, 留 法大大/e签宝接口)
//   后端 OfferEsignController @ /offer-esign
// ═══════════════════════════════════════════════════════════════
export interface OfferEsign {
  id: number
  offerId: number
  offerNo: string
  candidateId: number
  candidateName: string
  provider: string
  flowId?: string
  signUrl?: string
  smsSentAt?: string
  status: 'CREATED' | 'SENT' | 'VIEWED' | 'SIGNED' | 'REJECTED' | 'EXPIRED' | 'REVOKED'
  viewedAt?: string
  signedAt?: string
  rejectedAt?: string
  rejectReason?: string
  expiresAt?: string
}
export const offerEsignApi = {
  /** HR 发起电子签流程, 返回签字 H5 链接 */
  create: (offerId: number) =>
    request.post<OfferEsign>(`/offer-esign/${offerId}/create`, {}),
  /** HR 发送签字短信 */
  sendSms: (esignId: number, candidateMobile: string, candidateName: string, position: string, hrPhone?: string) =>
    request.post<OfferEsign>(`/offer-esign/${esignId}/send-sms`, null, {
      params: { candidateMobile, candidateName, position, hrPhone: hrPhone || '' },
    } as any),
  byOffer: (offerId: number) =>
    request.get<OfferEsign>(`/offer-esign/by-offer/${offerId}`),
  /** 候选人 H5 端 mock 签字 (上线后由 provider webhook 触发) */
  mockSign: (flowId: string) =>
    request.post<OfferEsign>(`/offer-esign/by-flow/${flowId}/public/sign`, {}),
}

// ═══════════════════════════════════════════════════════════════
// V165: 候选人扫码投递链接 (cand-apply) — HR 建链接, 候选人 H5 扫码投递真建档
//   后端 CandApplyController @ /cand-apply (createLink → rec:create)
// ═══════════════════════════════════════════════════════════════
export interface CandApplyLinkResp {
  link: any
  linkCode: string
  h5Path: string      // 推荐 H5 路径模板, 如 /h5/apply/{code}
  infoApi: string
  submitApi: string
}
export const candApplyApi = {
  /** HR 建投递链接 (demandId 或 jdId 二选一). 返回 linkCode + H5 路径 */
  createLink: (data: { demandId?: number; jdId?: number; expiresDays?: number }) =>
    request.post<CandApplyLinkResp>('/cand-apply/links', data),
  /** 列出投递链接 (按 demand/jd/启停 过滤) */
  listLinks: (params?: { demandId?: number; jdId?: number; enabled?: number }) =>
    request.get<any[]>('/cand-apply/links', { params }),
  /** 启停链接 */
  setEnabled: (id: number, value: number) =>
    request.put<any>(`/cand-apply/links/${id}/enabled`, null, { params: { value } } as any),
  /** 候选人 H5: 读取公开岗位摘要 (无需登录) */
  info: (code: string) =>
    request.get<any>(`/cand-apply/link/${encodeURIComponent(code)}/info`, { anonymous: true, silent: true }),
  /** 候选人 H5: 提交申请 (无需登录, multipart) */
  submit: (code: string, form: FormData) =>
    request.post<any>(`/cand-apply/link/${encodeURIComponent(code)}/submit`, form, {
      anonymous: true,
      silent: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  /** 候选人 H5: 简历预解析 (仅返回字段, 不落候选人档案) */
  parseResume: (code: string, resume: File) => {
    const fd = new FormData()
    fd.append('resume', resume)
    return request.post<any>(`/cand-apply/link/${encodeURIComponent(code)}/parse-resume`, fd, {
      anonymous: true,
      silent: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  getDraft: (code: string, token: string, mobile?: string) =>
    request.get<any>(`/cand-apply/link/${encodeURIComponent(code)}/draft`, { params: { token, mobile }, anonymous: true, silent: true }),
  saveDraft: (code: string, payload: Record<string, unknown>, draftToken?: string) =>
    request.post<any>(`/cand-apply/link/${encodeURIComponent(code)}/draft`, { draftToken, payload }, { anonymous: true, silent: true }),
  deleteDraft: (code: string, draftToken: string) =>
    request.delete<any>(`/cand-apply/link/${encodeURIComponent(code)}/draft`, { params: { token: draftToken }, anonymous: true, silent: true }),
}

// ═══════════════════════════════════════════════════════════════
// V158+ (UAT-FB#65 P0.5): 招聘分析 6 核心报表
//   后端 RecAnalyticsController @ /rec-analytics
// ═══════════════════════════════════════════════════════════════
export interface FunnelStage {
  stage: string; label: string; count: number
  conversionFromPrev: number; conversionFromTop: number
}
export interface SlaBucket { stage: string; total: number; overdue: number; overduePct: number }
export interface ChannelRoi {
  channel: string; totalCandidates: number; interviewedCount: number; offerCount: number; hiredCount: number
  interviewRate: number; offerRate: number; hireRate: number
}
export interface HrbpRank { hrbp: string; totalCandidates: number; hiredCount: number; avgCycleDays: number }
export interface DeptProgress {
  demandNo: string; position: string; dept: string; target: number; hired: number; pct: number; status: string
}
// V-de-mock (2026-07-14): 招聘周期趋势 — 近 6 月入职者 created→hired 平均天数 + 目标线 + 入职数
export interface CycleTrend { month: string; avgDays: number; target: number; hires: number }
export const recAnalyticsApi = {
  funnel: () => request.get<FunnelStage[]>('/rec-analytics/funnel'),
  sla: () => request.get<SlaBucket[]>('/rec-analytics/sla'),
  channelRoi: () => request.get<ChannelRoi[]>('/rec-analytics/channel-roi'),
  hrbpRanking: () => request.get<HrbpRank[]>('/rec-analytics/hrbp-ranking'),
  deptProgress: () => request.get<DeptProgress[]>('/rec-analytics/dept-progress'),
  cycleTrend: () => request.get<CycleTrend[]>('/rec-analytics/cycle-trend'),
}

// ═══════════════════════════════════════════════════════════════
// V-de-mock iv-P2 (2026-07-14): 面试分析 3 报表
//   后端 InterviewAnalyticsController @ /interview-analytics
//   聚合 rec_interview_eval / rec_interview + join employee(@DataScope 过滤)。
//   注: 无后端源的列(资质等级/专长/准时率/满意度/评分稳定)前端已删/占位, VO 不含。
// ═══════════════════════════════════════════════════════════════
export interface InterviewerStat {
  empId: number; name: string; center: string | null; pos: string | null
  totalRounds: number; monthRounds: number; avgRating: number; passRate: number
}
export interface RoundFunnelStat { round: string; total: number; pass: number; passRate: number }
export interface RescheduleTrendStat {
  month: string; total: number; reschedule: number; rescheduleRate: number
}
export const interviewAnalyticsApi = {
  interviewers: () => request.get<InterviewerStat[]>('/interview-analytics/interviewers'),
  roundFunnel: () => request.get<RoundFunnelStat[]>('/interview-analytics/round-funnel'),
  rescheduleTrend: () => request.get<RescheduleTrendStat[]>('/interview-analytics/reschedule-trend'),
}

// ═══════════════════════════════════════════════════════════════
// V163 (UAT-FB#65 P1.6): 招聘流程模板 (按岗位类型差异化)
// ═══════════════════════════════════════════════════════════════
export interface RecFlowTemplate {
  id?: number
  code: string
  name: string
  jobFamily?: string  // TECH/MGMT/SALES/BLUE_COLLAR/null=通用
  description?: string
  isDefault?: number
  enabled?: number
}
export interface RecFlowTemplateStage {
  id?: number
  templateId?: number
  seq: number
  stageCode: string
  stageName: string
  icon?: string
  slaDays?: number
  required?: number
  description?: string
}
export interface RecFlowTemplateDetail {
  template: RecFlowTemplate
  stages: RecFlowTemplateStage[]
}
export const recFlowTemplateApi = {
  list: () => request.get<RecFlowTemplate[]>('/rec-flow-template'),
  recommend: (jobFamily?: string) =>
    request.get<RecFlowTemplate>('/rec-flow-template/recommend', { params: jobFamily ? { jobFamily } : {} }),
  get: (id: number) => request.get<RecFlowTemplateDetail>(`/rec-flow-template/${id}`),
  save: (t: RecFlowTemplate) => request.post<RecFlowTemplate>('/rec-flow-template', t),
  saveStages: (id: number, stages: RecFlowTemplateStage[]) =>
    request.post<RecFlowTemplateStage[]>(`/rec-flow-template/${id}/stages`, stages),
}

// ═══════════════════════════════════════════════════════════════
// V164 (UAT-FB#65 P1.2): 面试录音 + 转录 + AI 纪要
// ═══════════════════════════════════════════════════════════════
export interface AiInterviewSessionFull {
  id: number
  sessionNo: string
  candidateId: number
  candidateName: string
  jdTitle: string
  status: string
  recordingUrl?: string
  recordingUploadedAt?: string
  transcriptText?: string
  transcriptStatus?: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED'
  transcriptProvider?: string
  transcriptError?: string
  summaryText?: string
  summaryHighlights?: string
  summaryConcerns?: string
  summaryAt?: string
  linkedInterviewId?: number
}
export const interviewTranscriptionApi = {
  setRecording: (sessionId: number, url: string) =>
    request.post<AiInterviewSessionFull>(`/interview-transcription/${sessionId}/recording`, { url }),
  transcribe: (sessionId: number) =>
    request.post<AiInterviewSessionFull>(`/interview-transcription/${sessionId}/transcribe`, {}),
  summarize: (sessionId: number) =>
    request.post<AiInterviewSessionFull>(`/interview-transcription/${sessionId}/summarize`, {}),
}

// ═══════════════════════════════════════════════════════════════
// V168 (UAT-FB#65 P2.2): 视频面试一键拉群
// ═══════════════════════════════════════════════════════════════
export const interviewMeetingApi = {
  create: (interviewId: number, subject?: string, hostEmails?: string[]) =>
    request.post<any>(`/interview-meeting/${interviewId}/create`, { subject, hostEmails }),
  cancel: (interviewId: number) =>
    request.post<any>(`/interview-meeting/${interviewId}/cancel`, {}),
  pullRecording: (interviewId: number) =>
    request.post<any>(`/interview-meeting/${interviewId}/pull-recording`, {}),
}

// ═══════════════════════════════════════════════════════════════
// V168+ (UAT-FB#65 P2.3): 用人部门/CHO Dashboard
// ═══════════════════════════════════════════════════════════════
export interface DashboardDeptView {
  orgId?: number
  totalDemands: number
  recruitingDemands: number
  totalHeadcount: number
  onboardedHeadcount: number
  openingHeadcount: number
  candidatesByStatus: Record<string, number>
  urgentDemands: Array<{
    demandNo: string; position: string; target: number; onboarded: number
    daysToDeadline: number; urgency: 'OVERDUE' | 'URGENT' | 'HIGH' | 'NORMAL'
  }>
}
export interface DashboardCho {
  totalDemands: number
  totalTarget: number
  totalOnboarded: number
  totalGap: number
  monthlyHiredTrend: Array<{ month: string; count: number }>
  alerts: Array<{ demandNo: string; position: string; urgency: string; daysOpen: number; reason: string }>
  topChannels: Array<{ channel: string; count: number }>
  acceptRate: number
  avgCycleDays: number
}
export const recDashboardApi = {
  deptView: (orgId?: number) =>
    request.get<DashboardDeptView>('/rec-dashboard/dept-view', { params: orgId ? { orgId } : {} }),
  cho: () => request.get<DashboardCho>('/rec-dashboard/cho'),
}

// ═══════════════════════════════════════════════════════════════
// V169 (UAT-FB#66 P3, 2026-06-17): 候选人入职门户
// ═══════════════════════════════════════════════════════════════
export interface OnboardingStepItem {
  itemId: number
  templateItemId: number
  code: string
  name: string
  itemType: 'FIELD' | 'FILE'
  docType?: string
  required?: number
  fieldMeta?: string  // JSON
  description?: string
  sampleUrl?: string
  valueJson?: string
  fileUrl?: string
  fileName?: string
  uploadedAt?: string
  reviewStatus?: string
  reviewComment?: string
}
export interface OnboardingStep {
  stepNo: number
  title: string
  itemCount: number
  requiredCount: number
  complete: boolean
  items: OnboardingStepItem[]
}
export interface OnboardingProgressView {
  docId: number
  candidateId: number
  status: string
  currentStep: number
  completedSteps: number[]
  submittedAt?: string
  empId?: number
  empNo?: string
  steps: OnboardingStep[]
}
export interface OnboardingConvertResult {
  empId: number
  empNo: string
  name: string
  docId: number
}
export const onboardingPortalApi = {
  /** HR 端: 列出 docs，日期与进行中状态由后端筛选，避免客户端对截断数据二次过滤。 */
  hrList: (status?: string, filters?: { expectedJoinDate?: string; activeOnly?: boolean }) =>
    request.get<any[]>('/portal/onboarding/hr/list', {
      params: { ...(status ? { status } : {}), ...(filters || {}) },
    }),
  /** 候选人 H5: 拉 10 步进度 */
  getProgress: (token: string) =>
    request.get<OnboardingProgressView>('/portal/onboarding', {
      params: { token }, silent: true, anonymous: true,
    }),
  /** 候选人 H5: 保存单字段 (草稿) */
  saveField: (token: string, itemId: number, valueJson: string) =>
    request.post<any>('/portal/onboarding/save-field', { itemId, valueJson }, {
      params: { token }, silent: true, anonymous: true,
    }),
  /** 候选人 H5: 推进步骤 */
  advanceStep: (token: string, step: number) =>
    request.post<any>('/portal/onboarding/advance-step', null, {
      params: { token, step }, silent: true, anonymous: true,
    }),
  /** 候选人 H5: 最终提交 */
  submit: (token: string) =>
    request.post<any>('/portal/onboarding/submit', null, {
      params: { token }, anonymous: true,
    }),
  /** HR 端: 拉某 doc 进度 */
  hrProgress: (docId: number) =>
    request.get<OnboardingProgressView>(`/portal/onboarding/hr/progress/${docId}`),
  /** HR 端: 一键转员工. 待入职管理 P4: 可选 terms 传直招「确认入职」敲定的任职条款 (offer 路径不传).
   * name/gender(M|F)/idType/idNo/mobile 为可选覆盖字段, 非空时优先覆盖门户/候选人回填值 (缺哪补哪);
   * idNo 传【明文】(后端 SM4 加密入库), 勿回传含 * 的脱敏值 (后端 400 拦) */
  hrConvert: (
    docId: number, orgId: number, positionId?: number, joinDate?: string,
    terms?: {
      level?: string; workCity?: string; legalEntityId?: number; probationUnit?: 'MONTH' | 'DAY'; probationMonths?: number; probationDays?: number; posName?: string;
      name?: string; gender?: 'M' | 'F'; idType?: string; idNo?: string; mobile?: string;
    },
  ) =>
    request.post<OnboardingConvertResult>(`/portal/onboarding/hr/convert/${docId}`, {
      orgId, positionId, joinDate, ...(terms || {}),
    }),
}

// ═══════════════════════════════════════════════════════════════
// V85 简历解析升级: JD ↔ 简历 匹配评分
//   后端: JdMatchController @ /rec-jd-match
//   返 { score: 0-100, matchedKeywords, missingKeywords, reasoning, source, jdTitle }
//   source: AI / TFIDF (AI 失败 fallback 本地 TF-IDF)
// ═══════════════════════════════════════════════════════════════
export const jdMatchApi = {
  /** body: { resumeText, jdId } */
  matchSingle: (data: { resumeText: string; jdId: number }) =>
    request.post<any>('/rec-jd-match/single', data),
  /** body: { resumeFileId, jdId } */
  matchByFile: (fileId: number, jdId: number) =>
    request.post<any>('/rec-jd-match/by-resume-file', { resumeFileId: fileId, jdId }),
}

// ═══════════════════════════════════════════════════════════════
// V96 (2026-06-06): 候选人简历模板可配置 + 多格式导出 (A2)
//   后端: CandidateResumeTemplateController @ /candidate-resume-template
//   功能: HR 在主数据 "简历模板" tab 维护模板 (上传 Word .docx + 字段映射),
//        候选人详情按 category/role_scope 筛模板, 一键导出 PDF / Word / HTML.
//   占位符约定 (docx 内写): ${name} ${currentCompany} ${highestSchool}
//        ${workHistory[0].company} ${skills} ...
// ═══════════════════════════════════════════════════════════════
export const candidateResumeTemplateApi = {
  /** 列表 (按 category / roleScope / enabled 过滤) */
  list: (params: { category?: string; roleScope?: string; enabled?: number; bodyFormat?: string } = {}) =>
    request.get<any[]>('/candidate-resume-template', { params }),
  get: (id: number) => request.get<any>(`/candidate-resume-template/${id}`),
  create: (data: any) => request.post<any>('/candidate-resume-template', data),
  update: (id: number, data: any) => request.put<any>(`/candidate-resume-template/${id}`, data),
  remove: (id: number) => request.delete(`/candidate-resume-template/${id}`),
  toggle: (id: number) => request.put<any>(`/candidate-resume-template/${id}/toggle`),
  /** 上传 docx, 返回解析出的占位符列表 [{placeholder, dataKey, label}] */
  uploadDocx: (id: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post<{ placeholder: string; dataKey: string; label: string }[]>(
      `/candidate-resume-template/${id}/upload`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
  },
  /** 渲染下载 (返二进制 Blob; format: PDF / WORD / HTML) */
  render: (cid: number, templateId: number, format: 'PDF' | 'WORD' | 'HTML' = 'PDF') =>
    request.get(`/candidate-resume-template/candidate/${cid}/render`, {
      params: { templateId, format },
      responseType: 'blob',
    }),
}

// ═══════════════════════════════════════════════════════════════
// V107 (2026-06-08): #Perf Phase 9 年度大盘点 4 模块 (#493)
//   后端 /perf-annual-kpi /perf-annual-review /perf-annual-goal-alignment /perf-annual-promotion
//   - kpi: 公司/部门/员工 3 级达成跟踪 (+ dashboard)
//   - review: 综合 4 季评级 → 年度评级 → 调薪/奖金/期权 (calculate / finalize / effect)
//   - goalAlign: STRATEGY → COMPANY → DEPT → TEAM → EMP 树 (tree 接口返树)
//   - promotion: 提名 → 经理/HRBP 推荐 → 委员会决议 → 生效 (含 eligibility 校验)
// ═══════════════════════════════════════════════════════════════
export const perfAnnualKpiApi = {
  list: (params: { year?: number; scopeType?: string; keyword?: string } = {}) =>
    request.get<any[]>('/perf-annual-kpi', { params }),
  dashboard: (year?: number) =>
    request.get<any>('/perf-annual-kpi/dashboard', { params: year ? { year } : {} }),
  get: (id: number) => request.get<any>(`/perf-annual-kpi/${id}`),
  create: (data: any) => request.post<any>('/perf-annual-kpi', data),
  update: (id: number, data: any) => request.put<any>(`/perf-annual-kpi/${id}`, data),
  remove: (id: number) => request.delete(`/perf-annual-kpi/${id}`),
}

export const perfAnnualReviewApi = {
  list: (params: { year?: number; grade?: string; keyword?: string } = {}) =>
    request.get<any[]>('/perf-annual-review', { params }),
  dashboard: (year?: number) =>
    request.get<any>('/perf-annual-review/dashboard', { params: year ? { year } : {} }),
  get: (id: number) => request.get<any>(`/perf-annual-review/${id}`),
  create: (data: any) => request.post<any>('/perf-annual-review', data),
  update: (id: number, data: any) => request.put<any>(`/perf-annual-review/${id}`, data),
  calculate: (id: number) => request.post<any>(`/perf-annual-review/${id}/calculate`),
  finalize: (id: number) => request.post<any>(`/perf-annual-review/${id}/finalize`),
  effect: (id: number) => request.post<any>(`/perf-annual-review/${id}/effect`),
  remove: (id: number) => request.delete(`/perf-annual-review/${id}`),
}

export const perfAnnualGoalApi = {
  list: (params: { year?: number; level?: string; keyword?: string } = {}) =>
    request.get<any[]>('/perf-annual-goal-alignment', { params }),
  tree: (year?: number) =>
    request.get<any[]>('/perf-annual-goal-alignment/tree', { params: year ? { year } : {} }),
  dashboard: (year?: number) =>
    request.get<any>('/perf-annual-goal-alignment/dashboard', { params: year ? { year } : {} }),
  get: (id: number) => request.get<any>(`/perf-annual-goal-alignment/${id}`),
  create: (data: any) => request.post<any>('/perf-annual-goal-alignment', data),
  update: (id: number, data: any) => request.put<any>(`/perf-annual-goal-alignment/${id}`, data),
  remove: (id: number) => request.delete(`/perf-annual-goal-alignment/${id}`),
}

export const perfAnnualPromotionApi = {
  list: (params: { year?: number; status?: string; keyword?: string } = {}) =>
    request.get<any[]>('/perf-annual-promotion', { params }),
  dashboard: (year?: number) =>
    request.get<any>('/perf-annual-promotion/dashboard', { params: year ? { year } : {} }),
  get: (id: number) => request.get<any>(`/perf-annual-promotion/${id}`),
  eligibility: (id: number) => request.get<any>(`/perf-annual-promotion/${id}/eligibility`),
  nominate: (data: any) => request.post<any>('/perf-annual-promotion', data),
  update: (id: number, data: any) => request.put<any>(`/perf-annual-promotion/${id}`, data),
  managerRecommend: (id: number, yes = true) =>
    request.post<any>(`/perf-annual-promotion/${id}/manager-recommend`, null, { params: { yes } }),
  hrbpRecommend: (id: number, yes = true) =>
    request.post<any>(`/perf-annual-promotion/${id}/hrbp-recommend`, null, { params: { yes } }),
  committeeDecide: (id: number, body: { decision: string; reason?: string; effectiveDate?: string }) =>
    request.post<any>(`/perf-annual-promotion/${id}/committee-decide`, body),
  effect: (id: number) => request.post<any>(`/perf-annual-promotion/${id}/effect`),
  remove: (id: number) => request.delete(`/perf-annual-promotion/${id}`),
}

// ═══════════════════════════════════════════════════════════════
// V114 绩效目标下发 (上→下 批量下发 + 接收/拒绝协商 + notify 集成)
// 后端: PerfGoalAssignmentController @ /perf-goal-assignment
// 状态: PENDING_ACCEPT / ACCEPTED / REJECTED / RENEGOTIATING / IN_PROGRESS / COMPLETED / CANCELLED
// ═══════════════════════════════════════════════════════════════
export interface PerfGoalAssignment {
  id: number
  code: string
  sourceType: string                // OBJECTIVE / ALIGNMENT
  sourceGoalId?: number
  sourceGoalName?: string
  sourceEmpNo: string
  sourceEmpName?: string
  targetEmpNo: string
  targetEmpName?: string
  cycleId?: number
  cycleLabel?: string
  goalName: string
  goalDescription?: string
  measure?: string
  targetValue?: string
  weight?: number
  krList?: string
  dueDate?: string
  status: string
  rejectReason?: string
  renegotiationRound?: number
  assignedAt?: string
  respondedAt?: string
  completedAt?: string
}

export interface BatchAssignItem {
  targetEmpNo: string
  targetEmpName?: string
  goalName: string
  goalDescription?: string
  measure?: string
  targetValue?: string
  weight?: number
  krList?: string
  dueDate?: string
}

export interface BatchAssignDto {
  sourceType: string                // OBJECTIVE / ALIGNMENT
  sourceGoalId?: number
  sourceGoalName?: string
  sourceEmpNo: string
  sourceEmpName?: string
  cycleId?: number
  cycleLabel?: string
  items: BatchAssignItem[]
}

export const perfGoalAssignmentApi = {
  /** 通用查询 (HR 用 — 不限 source/target) */
  list: (params: { targetEmpNo?: string; status?: string; cycleId?: number } = {}) =>
    request.get<PerfGoalAssignment[]>('/perf-goal-assignment', { params }),
  /** 员工: 我的待接收 */
  myPending: (empNo: string, cycleId?: number) =>
    request.get<PerfGoalAssignment[]>('/perf-goal-assignment/my-pending', {
      params: cycleId ? { empNo, cycleId } : { empNo },
    }),
  /** 经理: 我下发的 */
  myAssigned: (empNo: string, cycleId?: number) =>
    request.get<PerfGoalAssignment[]>('/perf-goal-assignment/my-assigned', {
      params: cycleId ? { empNo, cycleId } : { empNo },
    }),
  /** HR/老板: 4 级下发树 */
  cascadeTree: (cycleId?: number) =>
    request.get<any[]>('/perf-goal-assignment/cascade-tree', {
      params: cycleId ? { cycleId } : {},
    }),
  /** 下发率统计 */
  stats: (cycleId?: number) =>
    request.get<any>('/perf-goal-assignment/stats', {
      params: cycleId ? { cycleId } : {},
    }),
  /** 详情 */
  get: (id: number) => request.get<PerfGoalAssignment>(`/perf-goal-assignment/${id}`),
  /** 批量下发 */
  batchAssign: (dto: BatchAssignDto) =>
    request.post<PerfGoalAssignment[]>('/perf-goal-assignment/batch', dto),
  /** 接收 */
  accept: (id: number, empNo: string) =>
    request.post<PerfGoalAssignment>(`/perf-goal-assignment/${id}/accept`, null, { params: { empNo } }),
  /** 拒绝 */
  reject: (id: number, empNo: string, reason: string) =>
    request.post<PerfGoalAssignment>(`/perf-goal-assignment/${id}/reject`, { reason }, { params: { empNo } }),
  /** 协商重发 */
  renegotiate: (id: number, dto: BatchAssignDto) =>
    request.post<PerfGoalAssignment>(`/perf-goal-assignment/${id}/renegotiate`, dto),
  /** 完成 */
  complete: (id: number, empNo: string) =>
    request.post<PerfGoalAssignment>(`/perf-goal-assignment/${id}/complete`, null, { params: { empNo } }),
}

// ═══════════════════════════════════════════════════════════════
// V108 面试地点字典 + 视频会议平台配置 + 链接生成
// 推进对话框 (AdvanceStageDialog) "同时安排面试" 子表单用
// 后端: InterviewLocationController / VideoMeetingConfigController / VideoMeetingController
// type: ROOM 会议室 / PHONE 电话 / VIDEO 视频 / OFFSITE 外部
// provider: TENCENT / DINGTALK / FEISHU / ZOOM / MOCK
// ═══════════════════════════════════════════════════════════════
export const interviewLocationApi = {
  list: () => request.get<any[]>('/interview-location'),
  byType: (type: string) => request.get<any[]>(`/interview-location/by-type/${type}`),
  get: (id: number) => request.get<any>(`/interview-location/${id}`),
  create: (data: any) => request.post<any>('/interview-location', data),
  update: (id: number, data: any) => request.put<any>(`/interview-location/${id}`, data),
  toggle: (id: number) => request.post<any>(`/interview-location/${id}/toggle`),
  remove: (id: number) => request.delete(`/interview-location/${id}`),
  /**
   * V109: GPS + org 派生推荐.
   *   lat/lng 有 → Haversine 算距离升序, 每行带 distance (km)
   *   仅 orgId → 该 org 关联办公地点排前
   *   都无 → 全表按 sort
   * 后端 PHONE/VIDEO 永远附末尾.
   */
  findNearby: (params: { lat?: number | null; lng?: number | null; orgId?: number | null; maxDistance?: number }) =>
    request.get<any[]>('/interview-location/nearby', { params }),
}

export const videoMeetingConfigApi = {
  list: () => request.get<any[]>('/video-meeting-config'),
  enabled: () => request.get<any[]>('/video-meeting-config/enabled'),
  getDefault: () => request.get<any>('/video-meeting-config/default'),
  get: (id: number) => request.get<any>(`/video-meeting-config/${id}`),
  create: (data: any) => request.post<any>('/video-meeting-config', data),
  update: (id: number, data: any) => request.put<any>(`/video-meeting-config/${id}`, data),
  setDefault: (id: number) => request.post<any>(`/video-meeting-config/${id}/default`),
  remove: (id: number) => request.delete(`/video-meeting-config/${id}`),
}

export const videoMeetingApi = {
  /** 生成视频会议链接, 返 { meetingNo, provider, displayName, url, password, scheduledAt, durationMin, expireAt, notice? } */
  generate: (params: {
    provider?: string
    candidateId?: number
    candidateName?: string
    scheduledAt?: string
    durationMin?: number
  }) => request.post<any>('/video-meeting/generate', null, { params }),
}

// ═══════════════════════════════════════════════════════════════
// V123/V125 薪资结构 (Salary Structure)
// 薪资项字典 + 模板 + 员工绑定快照 (api/salaryStructure.ts)
// V125-P4: emp_salary_structure → emp_salary_binding (语义化 rename)
// ═══════════════════════════════════════════════════════════════
export {
  payComponentApi,
  payStructureTemplateApi,
  empSalaryBindingApi,
  payCalcRuleApi, // V125-P5
  templateStatusOf, // XCCS017 版本化
  latestPublishedTemplates, // XCCS017: 选包下拉只显每 code 最新 PUBLISHED 版
} from './salaryStructure'
export type {
  PayComponent,
  PayStructureTemplate,
  PayStructureNode,
  CalculatedNode,
  CalculatePreview,
  EmpSalaryBindingSnapshot,
  CurrentSnapshotResp,
  EmpSalaryBindingListRow, // V125-P8
  EmpSalaryBindingListResp, // V125-P8
  PayCalcRule, // V125-P5
  PayCalcRuleType,
  PayCalcDailyBasis,
  DryRunRequest, // XCCS017 试算
  DryRunItem,
  DryRunResult,
} from './salaryStructure'

// ⭐ V144 (2026-06-15): 编码规则真生产化 — 后端 CodeGeneratorService 接口
export const codeRuleApi = {
  list: () => request.get('/code-rule'),
  get: (module: string) => request.get(`/code-rule/${module}`),
  update: (module: string, data: any) => request.put(`/code-rule/${module}`, data),
  peek: (module: string) => request.get(`/code-rule/${module}/peek`),
  resetCounter: (module: string) => request.post(`/code-rule/${module}/reset-counter`),
}

// ⭐ V171 (UAT-FB#72.2, 2026-06-17): 合同变更历史 — 后端 EmpContractChangeController
// 前端 ContractChangeHistoryPanel.vue 用. ContractChangeDialog 在 procApi.start 成功后调用 create.
export interface EmpContractChangeRow {
  id: number
  contractId: number
  contractNo: string
  empId?: number
  empNo?: string
  empName?: string
  changeType: string
  beforeJson?: string | null
  afterJson?: string | null
  effectiveDate?: string
  reason?: string
  supplementaryTemplateCode?: string
  approver?: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'APPLIED'
  procInstanceId?: number | null
  appliedAt?: string | null
  rejectedAt?: string | null
  rejectReason?: string | null
  createdAt?: string
  updatedAt?: string
  orgId?: number | null
}
export const empContractChangeApi = {
  /** 后端原子创建 PENDING 变更记录并启动审批流程。 */
  create: (data: {
    contractId: number
    contractNo: string
    changeType: string
    beforeValues?: Record<string, unknown>
    afterValues?: Record<string, unknown>
    effectiveDate: string
    reason: string
    supplementaryTemplateCode?: string
    approver?: string
  }) => request.post<EmpContractChangeRow>('/emp-contract-change', data),
  /** 时间轴: 某合同的全部变更记录 (createdAt DESC). */
  byContract: (contractId: number) =>
    request.get<EmpContractChangeRow[]>(`/emp-contract-change/by-contract/${contractId}`),
  /** 员工聚合: 某员工所有合同变更 (跨合同). */
  byEmp: (empId: number) =>
    request.get<EmpContractChangeRow[]>(`/emp-contract-change/by-emp/${empId}`),
  /** 仅审批已通过的变更可应用；后端拒绝 PENDING 绕过流程。 */
  apply: (id: number) =>
    request.post<EmpContractChangeRow>(`/emp-contract-change/${id}/apply`),
}

// ⭐ V172 (UAT-FB#74.2, 2026-06-17): 离职证明电子签 — 后端 EmpOffboardCertController
// 前端 emp/Index.vue 在调 offboardApi.issueCertificate (现 fallback 警告),
// 本期接通后, 行内/批量按钮请改调 empOffboardCertApi.issue / batchIssue.
export interface EmpOffboardCertRow {
  id: number
  offboardId: number
  offboardNo: string
  empId: number
  empNo: string
  empName: string
  certTemplateCode?: string
  certNo?: string
  renderedPdfUrl?: string
  renderedAt?: string
  esignProvider?: 'MOCK' | 'FADADA' | 'ESIGN'
  esignFlowId?: string
  esignSignUrl?: string
  esignSmsSentAt?: string
  status: 'CREATED' | 'RENDERED' | 'SMS_SENT' | 'VIEWED' | 'SIGNED' | 'REJECTED' | 'EXPIRED'
  signedPdfUrl?: string
  signedAt?: string
  rejectedAt?: string
  rejectReason?: string
  ipAddress?: string
  userAgent?: string
  createdAt?: string
  updatedAt?: string
  orgId?: number | null
}

export interface BatchIssueCertResult {
  total: number
  success: number
  failed: number
  successCertIds: number[]
  failures: { offboardId: number | null; error: string }[]
}

export const empOffboardCertApi = {
  /** HR 单发起 — 一期只开放 OFFLINE/NONE，ELECTRONIC 会被后端拒绝 */
  issue: (offboardId: number, payload?: {
    sign?: 'ELECTRONIC' | 'OFFLINE' | 'NONE'
    candidateMobile?: string
    candidateEmail?: string
    issuerName?: string
    hrPhone?: string
  }) => request.post<EmpOffboardCertRow>(`/emp-offboard-cert/${offboardId}/issue`, payload || {}),

  /** HR 批量发起 — 单条失败收到 failures[], 不阻断 */
  batchIssue: (payload: {
    offboardIds: number[]
    sign?: 'ELECTRONIC' | 'OFFLINE' | 'NONE'
    candidateMobile?: string
    candidateEmail?: string
    issuerName?: string
    hrPhone?: string
  }) => request.post<BatchIssueCertResult>('/emp-offboard-cert/batch-issue', payload),

  /** 查最新一条证明 (一对一, 一个 offboard_id 仅一条活动记录) */
  byOffboard: (offboardId: number) =>
    request.get<EmpOffboardCertRow | null>(`/emp-offboard-cert/by-offboard/${offboardId}`),

  /** 该员工历史全部证明 (含 REJECTED/EXPIRED 重发的) */
  byEmp: (empId: number) =>
    request.get<EmpOffboardCertRow[]>(`/emp-offboard-cert/by-emp/${empId}`),
}

// ═══════════════════════════════════════════════════════════════
// V289 (行22, 2026-07-27): 入职合同电子签 — 复用通用 ESignProvider
//   后端 EmpContractSignController @ /emp-contract-sign
//   状态机 CREATED→SMS_SENT→VIEWED→SIGNED; markSigned 联动合同 EFFECTIVE (Q3 简化流)
// ═══════════════════════════════════════════════════════════════
export interface EmpContractSignRow {
  id: number
  contractId: number
  contractNo?: string
  empId?: number | null
  onboardId?: number | null
  candidateId?: number | null
  signerName?: string
  signMethod?: 'E_SIGN' | 'PAPER'
  esignProvider?: string
  esignFlowId?: string
  esignSignUrl?: string
  status?: string
  signedFileUrl?: string
  [k: string]: any
}
export const empContractSignApi = {
  /** 当前是否已配置真实电子签供应商 */
  providerStatus: () =>
    request.get<{ available: boolean; provider?: string }>('/emp-contract-sign/provider-status'),
  /** HR 发起签署 — signMethod 缺省 E_SIGN */
  issue: (contractId: number, payload?: {
    signMethod?: 'E_SIGN' | 'PAPER'
    signerName?: string
    signerMobile?: string
    signerEmail?: string
    hrPhone?: string
  }) => request.post<EmpContractSignRow>(`/emp-contract-sign/${contractId}/issue`, payload || {}),

  /** HR 批量发起 — 单条失败收到 failures[], 不阻断 */
  batchIssue: (payload: {
    contractIds: number[]
    signMethod?: 'E_SIGN' | 'PAPER'
    signerMobile?: string
    signerEmail?: string
    hrPhone?: string
  }) => request.post<any>('/emp-contract-sign/batch-issue', payload),

  /** 查某合同最新一条签署记录 */
  byContract: (contractId: number) =>
    request.get<EmpContractSignRow | null>(`/emp-contract-sign/by-contract/${contractId}`),

  /** 该员工历史签署记录 */
  byEmp: (empId: number) =>
    request.get<EmpContractSignRow[]>(`/emp-contract-sign/by-emp/${empId}`),

}

// ═══════════════════════════════════════════════════════════════
// V173 (UAT-FB#80, 2026-06-17): 工厂普工扫码待入职模块
//   后端 3 个 controller:
//     /md-worker-job-type           普工工种字典 (HR 主数据)
//     /portal/worker-onboard        扫码 token 签发 + H5 提交 + OCR + 池子列表
// ═══════════════════════════════════════════════════════════════

export interface MdWorkerJobType {
  id?: number
  code: string
  name: string
  baseSalary?: number | null
  description?: string | null
  enabled?: number
  sortOrder?: number
  createdAt?: string
  updatedAt?: string
}

export interface MdWorkerSalaryStandard {
  id?: number
  workerJobTypeCode: string
  standardSalary: number
  salaryMin?: number | null
  salaryMax?: number | null
  currencyCode?: string
  effectiveFrom: string
  effectiveTo?: string | null
  enabled?: number
  version?: number
  remark?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface WorkerQrToken {
  id: number
  token: string
  issuedBy?: number | null
  purpose: string
  expiresAt: string
  usedCount: number
  lastUsedAt?: string | null
  lastIp?: string | null
  lastUa?: string | null
}

export interface WorkerOcrResult {
  success: boolean
  name?: string
  idCard?: string
  gender?: string
  birthDate?: string
  address?: string
  note?: string
}

export interface WorkerQuickAddRequest {
  token: string
  name: string
  phone: string
  idCard: string
  jobType: string
  emergencyContactName?: string
  emergencyContactPhone?: string
  extra?: Record<string, any>
  contractSignMode?: 'ONLINE' | 'OFFLINE_RECORD'
  scanSource?: 'CANDIDATE_PHONE' | 'HR_TABLET_OCR'
}

export interface WorkerOnboardDoc {
  id: number
  status: string
  channel: string
  skipOffer: number
  empType?: string | null
  workerJobType?: string
  workerName?: string
  workerPhone?: string
  /** UAT-FB#86 (2026-06-17): SM4 加密后, 后端只返 mask 字段 */
  workerPhoneMask?: string
  workerIdCard?: string
  workerIdCardMask?: string
  emergencyContactName?: string
  emergencyContactPhone?: string
  emergencyPhoneMask?: string
  contractSignMode?: string
  scanSource?: string
  targetOrgId?: number | null
  expectedJoinDate?: string | null
  sourceRemark?: string | null
  salaryStandardId?: number | null
  salaryStandardAmount?: number | null
  salaryAgreedAmount?: number | null
  salaryCurrencyCode?: string | null
  salaryOverrideReason?: string | null
  salaryOutsideRange?: number
  salaryDecidedBy?: number | null
  salaryDecidedAt?: string | null
  /** V273: 扩展资料 JSON 字符串 (fieldCode → value, 与 emp_import_field_meta 同源) */
  extraJson?: string | null
  createdAt?: string
  updatedAt?: string
  empId?: number | null
}

export interface PageResult<T> {
  records: T[]
  total: number
  size: number
  current: number
}

export const mdWorkerJobTypeApi = {
  /** 全量列表 (含禁用) */
  list: () => request.get<MdWorkerJobType[]>('/md-worker-job-type'),
  /** 启用列表 (下拉用；普工 H5 无登录态也要能加载) */
  listEnabled: () => request.get<MdWorkerJobType[]>('/md-worker-job-type/list', { anonymous: true, silent: true }),
  get: (id: number) => request.get<MdWorkerJobType>(`/md-worker-job-type/${id}`),
  create: (data: MdWorkerJobType) => request.post<MdWorkerJobType>('/md-worker-job-type', data),
  update: (id: number, data: Partial<MdWorkerJobType>) =>
    request.put<MdWorkerJobType>(`/md-worker-job-type/${id}`, data),
  remove: (id: number) => request.delete(`/md-worker-job-type/${id}`),
}

export const mdWorkerSalaryStandardApi = {
  list: () => request.get<MdWorkerSalaryStandard[]>('/md-worker-salary-standard'),
  resolve: (workerJobTypeCode: string, effectiveDate?: string) =>
    request.get<MdWorkerSalaryStandard>('/md-worker-salary-standard/resolve', {
      params: { workerJobTypeCode, ...(effectiveDate ? { effectiveDate } : {}) },
    }),
  create: (data: MdWorkerSalaryStandard) =>
    request.post<MdWorkerSalaryStandard>('/md-worker-salary-standard', data),
  update: (id: number, data: Partial<MdWorkerSalaryStandard>) =>
    request.put<MdWorkerSalaryStandard>(`/md-worker-salary-standard/${id}`, data),
  remove: (id: number) => request.delete(`/md-worker-salary-standard/${id}`),
}

export const portalWorkerOnboardApi = {
  /** 公共 H5 字段元数据，不要求系统登录。 */
  formFields: () => request.get<any[]>('/portal/worker-onboard/form-fields', { anonymous: true, silent: true }),
  /** HR 端: 签发 24h 扫码 token */
  qrToken: (purpose?: string) =>
    request.get<WorkerQrToken>('/portal/worker-onboard/qr-token', { params: purpose ? { purpose } : {} }),
  /** public 端: 候选人 H5 提交 (token 在 body) */
  quickAdd: (req: WorkerQuickAddRequest) =>
    request.post<WorkerOnboardDoc>('/portal/worker-onboard/quick-add', req, { anonymous: true, silent: true }),
  /** HR 平板: 上传身份证图片 mock OCR */
  scanIdOcr: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return request.post<WorkerOcrResult>('/portal/worker-onboard/scan-id-ocr', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  /** HR 端: 普工待入池分页 */
  workerPage: (page = 1, size = 20, status?: string) =>
    request.get<PageResult<WorkerOnboardDoc>>('/portal/worker-onboard/worker-page', {
      params: { page, size, ...(status ? { status } : {}) },
    }),
  /** HR 端: 批量改部门 */
  batchUpdateOrg: (docIds: number[], targetOrgId: number) =>
    request.post<number>('/portal/worker-onboard/batch-update-org', { docIds, targetOrgId }),
  /** UAT-FB#82 (2026-06-17): 批量办入职 — 方案 C 双签约模式 */
  batchConvert: (
    docIds: number[],
    contractSignMode: 'ONLINE' | 'OFFLINE_RECORD',
    // M2c-2b: certExpiries(证件到期日补录 certType→yyyy-MM-dd) + overrideCompliance(超管豁免证件闸)
    overrides?: { joinDate?: string; empType?: string; managerId?: number; certExpiries?: Record<string, string>; overrideCompliance?: boolean },
  ) =>
    request.post<{ success: number; failed: number; contractSignMode: string; empIds: number[]; errors: string[] }>(
      '/portal/worker-onboard/batch-convert',
      { docIds, contractSignMode, ...(overrides || {}) },
    ),
  /** 2026-07-01 方案 A: HR 手动添加待入职 (channel=DIRECT_HIRE, 无 Offer/无扫码) */
  directHire: (data: {
    name: string; phone: string; idCard: string;
    targetOrgId: number; posName?: string; expectJoin?: string; remark?: string;
    empType?: string;   // V275: HR 选的员工类型, 决定入职字段模板
    status?: string;
    extra?: Record<string, string | number | boolean>;
  }) => request.post<any>('/portal/worker-onboard/direct-hire', data),
  /** 待入职管理 P3: (重新)签发直招 doc 的本人补资料门户短链 (「补资料链接」按钮) */
  directHirePortalLink: (docId: number) =>
    request.post<{ docId: number; portalPath: string }>(`/portal/worker-onboard/direct-hire/${docId}/portal-link`),
  /** 直招发起信息采集: 签发补资料短链 (30 天) + 按渠道通知. channels 至少 1 个 (INBOX 站内信给负责 HR 转交;
   * EMAIL/SMS 占位恒 accepted=false); templateId 可选 (仅校验存在性后透传). 逐渠道返回 accepted/reason */
  collect: (docId: number, body: { channels: ('INBOX' | 'EMAIL' | 'SMS')[]; templateId?: number }) =>
    request.post<{ url: string; channels: { channel: string; accepted: boolean; reason?: string }[] }>(
      `/portal/worker-onboard/direct-hire/${docId}/collect`, body),
  /** UAT-FB#83 (2026-06-17): HR 工种 inline 改 */
  updateJobType: (docId: number, jobType: string) =>
    request.patch<void>(`/portal/worker-onboard/${docId}/job-type`, { jobType }),
  /** V273 (2026-07-15, 冻结豁免): 普工池单人资料编辑 — phone/idCard 空=不改, 填新值后端重走 SM4 */
  updateWorker: (docId: number, data: {
    workerName?: string; phone?: string | null; idCard?: string | null;
    emergencyContactName?: string; emergencyContactPhone?: string | null;
    workerJobType?: string; targetOrgId?: number | null; targetOrgIdProvided?: boolean; contractSignMode?: string;
    expectedJoinDate?: string; remark?: string;
    empType?: string | null;   // 员工类型: 选哪套收集字段的标签 (不映射用工类型)
    status?: string;
    cancelReasonCode?: string;
    cancelReason?: string;
    extra?: Record<string, string | number | boolean>;
  }) => request.put<WorkerOnboardDoc>(`/portal/worker-onboard/${docId}`, data),
  salaryDecision: (docId: number, effectiveDate?: string) =>
    request.get<{
      standardId: number; workerJobTypeCode: string; standardSalary: number;
      salaryMin?: number | null; salaryMax?: number | null; agreedSalary: number;
      currencyCode: string; effectiveFrom: string; effectiveTo?: string | null;
      overridden: boolean; outsideRange: boolean; overrideReason?: string | null;
    }>(`/portal/worker-onboard/${docId}/salary-decision`, {
      params: effectiveDate ? { effectiveDate } : {},
    }),
  saveSalaryDecision: (docId: number, data: { agreedSalary: number; reason?: string; effectiveDate?: string }) =>
    request.put<any>(`/portal/worker-onboard/${docId}/salary-decision`, data),
  /** UAT-FB#89 (2026-06-17): 按需解密身份证 + 手机 (走审计) */
  revealIdCard: (docId: number, reason: string) =>
    request.get<{ idCard: string }>(`/portal/worker-onboard/${docId}/reveal/id-card`, { params: { reason } }),
  revealPhone: (docId: number, reason: string) =>
    request.get<{ phone: string }>(`/portal/worker-onboard/${docId}/reveal/phone`, { params: { reason } }),
}

// candOnboardingApi 加 workerPage (复用现有的 onboardingPortalApi pattern, 但路径是新的 worker-page)
// 设计权衡: 因 task spec 提到 candOnboardingApi.workerPage(), 这里为了 spec 兼容追加 alias
export const candOnboardingApi = {
  /** 普工通道池子分页 (alias of portalWorkerOnboardApi.workerPage) */
  workerPage: (page = 1, size = 20, status?: string) =>
    portalWorkerOnboardApi.workerPage(page, size, status),
}

// ═══════════════════════════════════════════════════════════════
// 入职资料/字段模板 (V162 + V274/V275 empType 维度)
//   后端: OnboardDocTemplateController @ /onboard-doc-template
//   用途: HR 配置"某员工类型(empType)入职收集哪些字段" (普工字段模板配置面板)
// ═══════════════════════════════════════════════════════════════
export const onboardDocTemplateApi = {
  /** 列模板; all=1 含停用 */
  list: (all = true) => request.get<any[]>('/onboard-doc-template', { params: all ? { all: 1 } : {} }),
  get: (id: number) => request.get<any>(`/onboard-doc-template/${id}`),
  /** 新建/更新模板 (body 含 code/name/empType/jobFamily/enabled/description) */
  save: (body: any) => request.post<any>('/onboard-doc-template', body),
  /** 列模板下资料项 */
  listItems: (id: number) => request.get<any[]>(`/onboard-doc-template/${id}/items`),
  /** 批量保存资料项 (先删后插) */
  saveItems: (id: number, items: any[]) => request.post<any[]>(`/onboard-doc-template/${id}/items`, items),
  /** 启用/停用 */
  setEnabled: (id: number, value: 0 | 1) =>
    request.put<any>(`/onboard-doc-template/${id}/enabled`, null, { params: { value } }),
}

// ═══════════════════════════════════════════════════════════════
// UAT-FB#61 Phase 2 (V178/V179, 2026-06-17): 试用期评估模板系统
//   后端 Controllers:
//     /probation-template              模板主表 (code/name/职族/职级/版本/状态)
//     /probation-template-dimension    评估维度 (绩效/态度/出勤/学习 + 权重)
//     /probation-template-signer       签字方 (DIRECT_MANAGER/HRBP/HR_LEAD/SELF/CUSTOM)
//     /probation-template-visibility   角色 × 维度 可见性矩阵
//   权限: hasAuthority('emp')
//   命名统一: V179 把 visibility.viewer_role 'EMP' → 'SELF', 与 signer_type 一致
//   注: 命名空间 probationTplApi (而非 probationApi) 避免与 line 1562 旧 emp_probation CRUD 重名
// ═══════════════════════════════════════════════════════════════
export const probationTplApi = {
  /** 评估模板主表 */
  template: {
    /** 列表, status 可选 (DRAFT/ACTIVE/ARCHIVED) */
    list: (status?: string) =>
      request.get<any[]>('/probation-template', { params: status ? { status } : {} }),
    get: (id: number) => request.get<any>(`/probation-template/${id}`),
    /** 按 code 取当前 ACTIVE 版本 (评估实例化时调) */
    byCodeActive: (code: string) =>
      request.get<any>(`/probation-template/by-code/${encodeURIComponent(code)}/active`),
    create: (data: any) => request.post<any>('/probation-template', data),
    update: (id: number, data: any) => request.put<any>(`/probation-template/${id}`, data),
    remove: (id: number) => request.delete(`/probation-template/${id}`),
  },
  /** 评估维度 (绩效/态度/出勤/学习, 同模板下权重合计 = 100) */
  dimension: {
    byTemplate: (tid: number) =>
      request.get<any[]>(`/probation-template-dimension/by-template/${tid}`),
    /** 权重合计 (校验 = 100 用) */
    sumWeight: (tid: number) =>
      request.get<number>(`/probation-template-dimension/by-template/${tid}/sum-weight`),
    create: (data: any) => request.post<any>('/probation-template-dimension', data),
    update: (id: number, data: any) =>
      request.put<any>(`/probation-template-dimension/${id}`, data),
    remove: (id: number) => request.delete(`/probation-template-dimension/${id}`),
  },
  /** 签字方 (DIRECT_MANAGER/HRBP/HR_LEAD/SELF/CUSTOM, 按 sequence 升序) */
  signer: {
    byTemplate: (tid: number) =>
      request.get<any[]>(`/probation-template-signer/by-template/${tid}`),
    create: (data: any) => request.post<any>('/probation-template-signer', data),
    update: (id: number, data: any) =>
      request.put<any>(`/probation-template-signer/${id}`, data),
    remove: (id: number) => request.delete(`/probation-template-signer/${id}`),
  },
  /** 字段可见性 (角色 × 维度, dimensionId NULL = 整体级别) */
  visibility: {
    byTemplate: (tid: number) =>
      request.get<any[]>(`/probation-template-visibility/by-template/${tid}`),
    /** 单角色视角 (Phase 3 渲染评估单时调) */
    forViewer: (tid: number, viewerRole: string) =>
      request.get<any[]>(`/probation-template-visibility/by-template/${tid}/for-viewer`, {
        params: { viewerRole },
      }),
    create: (data: any) => request.post<any>('/probation-template-visibility', data),
    update: (id: number, data: any) =>
      request.put<any>(`/probation-template-visibility/${id}`, data),
    remove: (id: number) => request.delete(`/probation-template-visibility/${id}`),
  },
  /**
   * UAT-FB#61 Phase 5 (V181, 2026-06-17): 员工"我的评估" + 知晓签字.
   * 后端 EmpProbationController @ /probation, ProbationReviewAcknowledgementController @ /probation-review-acknowledgement.
   * - myReviews(): 当前登录员工的全部评估单 (历史 + 当前, 按 startDate 倒序). 越权由后端 UserContext 隔离.
   * - selfView(id): 评估单详情 + SELF 视角 (按 visibility.canView 过滤维度). 即使 admin 也只能看自己的.
   * - acknowledge({ reviewId, ackResult, ackComment }): 员工知晓 / 异议签字.
   *   ackResult 写独立法律留痕，不覆盖 emp_probation 的 COMPLETED 流程终态.
   *   前置: emp_probation.status 必须 = COMPLETED.
   */
  review: {
    myReviews: () => request.get<any[]>('/probation/my-reviews'),
    selfView: (id: number) =>
      request.get<any>(`/probation/${id}/with-template-self-view`),
    /** HR 查看某评估单的员工签收/异议法律留痕。 */
    acknowledgements: (probationId: number) =>
      request.get<any[]>(`/probation-review-acknowledgement/by-review/${probationId}`),
    acknowledge: (data: { reviewId: number; ackResult: 'ACKED' | 'DISPUTED'; ackComment?: string }) =>
      request.post<any>('/probation-review-acknowledgement/acknowledge', data),
  },
  /**
   * UAT-FB#61 Phase 4 (V180, 2026-06-17): 签字记录 + 状态机.
   * 后端 endpoint /probation-review-signature/*
   * pending-for-me / history-for-me 自动按当前 SecurityUser 过滤
   * (signer_emp_id 精确匹配 OR signer_type 角色映射: SUPER/GROUP_HR→HR_LEAD, CENTER_HR/FACTORY_HR→HRBP, DEPT_MGR→DIRECT_MANAGER).
   */
  signature: {
    /** 按 review 列签字记录 (按签字时间升序) */
    byReview: (reviewId: number) =>
      request.get<any[]>(`/probation-review-signature/by-review/${reviewId}`),
    /** 当前用户"待签字"列表 (按 scheduledReviewDate 升序, 越紧急越靠前) */
    pendingForMe: () => request.get<any[]>('/probation-review-signature/pending-for-me'),
    /** 当前用户最近 N 天签字历史 (PASS/FAIL, 倒序) */
    historyForMe: (days = 90) =>
      request.get<any[]>('/probation-review-signature/history-for-me', { params: { days } }),
    /** Phase 4 状态机: 签字 + 自动推下一签字方 (PASS→next PENDING / FAIL→REJECTED 跳过后续) */
    signAndAdvance: (
      id: number,
      data: { signResult: 'PASS' | 'FAIL'; comment: string; sigImageUrl?: string },
    ) => request.post<any>(`/probation-review-signature/${id}/sign-and-advance`, data),
    /** 管理员补派/改派 ACTIVE + PENDING 签字任务，原因写入后端审计日志 */
    assign: (id: number, data: { signerEmpId: number; reason: string }) =>
      request.put<any>(`/probation-review-signature/${id}/assignee`, data),
    /** Phase 1 老 sign 方法 (不自动推 next), 仅留兼容; 新代码请用 signAndAdvance */
    sign: (
      id: number,
      data: { signResult: 'PASS' | 'FAIL'; comment?: string; sigImageUrl?: string },
    ) => request.post<any>(`/probation-review-signature/${id}/sign`, data),
  },
}
